// src/ui/VibesScreen.jsx — Vibes on the big screen: three panes.
//
//   left    my vibe — analytics (account status, reach, reports), pages, saved,
//           friends and notifications swap into this pane when summoned
//   centre  the feed: composer on top, the app's scored feed beneath
//   right   the squeeze pane — comments, a video, or someone's profile page slide in
//           here and the feed breathes in to make room, same motion as chat tools
//
// The registration gate runs first: no dateOfBirth on users/{me} → the same two-field
// form the app's age gate writes; under 16 → the same refusal.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import SupporterBadge from './SupporterBadge';   // 💛 the Supporter badge, next to the name
import {
  watchVibeSelf, completeVibeRegistration, vibesUnlockDate, watchFeed, likeWithNotify, addComment, deleteComment,
  toggleSave, createPost, watchNotifications, markNotificationRead, watchFriends,
  sendFriendRequest, acceptFriendRequest, declineFriendRequest, watchMyPosts,
  watchMyPages, fetchUserPosts, fetchPostsByIds, fetchTaggedPosts, LEGAL_AGE,
  recordShare, DEFAULT_COVER, setCoverImage, setPageCover, setPageImage, setProfilePrivacy, fetchProfileMeta, fetchPage,
  sharePostToFeed,
  fetchPagePosts, PAGE_CATEGORIES, createPage,
} from '../services/vibes';
import Avatar, { resolveName } from './Avatar';
import ChatAvatar, { ChatTitle } from './ChatAvatar';
import { isNoteToSelf, otherParticipant } from '../services/chats';
import { iBlockedNow, blockedMeNow, unblockUser } from '../services/blocking';   // 🚫
import { isGuestChat } from '../services/guestLinks';
import { isOfficialChat } from '../services/support';
import {
  X, Heart, HeartFill, Comment, CommentFill, Bookmark, BookmarkFill, Bell, PersonAdd2,
  Image as ImageIcon, Send, Play, Check, ShieldCheck, Eye2, Group as GroupIcon,
  Forward, Link as LinkIcon, ArrowRight, MapPin, Plane, Lock, Crosshair,
  Verified, DocText, ChevronLeft, ChevronRight, Download,
  Camera, Timer, Phone as PhoneIcon, Back, Video as VideoIcon, Poll,
  More, EyeOff, ArrowDown, Trash, Alert, Search, Spinner, Gear, Globe, Sliders,
  Refresh, Tag, Cake, Pencil, Pause,
} from './icons';
import BrandLottie from './BrandLottie';
import {
  loadSearchCorpus, invalidateSearchCorpus, searchPosts, peopleFrom, highlightParts,
  KINDS, kindOf, loadHashtagPosts, serverSearch,
} from '../services/vibeSearch';
import Pic from './VibeAvatar';
import SafeImg from './SafeImg';
import PostVideo from './PostVideo';
import VolumeControl from './VolumeControl';
import { TextStyleBar, TextPostCard } from './TextPostStyler';
import VibePoll from './VibePoll';
import VibeComments, { repliesUnder } from './VibeComments';
import FollowersPane from './FollowersPane';
/* 📊 the poll's shape and 👥 the follow, both shared with the phone case for
   case — see the headers of services/vibePoll.js and services/pageFollow.js */
import {
  isPollPost, pollQuestionIsTheContent, buildPollFields, MAX_POLL_OPTIONS,
} from '../services/vibePoll';
/* 👁️ one answer to "what does a post look like as a square", shared with the
   phone — see services/postGrid on both clients */
import {
  showsViews, tileViews, compactViews, postKind, pollPreview,
  authorOf, postCounts, previewMeta,
} from '../services/postGrid';
/* 🏷 / ⚠️ ONE ANSWER TO "WHAT IS A TAG" AND "IS THIS CLIP DANGEROUS", shared
   with the phone and the admin server — see services/videoTags. */
import {
  postSafetyKind, orphanHashtags, hashtagQuery, hashtagOfQuery, parseHashtags,
} from '../services/videoTags';
import { SafetyNotice, ReelCaption, ReelTags } from './ReelMeta';
/* ©️ what a post says about itself on rights — the AI pill, the muted-sound
   banner, and the notice form. Rules: services/contentRights (shared with the
   phone and the server). */
import { AiBadge, MutedNotice, MutedPill, CopyrightNoticeSheet, MutedExplainSheet, RIGHTS_TEXT } from './RightsMeta';
import { commentCountOf, localCountAfter } from '../services/vibeComments';
/* ❤️ likes a viral post can hold: the count is the shared rule's, the heart the shared store's */
import { likeCountOf } from '../services/postCounts';
import { useLikedByMe, reactionOf, onReactionChange } from '../services/likeApi';
/* 😍 a like with a kind */
import { ReactButton, ReactionGlyph, ReactionSummary, ReleaseToCancel, ReactionsSheet } from './ReactionKit';   // 😍 the reaction button, tray, summary and list
import { useLiveReactions } from '../services/reactionLive';
/* 📄 the thread's pages — comments are documents now, a page at a time */
import { commentPager } from '../services/vibeCommentApi';
import { railCounts } from '../services/postGrid';
import { STR as RIGHTS_STR } from '../services/contentRights';
import { openSupport } from '../services/support';
import { attribution as soundAttribution, PROVIDER_LABEL as SOUND_PROVIDER_LABEL, showsSoundCredit } from '../services/soundLibrary';
/* 🎨 the filter tint, the stickers and the caption drawn ON the picture — the
   browser had never rendered any of it; see ui/Overlay.jsx */
import { Overlay, ReelResults, hasAuthorResults } from './Overlay';
/* 🎨 the phone's photo/video editor, ported — see ui/MediaEditor.jsx */
import MediaEditor, { blankEdit, isEdited } from './MediaEditor';
/* 🔒 the phone's `users/{uid}.isPrivate`, read and written by the same rules —
   see services/profilePrivacy */
import { privacyBadge, privacyOf, privacyValue } from '../services/profilePrivacy';
import {
  isOfficialPage, followerCountOf, amIFollowing,
  toggleFollow as toggleFollowPage, OFFICIAL_PAGE_ID, pageFollowedBy,
} from '../services/pageFollow';
/* 🌍 the world map — the door at the top of the left pane, and the map itself */
import WorldMap, { WorldMapCard } from './WorldMap';
/* ⏩ ➕ hold-to-2× and the avatar's follow badge — the phone's rules, ported */
import { reelFollowState, REEL_FOLLOW_DONE_MS } from '../services/reelFollow';
import {
  DEFAULT_TEXT_STYLE, SUGGESTED_BG, isStyledText, canStyleText, textPostCss,
} from '../services/textPost';
import {
  postAudience, accountStats, engagementRate, likesOf, commentsOf, fetchInsights, audienceForScreen, sendViewEvents,
} from '../services/vibeStats';
import {
  REPORT_REASONS, AlreadyReported, hidePost, showLessLike, reportPost,
  setCommentsEnabled, deletePost, banState,
} from '../services/vibeModeration';
/* 🎯 THE RANKED FEED AND THE LEDGER IT LEARNS FROM. `feedApi` answers
   "which posts, in what order" and returns null on any failure — the old
   `watchFeed` is still here and still runs whenever it does. `feedSignals` is
   write-only telemetry about the feed and nothing else. */
import * as feedApi from '../services/feedApi';
import * as feedSignals from '../services/feedSignals';
import { createViewTracker } from '../services/viewTracker';   // 👁️ impressions, reach and views — counted by the server
/* 🏷 tags: who shows on a post is the server's answer (tagRoutes.js) */
import { tagLine, shownTags, isTaggedIn, mayOpenFriendsPost } from '../services/postTags';
import { respondTag, removeTag } from '../services/vibeSocial';
import TagPicker from './TagPicker';
import CreatorDashboard, { DashboardStrip } from './CreatorDashboard';   // 📈 your numbers, on your own profile
import EditPostModal from './EditPostModal';   // ✏️ edit a post you made
import PostTranslation from './PostTranslation';   // 🌐 See translation
import MemoriesStrip from './MemoriesStrip';   // 🕰 On this day
import StoriesBar from './StoriesBar';   // 📸 stories, under the composer row
import CreatePostDialog from './CreatePostDialog';   // ✍️ "Create post" — the phone's full-screen composer, as a dialog
import {
  composeL, feelingSegment, draftPreview, draftWorthKeeping, playsOwnMusic,
  feelingPieces, placeAfterFeelingPieces, nearLabelFor,
} from '../services/postCompose';
import LinePieces, { LineGlyph } from './LinePieces';   // 🧭 minimal icons for activities, the pin, the plane
import { loadDraft, saveDraft, clearDraft, peekDraft, subscribeDraft } from '../services/composeDraftWeb';
import { toggleMusic, stopIf as stopPostMusic, playingPost, subscribeMusic } from '../services/postMusicWeb';
import { isEdited as isPostEdited } from '../services/postEdit';

import { auth as viewAuth } from '../config/firebase';
/* 🔁 when to ask for the next ranked page, and what to keep from it — shared
   with the phone, executed and mutation-tested. See services/feedPaging. */
import { shouldFetchMore, mergeNewPage, pageRequest } from '../services/feedPaging';
import { feedSource } from '../services/feedPaging';
import { reelsSeed, readUntilSome } from '../services/feedPaging'; // 🎬 the clicked clip, then the reels ranking
import usePullToRefresh from './usePullToRefresh';
/* 🎬 the reels shelf, and the rule that decides where in the feed it sits —
   generated from the phone's, so a strip is placed the same way in both */
import ReelsRow from './ReelsRow';
import { withReelsRow, enoughReels, newReelsSeed, REELS_MAX } from '../services/reelsRow';
import { searchUsers } from '../services/users';
import { shareVibePost } from '../services/vibeShare';
import { isShare, MAX_SHARE_CAPTION } from '../services/sharedPost';
import { mt, pickLang, reasonLabel } from '../services/modStrings';
import ProfileFacts from './ProfileFacts';
import { ProfileDetailsCard, ProfileDetailsSheet } from './ProfileDetailsKit';   // 🪪 profile details
import { acceptsFriendRequests } from '../services/friendRequests';
/* 🪪 the same catalogue the phone asks from, generated from the same source —
   including WHICH categories are asked, so the two forms cannot diverge */
import { identityFor, fieldByKey, fieldLabel, optionLabel, fieldsL } from '../services/profileFields';
/* EyeOff is already imported with the rest of the icon set above */
import { Eye } from './icons';
import { MENTION_NOTIF_TEXT } from '../services/commentMentions';

/* ✏️ the screen's "edit this post" — set by VibesScreen, called from any post's menu */
let editPostHandler = null;

const ago = (ts) => {
  const t = ts?.toDate ? ts.toDate().getTime() : (typeof ts === 'number' ? ts : 0);
  if (!t) return '';
  const m = Math.floor((Date.now() - t) / 60000);
  if (m < 1) return 'now';
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  return `${Math.floor(h / 24)}d`;
};

/* ══ IMAGES ══════════════════════════════════════════════════════════════════
   Every remote image on this screen goes through SafeImg (see ui/SafeImg.jsx):
   it refuses device-local URIs outright, retries twice before believing an
   onError, and only then shows a labelled placeholder instead of the browser's
   torn-paper glyph. Avatars keep their own fallback — the initial — below. */

/** An avatar that falls back to the initial, and forgives a transient abort. */
/* ── composer media tray: real previews, not filenames ────────────────────── */
function MediaTray({ files, onRemove, onEdit, edited }) {
  const urls = useMemo(() => files.map((f) => URL.createObjectURL(f)), [files]);
  useEffect(() => () => urls.forEach((u) => URL.revokeObjectURL(u)), [urls]);
  return (
    <div className={`vb-tray n${Math.min(files.length, 4)}`}>
      {files.map((f, i) => (
        <span className="vb-tray-cell" key={i}>
          {f.type?.startsWith('video/')
            /* 🟥 `#t=0.1`, WITHOUT WHICH THE CELL IS AN EMPTY BOX. `preload="metadata"`
               fetches the header and stops; nothing is decoded, so the element
               paints its own background and an attached clip looked like a big
               blank panel in the composer. The fragment asks for the frame at
               that timestamp — one range request. `0.1` rather than `0` because
               the first frame of a phone recording is frequently black; the
               profile grid uses exactly this. */
            ? <video src={`${urls[i]}#t=0.1`} muted playsInline preload="metadata" />
            : <img src={urls[i]} alt="" />}
          {f.type?.startsWith('video/') && <em className="vb-tray-vid"><Play width={12} height={12} /></em>}
          <button className="vb-tray-x" onClick={() => onRemove(i)} aria-label="Remove"><X width={12} height={12} /></button>
        </span>
      ))}
      {/* 🟢 THE WAY INTO THE EDITOR SITS ON THE MEDIA, not on a row of its own
          under it. It is about these pictures, and a stranded pill below the
          tray reads as another composer button rather than as "do something to
          this". */}
      {onEdit && (
        <button type="button" className="vb-tray-edit" onClick={onEdit}>
          <Sliders width={13} height={13} />
          {edited ? 'Edit media' : 'Add stickers, text or a filter'}
        </button>
      )}
    </div>
  );
}

/* ── link card, web edition ───────────────────────────────────────────────────
   A big browser-style preview of the site itself, not the app's compact card.
   The banner is a live screenshot of the page (WordPress mShots — free, no
   key); if the post carried a real og:image we prefer that. A favicon + domain
   chip floats over the shot, the title and a "Visit" affordance sit beneath. */
const faviconOf = (domain) => `https://www.google.com/s2/favicons?domain=${domain}&sz=128`;
const screenshotOf = (url) => `https://s0.wp.com/mshots/v1/${encodeURIComponent(url)}?w=1280&h=720`;

function LinkCard({ p }) {
  const [step, setStep] = useState(0); // 0 = first choice, 1 = fallback, 2 = plain strip
  let domain = p.linkDomain;
  if (!domain) { try { domain = new URL(p.linkUrl).hostname.replace(/^www\./, ''); } catch { domain = p.linkUrl; } }
  const hasPhoto = p.linkImage && !/s2\/favicons|favicon|apple-touch-icon/i.test(p.linkImage);
  // og:image first when the post has one, live page screenshot otherwise;
  // whichever loses becomes the fallback rung before the imageless strip
  const rungs = hasPhoto ? [p.linkImage, screenshotOf(p.linkUrl)] : [screenshotOf(p.linkUrl)];
  const shot = rungs[step] || null;
  return (
    <a className="vb-link" href={p.linkUrl} target="_blank" rel="noreferrer">
      {shot && (
        <span className="vb-link-shot">
          <img src={shot} alt="" loading="lazy" onError={() => setStep(step + 1)} />
          <span className="vb-link-chip">
            <SafeImg src={faviconOf(domain)} kind="inline" />
            {domain}
          </span>
        </span>
      )}
      <span className="vb-link-under">
        <span className="vb-link-body">
          <b>{p.linkTitle || domain}</b>
          {!shot && <i className="vb-link-dom"><LinkIcon width={11} height={11} /> {domain}</i>}
        </span>
        <span className="vb-link-visit">Visit <ArrowRight width={13} height={13} /></span>
      </span>
    </a>
  );
}

/* 😊 "is 😊 feeling happy" — the feeling's KEY is on the post, the words are the
   reader's (services/postCompose, shared with the phone); a value outside the
   catalogue draws nothing. After a feeling, the place loses its own "is". */
const StatusLine = ({ status, feeling }) => {
  const lang = (typeof navigator !== 'undefined' ? (navigator.language || 'en') : 'en').slice(0, 2);
  const seg = feelingSegment(feeling, lang);
  if (!status?.type && !seg) return null;
  /* 🧭 an activity, the pin and the plane are drawn as small line icons (ui/LinePieces) */
  return (
    <em className="vb-statusline">
      {seg ? <span className="vb-feeling"><LinePieces pieces={feelingPieces(feeling, lang)} /></span> : null}
      {status?.type ? (seg
        ? <><LinePieces pieces={placeAfterFeelingPieces(status.type, lang)} /><b>{status.type === 'check-in' ? status.location : status.to}</b></>
        : (status.type === 'check-in'
          ? <> is <LineGlyph icon="pin" /> at <b>{status.location}</b></>
          : <> is <LineGlyph icon="plane" /> traveling to <b>{status.to}</b></>)) : null}
    </em>
  );
};

/* 🎵 ▶ / ❚❚ for a post whose song has no clip to follow (services/postMusicWeb) */
function MusicPlayButton({ p }) {
  const [on, setOn] = useState(playingPost() === p.id);
  useEffect(() => subscribeMusic(() => setOn(playingPost() === p.id)), [p.id]);
  useEffect(() => () => stopPostMusic(p.id), [p.id]);
  return (
    <button
      type="button" className={`vb-music-play ${on ? 'on' : ''}`} aria-label={on ? 'Pause music' : 'Play music'} aria-pressed={on}
      onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleMusic(p.id, p.audioTrackUrl, Number(p.audioStartMs) || 0); }}
    >
      {on ? <Pause width={12} height={12} /> : <Play width={12} height={12} />}
    </button>
  );
}

// 🟢 only when a track was actually picked — no "Original Sound" filler anywhere
/* 🟢 THE TRACK, REDESIGNED. It used to be a bare ♪ and a line of text floating
   above the media with a 48px left indent — indented to nothing in particular,
   attached to nothing in particular. A track belongs TO the media, so it now
   rides on it: a frosted pill in the bottom-left corner with a moving equaliser,
   which is where every short-video app puts it and where the eye already looks.
   The 'inline' variant is the fallback for a post with a track and no media. */
/* 🟥 THE VARIANT IS NAMESPACED, AND IT HAS TO BE. This shipped as
   `vb-music ${variant}` — so the overlay pill also carried a bare `overlay`
   class, and theme.css has owned `.overlay` since day one:

       .overlay { position: fixed; inset: 0; background: rgba(10,15,25,.55);
                  display: grid; place-items: center; z-index: 100; }

   A full-screen modal scrim. `inset: 0` beat the pill's own `left`/`bottom`, so
   instead of a small pill in the corner the track spread across the entire post
   and greyed the picture out. Prefixed class names are not a style preference in
   a shared stylesheet — an unprefixed one is a collision waiting for a name
   somebody else already took. */
/* 🎵 THE PILL IS THE CREDIT. Jamendo's API terms want the artist named, Jamendo
   named as the provider, and a link to the track's page — so the pill says
   "Title — Artist", says who provided it, and opens the page. The phone's pill
   says the same (soundAttribution, both clients). */
const MusicLine = ({ p, light, variant = 'inline' }) => {
  /* 🎵 only a song the post actually plays — never a muted, untapped suggestion */
  if (!showsSoundCredit(p)) return null;
  const a = soundAttribution({ title: p.audioTrackTitle, artist: p.audioTrackArtist, page: p.audioTrackPage, provider: p.audioTrackProvider });
  const body = (
    <>
      <span className="vb-eq" aria-hidden><i /><i /><i /></span>
      <span className="vb-music-txt">
        {a?.line || p.audioTrackTitle}
        {p.audioTrackProvider && <em className="vb-music-via">via {SOUND_PROVIDER_LABEL}</em>}
      </span>
    </>
  );
  const cls = `vb-music vb-music-${variant} ${light ? 'light' : ''}`;
  const credit = p.audioTrackPage
    ? <a className={cls} href={p.audioTrackPage} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} title={`On ${SOUND_PROVIDER_LABEL}`}>{body}</a>
    : <div className={cls}>{body}</div>;
  /* 🎵 a song with no clip to follow is played on request */
  if (!playsOwnMusic(p)) return credit;
  return <span className="vb-music-row"><MusicPlayButton p={p} />{credit}</span>;
};

/* 🟢 PREFER THE OPTIMISED VARIANT — IT IS THE ONE WITH A CORRECT CONTENT TYPE.
   The app uploads post media straight from an XHR blob, and that blob usually
   carries no `type`, so Firebase stored the original as application/octet-stream.
   Native image loaders sniff the bytes and don't care; a browser will not decode
   an <img> whose Content-Type isn't an image, which is why such a post renders on
   the phone and breaks here (and why opening the URL DOWNLOADS the file instead
   of showing it). The app now sets contentType explicitly — but posts already
   uploaded still carry the bad original.

   The server's /api/optimize-image wrote `variants.medium` and `variants.small`
   with `contentType: 'image/jpeg'` and `public: true`, so those render fine.
   Preferring them repairs every already-posted image without touching Storage —
   and it's the smaller file too (1080px @ q80 rather than the camera original). */
/* 🟢 THE VIEW COUNT IS THE AUTHOR'S BUSINESS, NOT THE READER'S. How many people
   have seen a post is a stat for whoever posted it; showing it to everyone else
   turns the feed into a scoreboard and leaks how a stranger's post is doing. The
   phone only surfaces views in the owner's own analytics, so the web matches.
   A page post still carries its creator's `userId`, so posting as a page keeps
   the count visible to the person who runs the page — which is right. */
const isMyPost = (p, me) => !!p?.userId && !!me?.uid && p.userId === me.uid;

const bestImg = (m) => m?.variants?.medium || m?.variants?.small || m?.url || null;
/** The same rescue for the legacy top-level p.imageUrl. */
const bestPostImg = (p) => {
  const first = (p?.media || []).find((m) => m?.type === 'image') || (p?.media || [])[0];
  return first?.variants?.medium || first?.variants?.small || p?.imageUrl || null;
};

/* ══ THE POST MENU ═════════════════════════════════════════════════════════
   Two menus behind one button, because the actions available on your own post
   and on someone else's have nothing in common:

     mine    comments on/off · delete
     theirs  hide · show less like this · report

   Report opens a second step with the app's four headings, in the app's order.
   Everything writes the app's own fields — see services/vibeModeration.js for
   what the investigation turned up about each one. */
/* 😍 THE HEART — MINE ONLY. Everybody's reactions are on the right of the
   post (ReactionSummary). A click likes; hover (or hold and slide on a touch
   screen) offers the other kinds — see ReactionKit. A pick plays its face once. */
function ReactHeart({ p, me, liked, onLike, onHold, placement, dark, className = 'vb-act', size = 17, count = false }) {
  const [, bump] = useState(0);
  const [play, setPlay] = useState(0);
  useEffect(() => onReactionChange(() => bump((n) => n + 1)), []);
  const mine = reactionOf(p, me.uid, liked);
  /* the count with my unconfirmed press on it — `p` is the server's copy (the press goes out against it) */
  const shown = useLiveReactions(p);
  return (
    <ReactButton
      mine={mine} placement={placement} dark={dark} onHold={onHold}
      className={`${className} ${liked ? 'liked' : ''}`}
      onTap={() => { if (!liked) setPlay((n) => n + 1); onLike(p, liked); }}
      onPick={(r) => { if (r !== mine) setPlay((n) => n + 1); onLike(p, liked, r); }}
    >
      <ReactionGlyph mine={mine} size={size} play={play} />
      {/* the count, beside the heart (the feed and the post pane) */}
      {count ? <span className="rx-count">{likeCountOf(shown) || ''}</span> : null}
    </ReactButton>
  );
}

/* 🏷 "with Ali and 3 others" — each name opens that person. Spans, not buttons:
   the byline is itself a button, and a button inside a button is not HTML. */
function TagLineView({ p, onProfile }) {
  const tl = tagLine(p);
  if (!tl.named.length) return null;
  const open = (e, x) => { e.stopPropagation(); onProfile && onProfile({ uid: x.uid, name: x.name }); };
  return (
    <span className="vb-taglinev">
      {' with '}
      {tl.named.map((x, i) => (
        <span key={x.uid}>
          {i > 0 ? ' and ' : ''}
          <span role="link" tabIndex={0} className="vb-tagname" onClick={(e) => open(e, x)} onKeyDown={(e) => { if (e.key === 'Enter') open(e, x); }}>{x.name}</span>
        </span>
      ))}
      {tl.others > 0 && (
        <span>
          {' and '}
          <span className="vb-tagname" title={shownTags(p).map((x) => x.name).join('\n')} onClick={(e) => e.stopPropagation()}>{tl.others} others</span>
        </span>
      )}
    </span>
  );
}

function PostMenu({ p, me, onClose, onHidden, onDeleted, onCopyright }) {
  const mine = isMyPost(p, me);
  /* 🏷 a post I am tagged in (or asked to be) — I can take myself off it */
  const taggedMe = !mine && (isTaggedIn(p, me.uid) || (p.tagStatus && p.tagStatus[me.uid] === 'pending'));
  const [step, setStep] = useState('main');      // main | why | report | confirmDelete
  const [busy, setBusy] = useState('');
  /* null = not asked yet; `{ error: true }` = the ranker had nothing to say,
     which is a real answer and not a failure */
  const [why, setWhy] = useState(null);
  const loadWhy = async () => {
    setWhy(null);
    const r = await feedApi.whyThisPost(p.id);
    setWhy(r || { error: true });
  };
  const [err, setErr] = useState('');
  const ref = useRef(null);

  // click-away and Escape — a menu you can only close by picking something is a trap
  useEffect(() => {
    const away = (e) => { if (ref.current && !ref.current.contains(e.target)) onClose(); };
    const key = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('mousedown', away);
    document.addEventListener('keydown', key);
    return () => {
      document.removeEventListener('mousedown', away);
      document.removeEventListener('keydown', key);
    };
  }, [onClose]);

  const run = async (tag, fn, after) => {
    setBusy(tag); setErr('');
    try { await fn(); after?.(); onClose(); }
    catch (e) {
      setErr(e instanceof AlreadyReported
        ? 'You have already reported this post for that reason.'
        : 'That didn’t go through — try again.');
      setBusy('');
    }
  };

  return (
    <div className="vb-menu" ref={ref} role="menu">
      {/* 🔖 any post: save it for later (my Saved tab) */}
      {step === 'main' && (
        <button className="vb-menu-item" disabled={!!busy}
          onClick={() => run('save', () => toggleSave(me.uid, p.id, (me.savedPosts || []).includes(p.id)))}>
          {(me.savedPosts || []).includes(p.id) ? <BookmarkFill width={15} height={15} /> : <Bookmark width={15} height={15} />}
          {(me.savedPosts || []).includes(p.id) ? 'Unsave post' : 'Save post'}
        </button>
      )}
      {step === 'main' && mine && (
        <>
          {/* ✏️ the words, the audience, the tags, the comments */}
          <button className="vb-menu-item" disabled={!!busy} onClick={() => { onClose(); if (editPostHandler) editPostHandler(p); }}>
            <Pencil width={15} height={15} /> Edit post
          </button>
          <button
            className="vb-menu-item"
            disabled={!!busy}
            onClick={() => run('comments', () => setCommentsEnabled(p.id, p.commentsEnabled === false))}
          >
            <Comment width={15} height={15} />
            {p.commentsEnabled === false ? 'Turn comments on' : 'Turn comments off'}
          </button>
          <button className="vb-menu-item danger" onClick={() => setStep('confirmDelete')}>
            <Trash width={15} height={15} /> Delete post
          </button>
        </>
      )}

      {step === 'main' && !mine && (
        <>
          <button
            className="vb-menu-item" disabled={!!busy}
            onClick={() => { feedSignals.hidden(p.id); run('hide', () => hidePost(me.uid, p.id), onHidden); }}
          >
            <EyeOff width={15} height={15} />
            <span>Hide this post<i>You won&rsquo;t see it again</i></span>
          </button>
          <button
            className="vb-menu-item" disabled={!!busy}
            onClick={() => { feedSignals.notInterested(p.id); run('less', () => showLessLike(me.uid, p.userId), onHidden); }}
          >
            <ArrowDown width={15} height={15} />
            <span>Show less like this<i>Fewer posts from {p.userName || 'this account'}</i></span>
          </button>
          <div className="vb-menu-sep" />
          {/* 🟥 A RECOMMENDER YOU CANNOT INTERROGATE IS ONE PEOPLE END UP
              SUSPECTING. The answer comes from the ranker's own breakdown — the
              same object the console's Explain view renders — so it cannot
              drift from the decision, because it IS the decision. */}
          <button className="vb-menu-item" disabled={!!busy} onClick={() => { setStep('why'); loadWhy(); }}>
            <Eye2 width={15} height={15} />
            <span>Why am I seeing this?</span>
          </button>
          {taggedMe && (
            <button
              className="vb-menu-item" disabled={!!busy}
              onClick={() => run('untag', async () => { const r = await removeTag(p.id); if (!r || !r.ok) throw new Error('untag'); }, onHidden)}
            >
              <Tag width={15} height={15} />
              <span>Remove tag<i>You&rsquo;ll be taken off this post and it won&rsquo;t show on your page</i></span>
            </button>
          )}
          <button className="vb-menu-item danger" onClick={() => setStep('report')}>
            <Alert width={15} height={15} /> Report post
          </button>
        </>
      )}

      {step === 'why' && (
        <>
          <div className="vb-menu-head">
            <button className="vb-menu-back" onClick={() => setStep('main')}><Back width={14} height={14} /></button>
            Why am I seeing this?
          </div>
          {!why ? <div className="vb-menu-note">Working it out…</div> : why.error ? (
            <div className="vb-menu-note">
              This one came through the ordinary feed rather than the recommender — there is nothing to explain.
            </div>
          ) : (
            <div className="vb-menu-note">
              {why.matchedYourInterests?.length ? (
                <>It is about <b>{why.matchedYourInterests.join(', ')}</b>, which you have been watching.</>
              ) : (
                <>It is not matched to anything you have watched — some of every feed is deliberately kept for
                  things outside what we already think you like.</>
              )}
              {why.topics?.length ? <div>Tagged: {why.topics.map((t) => t.id).join(' · ')}</div> : null}
              {/* 🟥 SAYING SO OUT LOUD IS THE POINT. A promoted post that looks
                  organic is the thing people are right to be suspicious of. */}
              {why.boosted ? <div><b>Allo promoted this post.</b></div> : null}
            </div>
          )}
          {/* 🟥 AND A WAY OUT OF IT. Being able to see what a recommender thinks
              of you and NOT being able to change it is worse than not being
              shown at all — it is a mirror you cannot move. This clears the
              interest vectors and starts the feed from nothing. */}
          <button
            className="vb-menu-item" disabled={busy === 'reset'}
            onClick={() => run('reset', () => feedApi.resetMyFeed())}
          >
            <ArrowDown width={15} height={15} />
            <span>Start my feed again<i>Forget what Allo has learned about what I watch</i></span>
          </button>
        </>
      )}

      {step === 'report' && (
        <>
          <div className="vb-menu-head">
            <button className="vb-menu-back" onClick={() => setStep('main')}><Back width={14} height={14} /></button>
            Why are you reporting this?
          </div>
          {REPORT_REASONS.map((r) => (
            <button
              key={r.id} className="vb-menu-item" disabled={!!busy}
              onClick={() => run(r.id, () => reportPost({ post: p, reason: r.id, me }))}
            >
              {busy === r.id ? <span className="vb-sending" /> : <Alert width={15} height={15} />}
              {r.label}
            </button>
          ))}
          {/* ©️ A COPYRIGHT CLAIM IS NOT A REPORT. It goes to a different queue,
              needs a name and a signature, and carries legal deadlines — so it
              opens its own form rather than counting as one more report. */}
          {onCopyright && (
            <button className="vb-menu-item" onClick={() => { onClose(); onCopyright(); }}>
              <Alert width={15} height={15} /> {RIGHTS_TEXT.rights_report}
            </button>
          )}
          <div className="vb-menu-note">
            Reports are anonymous. Repeated reports on a post remove it and put a strike
            on the account that posted it.
          </div>
        </>
      )}

      {step === 'confirmDelete' && (
        <>
          <div className="vb-menu-head">
            <button className="vb-menu-back" onClick={() => setStep('main')}><Back width={14} height={14} /></button>
            Delete this post?
          </div>
          <div className="vb-menu-note">
            It disappears for everyone, along with its likes and comments. This can&rsquo;t be undone.
          </div>
          <button
            className="vb-menu-item danger" disabled={!!busy}
            onClick={() => run('del', () => deletePost(p.id), onDeleted)}
          >
            <Trash width={15} height={15} /> {busy === 'del' ? 'Deleting…' : 'Delete permanently'}
          </button>
        </>
      )}

      {err && <div className="vb-menu-err">{err}</div>}
    </div>
  );
}

/* ── the feed card ────────────────────────────────────────────────────────── */
/* ════════════════════════════════════════════════════════════════════════════
   SHARED POST — the card-inside-a-card.

   🟢 IT PAINTS FROM THE SNAPSHOT, NOT FROM A READ. services/sharedPost.js freezes
      the original's author, words and media into the share document, so this
      renders with the one document the feed already has. A feed where a third of
      the posts are shares would otherwise fire fifty extra reads before anything
      appeared — and with a chain, several per card.

   🟥 WHICH MEANS ONE THING HERE IS DELIBERATELY A LINK AND NOT A COPY: opening it.
      The snapshot can be stale — an edited post, a changed avatar, a DELETED
      post — so the tap goes to `postId` and reads live. A card that painted the
      original's comments from the snapshot would show a thread frozen at the
      moment somebody shared it, which is worse than showing none.

   🟢 AND THE COMMENT COUNTS BELOW BELONG TO THE SHARE, NOT TO THIS. Tapping the
      embed opens the original and ITS thread; the footer of the outer card is the
      sharer's own. That separation is the whole reason a re-share is its own post.
   ══════════════════════════════════════════════════════════════════════════ */
function SharedEmbed({ node, depth = 0, onOpenPost, onProfile }) {
  if (!node || !node.postId) return null;

  const isPage = node.authorType === 'page';
  const imgs = (node.media || []).filter((m) => m.type === 'image');
  const vid = (node.media || []).find((m) => m.type === 'video')
    || (node.videoUrl ? { url: node.videoUrl } : null);
  if (!imgs.length && node.imageUrl && !vid) imgs.push({ url: node.imageUrl, id: 'legacy' });
  /* the snapshot keeps at most MAX_EMBED_MEDIA items but records the true count,
     so "+6" is right even though only four URLs travelled */
  const hidden = Math.max(0, (node.mediaCount || imgs.length) - imgs.length);

  const open = (e) => { e.stopPropagation(); onOpenPost?.(node.postId); };

  return (
    <div
      className={`vb-embed d${Math.min(depth, 2)}`}
      role="button" tabIndex={0}
      onClick={open}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); open(e); } }}
      aria-label={`Open ${node.userName || 'Allo user'}'s post`}
    >
      <header className="vb-embed-head">
        {/* 🟥 THE ORIGINAL AUTHOR STAYS THE AUTHOR. Their name and picture are what
            makes a share a share rather than a copy — and the byline is its own
            button, so tapping the person goes to the person and tapping anywhere
            else goes to the post. */}
        <button
          className="vb-who vb-embed-who"
          onClick={(e) => {
            e.stopPropagation();
            onProfile?.({
              uid: node.userId, name: node.userName, image: node.userImage,
              ...(isPage ? { pageId: node.pageId } : {}),
            });
          }}
        >
          <Pic src={isPage ? (node.pageImage || node.userImage) : node.userImage} name={node.userName} size={30} />
          <span>
            <b>
              {node.userName || 'Allo user'}
              {isPage && <em className="vb-pagechip">{node.pageCategory || 'Page'}</em>}
            </b>
            <i>{ago(node.createdAtMs)}{nearLabelFor(node) ? ` · near ${nearLabelFor(node)}` : ''}</i>
          </span>
        </button>
      </header>

      {node.content ? (
        isStyledText(node)
          ? <TextPostCard style={node.textStyle} text={node.content} />
          : <p className="vb-text vb-embed-text">{node.content}</p>
      ) : null}

      {node.linkUrl ? <LinkCard p={node} /> : null}

      {vid ? (
        /* 🟥 A POSTER, NOT A PLAYER. The feed already autoplays every video it
           shows; an embedded one would be a second video playing inside the
           first, and a chain three deep would be three. The tap opens the
           original, where it plays properly. */
        <div className="vb-embed-media vb-embed-vid">
          <SafeImg src={node.imageUrl || undefined} loading="lazy" label="Video" />
          <span className="vb-embed-play"><Play width={14} height={14} /></span>
        </div>
      ) : imgs.length ? (
        <div className={`vb-embed-media vb-imgs n${Math.min(imgs.length, 4)}`}>
          {imgs.slice(0, 4).map((m, i) => (
            <SafeImg key={m.id || i} src={bestImg(m)} loading="lazy" />
          ))}
          {hidden > 0 && <span className="vb-img-more">+{hidden}</span>}
        </div>
      ) : null}

      {/* one level further down, drawn flatter each time */}
      {node.inner ? (
        <SharedEmbed node={node.inner} depth={depth + 1} onOpenPost={onOpenPost} onProfile={onProfile} />
      ) : null}

      {/* 🟢 WHAT THE DEPTH CAP DROPPED, SAID OUT LOUD. The chain is cut in the
          DATA at MAX_EMBED_DEPTH so a post document cannot grow past Firestore's
          1 MB ceiling; without this line the cut would be invisible and the card
          would quietly claim the third post was the original. */}
      {node.moreCount > 0 && !node.inner ? (
        <div className="vb-embed-more">
          Shared from {node.moreCount} earlier {node.moreCount === 1 ? 'post' : 'posts'} — open to see the original
        </div>
      ) : null}
    </div>
  );
}

/** A share whose embed is missing or half-written — see the tombstone note. */
function EmbedGone() {
  /* 🟥 NEVER RENDER NOTHING. A share with a broken embed would otherwise look
     like an ordinary post carrying only a caption, which reads as the sharer
     having written the words themselves. Saying the content is gone is the
     honest failure, and it is the same thing Facebook shows. */
  return (
    <div className="vb-embed vb-embed-gone">
      <b>This content isn&apos;t available</b>
      <i>It may have been deleted or its privacy changed.</i>
    </div>
  );
}

function PostCard({ p: rawP, me, onLike, onComments, onShare, onVideo, onProfile, onOpenPost, onOpenPostId, onInsights, onGone, onPollVoted }) {
  /* 😍 the post with my unconfirmed reaction on it — every copy agrees (reactionLive) */
  const p = useLiveReactions(rawP);
  const [rxHold, setRxHold] = useState({ open: false, hover: null });
  const [rxSheet, setRxSheet] = useState(false);
  /* ©️ the notice sheet, opened from the menu */
  const [noticeOpen, setNoticeOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  /* ❤️ past 500 likers the post's array cannot answer — the shared store can */
  const liked = useLikedByMe(p, me.uid);
  const vid = (p.media || []).find((m) => m.type === 'video') || (p.videoUrl ? { url: p.videoUrl } : null);
  const imgs = (p.media || []).filter((m) => m.type === 'image');
  if (!imgs.length && p.imageUrl && !vid) imgs.push({ url: p.imageUrl, id: 'legacy' });
  const isPage = p.authorType === 'page';

  return (
    /* 🟢 THE ID IS ON THE ELEMENT SO ONE OBSERVER CAN WATCH THE WHOLE FEED.
       A per-card observer would be one IntersectionObserver per post, each with
       its own callback closing over a post that re-renders on every like. */
    <article className="vb-card" data-post-id={p.id}>
      <header>
        <button
          className="vb-who"
          onClick={() => onProfile({ uid: p.userId, name: p.userName, image: p.userImage, ...(isPage ? { pageId: p.pageId } : {}) })}
        >
          <Pic src={isPage ? (p.pageImage || p.userImage) : p.userImage} name={p.userName} />
          <span>
            <b>
              {p.userName || 'Allo user'}
              {/* ✔️ THE BLUE CHECK. It reads `p.pageId` against the one official
                  page id — a FACT about which page posted, not a name this file
                  recognises. Matching on the display name would hand the badge
                  to anybody who called their page "Allo", which is the entire
                  reason a badge exists. */}
              {!isPage && p.userId ? <SupporterBadge uid={p.userId} size={14} /> : null}
              {isPage && p.pageId === OFFICIAL_PAGE_ID && (
                <span className="official-badge" title="Official Allo account">
                  <Verified width={14} height={14} />
                </span>
              )}
              {/* 🟥 NO CATEGORY CHIP IN THE FEED. The phone leaves it out here on
                  purpose — VibesScreen.tsx says so in as many words: "category
                  badge intentionally NOT rendered in the FEED card (name reads
                  cleaner here)", while the reels player, the viewers and the
                  page profile all still show it. The browser drew it anyway, so
                  every page post carried a blue "App" pill nobody asked for,
                  wedged between the name and the verified check. The chip lives
                  on the profile pane, where the phone also puts it. */}
              <StatusLine status={p.status} feeling={p.feeling} />
              <TagLineView p={p} onProfile={onProfile} />
            </b>
            <i>
              {/* 🟢 "shared a post" sits in the byline rather than above the card:
                  the card already shows the embed, so this is a label for what the
                  person DID, in the one place that already describes them. */}
              {isShare(p) ? 'shared a post · ' : ''}
              {ago(p.timestamp)}{isPostEdited(p) ? ' · Edited' : ''}{nearLabelFor(p) ? ` · near ${nearLabelFor(p)}` : ''}
            </i>
          </span>
        </button>
        {p.audience === 'friends' && <span className="vb-aud" title="Friends only"><GroupIcon width={14} height={14} /></span>}
        <div className="vb-menu-wrap">
          <button
            className={`vb-dots ${menuOpen ? 'on' : ''}`} aria-label="Post options"
            aria-haspopup="menu" aria-expanded={menuOpen}
            onClick={() => setMenuOpen((v) => !v)}
          >
            <More width={17} height={17} />
          </button>
          {menuOpen && (
            <PostMenu
              p={p} me={me}
              onClose={() => setMenuOpen(false)}
              onHidden={() => onGone?.(p)}
              onDeleted={() => onGone?.(p)}
              onCopyright={() => setNoticeOpen(true)}
            />
          )}
        </div>
      </header>

      {/* the words open the post too — a text-only post has nothing else to click */}
      {/* 🟥 a poll's question was drawn twice — here as the caption and again in the
          poll block as its bold heading, because one composer text box fed both
          fields. See services/vibePoll.pollQuestionIsTheContent. */}
      {p.content && !pollQuestionIsTheContent(p) ? (
        isStyledText(p) ? (
          <div className="vb-openable" onClick={() => onOpenPost(p, 0)}>
            <TextPostCard style={p.textStyle} text={p.content} />
          </div>
        ) : (
          <p className="vb-text vb-openable" onClick={() => onOpenPost(p, 0)}>{p.content}</p>
        )
      ) : null}
      {/* 🌐 "See translation" — only when the words are clearly in another language */}
      {p.content && p.type !== 'poll' ? <PostTranslation post={p} /> : null}

      {/* 📊 A POLL, above any media on purpose: the question is the post, and a
          picture between the caption and the answers turns a poll into an
          illustrated post somebody has to scroll back up to answer. */}
      {isPollPost(p) ? <VibePoll post={p} myUid={me.uid} onVoted={onPollVoted} /> : null}

      {p.linkUrl ? <LinkCard p={p} /> : null}

      {/* ⭐ A RE-SHARE. The caption above is the sharer's own words; this is what
          they shared. Rendered BEFORE the media block because a share carries no
          media of its own — the embed IS its body. */}
      {isShare(p) ? (
        <SharedEmbed node={p.sharedFrom} onOpenPost={onOpenPostId} onProfile={onProfile} />
      ) : p.sharedPostId ? <EmbedGone /> : null}

      {vid ? (
        /* 🟢 THE FEED VIDEO PLAYS WHERE IT SITS. It used to be a poster with a
           play badge, which made a volume control meaningless — there was
           nothing making sound to turn down. It now autoplays muted as it
           scrolls into view, the way every feed does, and the speaker in the
           corner is what turns it on. Clicking the picture still opens it full
           height as a reel; the speaker swallows its own clicks so turning the
           sound down doesn't navigate away from what you were listening to. */
        <div className="vb-mediawrap">
          <div
            className="vb-video live" role="button" tabIndex={0}
            aria-label="Open video"
            onClick={() => onVideo(p)}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onVideo(p); } }}
          >
            <PostVideo
              post={p} src={vid.url} poster={bestPostImg(p) || undefined}
              autoPlayInView volumeScope="feed" chrome
            />
            {/* 🎨 what the author drew on it. Interactive here for the same
                reason the phone's feed card is: a poll you have to open the
                reel to answer is a poll most people never answer. */}
            <Overlay post={p} me={me} interactive />
            <span className="vb-expand" title="Open"><Play width={13} height={13} /></span>
            {/* ©️ the AI label on the picture, and the muted marker where the
                speaker would otherwise be */}
            <AiBadge post={p} className="vb-on-media vb-feed-ai" />
            <MutedPill post={p} />
          </div>
          <MusicLine p={p} variant="overlay" />
        </div>
      ) : imgs.length ? (
        <div className="vb-mediawrap">
          <div className={`vb-imgs n${Math.min(imgs.length, 4)}`}>
            {imgs.slice(0, 4).map((m, i) => (
              <button className="vb-img-open" key={m.id || i} onClick={() => onOpenPost(p, i)} aria-label="Open post">
                <SafeImg src={bestImg(m)} loading="lazy" />
                {/* 🖼 EACH CELL ITS OWN STICKERS. The caption and tint are the
                    first photo's (the post is stamped from it) and Overlay draws
                    them only at index 0; stickers are tagged with the photo they
                    were placed on (services/vibeStickers stickersForMedia). */}
                <Overlay post={p} me={me} interactive mediaIndex={Math.max(0, (p.media || []).indexOf(m))} />
                {i === 0 && <AiBadge post={p} className="vb-on-media vb-feed-ai" />}
                {i === 3 && imgs.length > 4 && <span className="vb-img-more">+{imgs.length - 4}</span>}
              </button>
            ))}
          </div>
          <MusicLine p={p} variant="overlay" />
        </div>
      ) : (
        /* a track with nothing to sit on — the pill goes inline, un-indented */
        <MusicLine p={p} />
      )}

      <footer>
        {/* 😍 a click likes; a hover (or hold and slide) offers the other kinds.
            The press goes out against the copy as the server sent it (rawP). */}
        <ReactHeart p={rawP} me={me} liked={liked} onLike={onLike} onHold={setRxHold} count />
        <button className="vb-act" onClick={() => onComments(p)}>
          <Comment width={17} height={17} />{commentCountOf(p) || ''}
        </button>
        <button className="vb-act" onClick={() => onShare(p)}>
          <Forward width={17} height={17} />{p.sharesCount || ''}
        </button>
        {isMyPost(p, me) && (
          /* 🟢 the count is now the door to the insights pane — and it shows the
             DEDUPED audience, not the raw viewsCount counter, so the number here
             and the number inside agree (and both agree with the phone) */
          <button
            className="vb-views act" title="See who this reached — only you can"
            onClick={() => onInsights(p)}
          >
            <Eye2 width={14} height={14} />{postAudience(p).views}
          </button>
        )}
        {/* 😍 everybody's reactions, on the right — the door to who reacted */}
        <ReactionSummary post={p} onOpen={() => setRxSheet(true)} className="vb-rx-right" showCount={false} />
        <ReleaseToCancel hold={rxHold} />
      </footer>
      {rxSheet && <ReactionsSheet post={p} me={me} onClose={() => setRxSheet(false)} onProfile={(u) => { setRxSheet(false); onProfile(u); }} />}
      {noticeOpen && <CopyrightNoticeSheet post={p} me={me} onClose={() => setNoticeOpen(false)} />}
    </article>
  );
}

/* ══ DOWNLOADING A POST IMAGE ══════════════════════════════════════════════
   🟥 `<a download>` DOES NOT WORK CROSS-ORIGIN. The browser silently ignores the
      attribute for a URL on another origin and navigates instead — so a plain
      download link on a Firebase Storage URL opens the image rather than saving
      it. The only way to actually save the bytes is to fetch them and hand the
      browser a same-origin blob.

      That fetch needs CORS on the bucket. If it isn't configured the fetch
      throws, and rather than failing silently we fall back to opening the URL in
      a new tab — which at least gets the user to the file. To make the first
      path work everywhere, set a CORS rule on the bucket once:

        gsutil cors set cors.json gs://allo-c4cd5.firebasestorage.app
        # cors.json: [{"origin":["*"],"method":["GET"],"maxAgeSeconds":3600}]
*/
async function downloadImage(url, filename) {
  if (!url) return;
  try {
    const res = await fetch(url, { mode: 'cors' });
    if (!res.ok) throw new Error(String(res.status));
    const blob = await res.blob();
    const href = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = href;
    a.download = filename || 'allo-image.jpg';
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(href), 4000);
  } catch {
    // no CORS on the bucket — open it instead of pretending nothing happened
    try { window.open(url, '_blank', 'noopener'); } catch {}
  }
}

/* ══ THE POST VIEWER ═══════════════════════════════════════════════════════
   Deliberately NOT the comments pane. Comments is a thread with the post as a
   small header; this is the POST — the picture at the size the pane allows,
   arrow-through for a multi-image post, a download button on the image itself —
   with the thread underneath so you can still reply without leaving. Same right
   pane, different mode, and the pane is draggable so a photo can have as much
   room as you want to give it. */
function PostPane({ post, startIndex = 0, me, myPages, onProfile, onShare, onInsights, onOpenPostId, onDeleteComment, mute }) {
  const [i, setI] = useState(startIndex);

  const imgs = useMemo(() => {
    const list = (post?.media || []).filter((m) => m?.type === 'image');
    if (!list.length && post?.imageUrl) list.push({ id: 'legacy', url: post.imageUrl });
    return list;
  }, [post]);

  // a post can lose an image while you're looking at it (deleted, edited)
  useEffect(() => { if (i > imgs.length - 1) setI(Math.max(0, imgs.length - 1)); }, [imgs.length, i]);

  const video = videoUrlOf(post);
  const cur = imgs[i] || null;
  /* ❤️ past 500 likers the post's array cannot answer — the shared store can */
  const liked = useLikedByMe(post, me.uid);
  /* 😍 the counts with my unconfirmed reaction on them, and who reacted */
  const shown = useLiveReactions(post);
  const [rxSheet, setRxSheet] = useState(false);
  const [rxHold, setRxHold] = useState({ open: false, hover: null });
  const many = imgs.length > 1;
  const step = (d) => setI((n) => (n + d + imgs.length) % imgs.length);

  return (
    <>
      <div className="vb-right-body vb-postpane">
        {/* ── the media, as big as the pane allows ── */}
        {video ? (
          /* 🟥 THE VIDEO NEEDED A POSITIONED PARENT IT DID NOT HAVE. The
              still branch below has always had `.vb-pp-stage`; the video was a
              bare element, so an absolutely-positioned overlay on it would have
              escaped to the nearest positioned ancestor — the whole pane. */
          <div className="vb-pp-stage">
            <PostVideo
              post={post} className="vb-pp-media" src={video}
              poster={bestPostImg(post) || undefined} controls loop={false}
            />
            <Overlay post={post} me={me} interactive />
            {/* ©️ the label and the mute notice follow the clip into the pane */}
            <AiBadge post={post} size="md" long className="vb-on-media" />
            <MutedNotice post={post} myUid={me?.uid} className="vb-pp-muted" onExplain={mute?.explain} onOpenChat={mute?.openChat} onDisputed={mute?.disputed} />
          </div>
        ) : cur ? (
          <div className="vb-pp-stage">
            <SafeImg src={bestImg(cur)} className="vb-pp-img" label="Image unavailable" />
            {/* 🖼 this photo's own stickers; the caption and tint only on the first */}
            <Overlay key={`ov-${i}`} post={post} me={me} interactive mediaIndex={Math.max(0, (post.media || []).indexOf(cur))} />
            {/* ©️ the AI label follows the picture into the viewer */}
            <AiBadge post={post} size="md" long className="vb-on-media" />
            {many && (
              <>
                <button className="vb-pp-nav prev" onClick={() => step(-1)} aria-label="Previous">
                  <ChevronLeft width={20} height={20} />
                </button>
                <button className="vb-pp-nav next" onClick={() => step(1)} aria-label="Next">
                  <ChevronRight width={20} height={20} />
                </button>
                <span className="vb-pp-count">{i + 1} / {imgs.length}</span>
              </>
            )}
            <button
              className="vb-pp-dl"
              title="Download this image"
              onClick={() => downloadImage(bestImg(cur), `allo-${post.id}-${i + 1}.jpg`)}
            >
              <Download width={16} height={16} /> Download
            </button>
          </div>
        ) : null}

        {/* every image at once, when there is more than one */}
        {many && (
          <div className="vb-pp-strip">
            {imgs.map((m, n) => (
              <button
                key={m.id || n} className={`vb-pp-thumb ${n === i ? 'on' : ''}`}
                onClick={() => setI(n)} aria-label={`Image ${n + 1}`}
              >
                <SafeImg src={bestImg(m)} kind="inline" loading="lazy" />
              </button>
            ))}
          </div>
        )}

        {/* ── who, and what they said ── */}
        <div className="vb-pp-head">
          <button className="vb-who" onClick={() => onProfile({ uid: post.userId, name: post.userName, image: post.userImage, ...(post.authorType === 'page' ? { pageId: post.pageId } : {}) })}>
            <Pic src={post.userImage} name={post.userName} size={38} />
            <span>
              <b>{post.userName || 'Allo user'}<StatusLine status={post.status} feeling={post.feeling} /></b>
              <i>{ago(post.timestamp)}{nearLabelFor(post) ? ` · near ${nearLabelFor(post)}` : ''}</i>
            </span>
          </button>
        </div>

        {/* same duplicate, on the post page */}
        {post.content && !pollQuestionIsTheContent(post) ? (
          isStyledText(post)
            ? <div className="vb-pp-styled"><TextPostCard style={post.textStyle} text={post.content} /></div>
            : <p className="vb-pp-text">{post.content}</p>
        ) : null}
        {post.linkUrl ? <LinkCard p={post} /> : null}
        {/* ⭐ the opened view of a share shows the same embed the feed card does —
            and the thread below it is the SHARER's, which is the distinction the
            whole feature turns on. Tapping the embed swaps this pane for the
            original and its own thread. */}
        {isShare(post) ? (
          <SharedEmbed node={post.sharedFrom} onOpenPost={onOpenPostId} onProfile={onProfile} />
        ) : post.sharedPostId ? <EmbedGone /> : null}
        <MusicLine p={post} />

        <div className="vb-pp-acts">
          <ReactHeart p={post} me={me} liked={liked} onHold={setRxHold} count
            onLike={(pp, liked, rx) => { if (!liked) feedSignals.liked(pp.id); likeWithNotify(pp, me, liked, rx || null).catch(() => {}); }} />
          <span className="vb-act static"><Comment width={17} height={17} />{commentCountOf(post) || ''}</span>
          <button className="vb-act" onClick={() => onShare(post)}>
            <Forward width={17} height={17} />{post.sharesCount || ''}
          </button>
          {isMyPost(post, me) && (
            <button className="vb-views act" title="See who this reached" onClick={() => onInsights?.(post)}>
              <Eye2 width={14} height={14} />{postAudience(post).views}
            </button>
          )}
          <ReactionSummary post={shown} onOpen={() => setRxSheet(true)} className="vb-rx-right" showCount={false} />
          <ReleaseToCancel hold={rxHold} />
        </div>
        {rxSheet && <ReactionsSheet post={shown} me={me} onClose={() => setRxSheet(false)} onProfile={(u) => { setRxSheet(false); onProfile(u); }} />}

        {/* ── the thread, right here, so replying costs no navigation.
               Its own header ("33 comments" + the sort) is VibeComments'. ── */}
      </div>

        {/* 💬 ONE THREAD COMPONENT, THREE SURFACES. This markup used to exist
            three times, byte for byte — so replies, deletion and the "@name"
            prefix would have been three implementations to keep in step. */}
        <VibeComments
          post={post} me={me} myPages={myPages}
          onAdd={(text, replyingTo) => addComment(post, me, text, replyingTo)}
          onDelete={(c) => onDeleteComment && onDeleteComment(post, c)}
          onProfile={onProfile}
        />
    </>
  );
}

/* ══ POST INSIGHTS ═════════════════════════════════════════════════════════
   The app's AdvancedInsightsModal, in the right pane instead of a sheet — same
   numbers, same denominators, same sampling. Everything comes from
   services/vibeStats.js, which is a port of the app's own maths rather than a
   second opinion about it. Owner-only: the view count that opens this is only
   rendered on your own posts. */
function InsightsPane({ post, me }) {
  const [demo, setDemo] = useState(null);
  const [nums, setNums] = useState(null);
  const [busy, setBusy] = useState(true);

  const audience = useMemo(() => postAudience(post), [post]);

  /* 📊 the server's answer (insightsRoutes.js): every person who reached the post,
     described once — not a sample of 100 guessed from phone numbers */
  useEffect(() => {
    let live = true;
    setBusy(true); setDemo(null); setNums(null);
    fetchInsights(post.id)
      .then((r) => {
        if (!live) return;
        if (r) { setNums(r.numbers); setDemo(audienceForScreen(r.audience, navigator.language || 'en')); }
        else setDemo({ empty: true, unreachable: true });
        setBusy(false);
      })
      .catch(() => { if (live) { setDemo({ empty: true, unreachable: true }); setBusy(false); } });
    return () => { live = false; };
  }, [post.id]);

  const posted = post.timestamp?.toDate ? post.timestamp.toDate() : new Date(post.timestamp || 0);
  const rate = engagementRate(post);
  const likes = likesOf(post);
  const comments = commentsOf(post);
  const N = nums || { impressions: audience.impressions, reach: audience.reach, views: audience.views };
  const plural = (n, w) => `${n} ${w}${n === 1 ? '' : 's'}`;

  return (
    <div className="vb-right-body vb-ins">
      <div className="vb-ins-when">
        Posted {posted.toLocaleDateString(undefined, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
      </div>

      <div className="vb-ins-top">
        <div className="vb-ins-card">
          <span className="vb-ins-ico eye"><Eye2 width={20} height={20} /></span>
          <b>{N.impressions}</b>
          <i>Impressions</i>
        </div>
        <div className="vb-ins-card">
          <span className="vb-ins-ico ppl"><GroupIcon width={20} height={20} /></span>
          <b>{N.reach}</b>
          <i>Accounts reached</i>
        </div>
        <div className="vb-ins-card">
          <span className="vb-ins-ico eye"><Eye2 width={20} height={20} /></span>
          <b>{N.views}</b>
          <i>Views</i>
        </div>
        <div className="vb-ins-card">
          <span className="vb-ins-ico eng"><Heart width={19} height={19} /></span>
          <b>{rate}%</b>
          <i>Engagement rate</i>
        </div>
      </div>

      <div className="vb-ins-note">
        Impressions count every time the post was on a screen; accounts reached counts each person once;
        views are the people who stayed on it at least a second and a half (or played it).
        Engagement rate is ({plural(likes, 'like')} + {plural(comments, 'comment')}) ÷ {plural(N.reach, 'account')} reached.
        Your own looks and likes never count.
      </div>

      {busy ? (
        <div className="ct-empty subtle"><span>Reading the audience…</span></div>
      ) : demo?.unreachable ? (
        <div className="ct-empty subtle"><span>The audience could not be loaded right now — try again in a moment.</span></div>
      ) : !demo || !(demo.base > 0) ? (
        <div className="ct-empty subtle">
          <span>No one has seen this post yet — insights appear once it has an audience.</span>
        </div>
      ) : (
        <>
          {/* ── gender ── */}
          <div className="vb-ins-sec">
            <div className="ap-label">Gender</div>
            {demo.gender ? (
              <>
                <div className="vb-ins-bar">
                  <span style={{ flex: demo.gender.male || 0.001, background: '#4285F4' }} />
                  <span style={{ flex: demo.gender.female || 0.001, background: '#E91E63' }} />
                  <span style={{ flex: demo.gender.other || 0.001, background: '#9E9E9E' }} />
                </div>
                <div className="vb-ins-legend">
                  <span><em style={{ background: '#4285F4' }} />Male<b>{demo.gender.male}%</b></span>
                  <span><em style={{ background: '#E91E63' }} />Female<b>{demo.gender.female}%</b></span>
                  <span><em style={{ background: '#9E9E9E' }} />Other<b>{demo.gender.other}%</b></span>
                </div>
                <div className="vb-ins-den">of {plural(demo.genderBase, 'person')} who told us</div>
              </>
            ) : <div className="vb-ins-none">Not enough data</div>}
          </div>

          {/* ── age ── */}
          <div className="vb-ins-sec">
            <div className="ap-label">Age range</div>
            {demo.ageGroups ? (
              <>
                <div className="vb-ins-ages">
                  {Object.keys(demo.ageGroups).map((k) => {
                    const pct = demo.ageGroups[k] || 0;
                    return (
                      <div className="vb-ins-age" key={k}>
                        <b>{pct > 0 ? `${pct}%` : ''}</b>
                        <span><i style={{ height: `${Math.max(pct, 2)}%`, opacity: pct > 0 ? 1 : 0.22 }} /></span>
                        <u>{k}</u>
                      </div>
                    );
                  })}
                </div>
                <div className="vb-ins-den">of {plural(demo.ageBase, 'person')} with a birthday on file</div>
              </>
            ) : <div className="vb-ins-none">Not enough data</div>}
          </div>

          {/* ── where they are ── */}
          <div className="vb-ins-sec">
            <div className="ap-label">Top locations</div>
            {demo.countries?.length ? (
              <>
                {demo.countries.map((c, i) => (
                  <div className="vb-ins-loc" key={c.iso || `x${i}`}>
                    <span className="vb-ins-flag">{c.flag}</span>
                    <span className="vb-ins-locname">{c.other ? 'Other countries' : c.name}</span>
                    <span className="vb-ins-locbar"><i style={{ width: `${Math.max(c.pct, 3)}%` }} /></span>
                    <b>{c.pct}%</b>
                  </div>
                ))}
                <div className="vb-ins-den">
                  of all {plural(demo.base, 'person')} reached · location from their connection, or an
                  international phone number · IP geolocation by <a href="https://db-ip.com" target="_blank" rel="noreferrer">DB-IP</a>
                </div>
              </>
            ) : <div className="vb-ins-none">Not enough data</div>}
          </div>

          {demo.source === 'legacy' && (
            <div className="vb-ins-note">
              This post is older than the new audience tracking, so this describes the first viewers it recorded.
            </div>
          )}
        </>
      )}
    </div>
  );
}

/* ── the right squeeze pane ───────────────────────────────────────────────── */
/* 📰 `renderPost` IS THE FEED'S OWN CARD, HANDED IN RATHER THAN REBUILT.
   🟥 A PAGE SHOWED THUMBNAILS AND NOTHING ELSE. You could not read a page's
      words, answer its poll, play its video, like, comment or share without
      opening each tile — so a page was a contact sheet while a person's profile
      was a conversation. The fix is not a second card written in here: it would
      drift from the feed's within a week and every fix would have to be
      remembered twice. The screen already has the real one. */
function RightPane({ panel, me, myPages, feed, onClose, onOpenUser, onOpenPost, onOpenPostId, onShare, onInsights, onDrag, onResetWidth, renderPost, onDeleteComment, mute }) {
  const [profilePosts, setProfilePosts] = useState(null);
  const picRef = useRef(null);
  const [picBusy, setPicBusy] = useState(false);
  const [meta, setMeta] = useState(null);       // user meta (cover, names) or page doc
  /* 🟢 THE FOLLOWER LIST REPLACES THIS PANE RATHER THAN FLOATING OVER IT. The
     right pane is already the "one thing at a time" surface; a modal on top of
     it would have to re-explain its own close button, and the back arrow here
     returns to the page it came from, which is what a reader expects. */
  const [showFollowers, setShowFollowers] = useState(false);
  /* 🟥 CLOSED WHENEVER THE PANE CHANGES. Without this, opening a follower's
     profile from the list and coming back leaves the list still flagged open —
     so the next page opened shows somebody else's followers. */
  useEffect(() => { setShowFollowers(false); }, [panel]);
  const [coverBusy, setCoverBusy] = useState(false);
  const coverRef = useRef(null);
  /* 🔒 THE SETTINGS SHEET, closed whenever the pane changes — for the same
     reason `showFollowers` is: leaving it flagged open means the next profile
     you open shows somebody else's settings. */
  const [showSettings, setShowSettings] = useState(false);
  useEffect(() => { setShowSettings(false); }, [panel]);
  /* 📈 the creator dashboard, opened from the strip on my own profile */
  const [dashOpen, setDashOpen] = useState(false);
  useEffect(() => { setDashOpen(!!panel.dashboard); }, [panel]);
  const post = panel.post ? (feed.find((x) => x.id === panel.post.id) || panel.post) : null;
  const isPagePane = panel.type === 'profile' && !!panel.user?.pageId;
  /* 🟢 MY OWN PERSONAL PROFILE — not a page of mine, and not anybody else's.
     The gear and the badge are both about a setting that only exists on a
     person's account. */
  const isMine = panel.type === 'profile' && !isPagePane && panel.user?.uid === me.uid;
  /* 🪪 the details sheet: everything ('view') or my editor ('edit') */
  const [detailsSheet, setDetailsSheet] = useState(null);

  useEffect(() => {
    let live = true;
    setProfilePosts(null); setMeta(null);
    if (panel.type === 'profile' && panel.user?.pageId) {
      fetchPage(panel.user.pageId).then((pg) => live && setMeta(pg || {}));
      fetchPagePosts(panel.user.pageId).then((r) => live && setProfilePosts(r));
    } else if (panel.type === 'profile' && panel.user?.uid) {
      fetchProfileMeta(panel.user.uid).then((m) => live && setMeta(m || {}));
      fetchUserPosts(panel.user.uid).then((r) => live && setProfilePosts(r));
    }
    return () => { live = false; };
  }, [panel]);

  return (
    <aside className="vb-right">
      {/* drag the seam to give a photo more room — double-click snaps back */}
      <div
        className="vb-grip" title="Drag to resize · double-click to reset"
        onPointerDown={onDrag} onDoubleClick={onResetWidth}
      />

      <div className="ct-head">
        <div className="ct-tabs"><button className="on">
          {panel.type === 'insights' ? 'Post insights'
            : panel.type === 'post' ? 'Post'
              : panel.type === 'comments' ? 'Comments'
              : panel.type === 'video' ? 'Video'
                : panel.user?.name || 'Profile'}
        </button></div>
        <button className="icon-btn" onClick={onClose} aria-label="Close"><X /></button>
      </div>

      {panel.type === 'insights' && post && <InsightsPane post={post} me={me} />}

      {panel.type === 'post' && post && (
        <PostPane
          post={post} startIndex={panel.index || 0} me={me} myPages={myPages}
          onProfile={onOpenUser} onShare={onShare} onInsights={onInsights}
          onOpenPostId={onOpenPostId} onDeleteComment={onDeleteComment} mute={mute}
        />
      )}

      {panel.type === 'comments' && post && (
        <>
          <div className="vb-right-body">
            {/* 💬 a bell row's landing: the pane is opened with the comment id */}
            {/* 🟢 the post itself, on top — so the thread below has its context */}
            <div className="vb-cpost">
              <header>
                <Pic src={post.userImage} name={post.userName} size={34} />
                <span><b>{post.userName || 'Allo user'}<StatusLine status={post.status} feeling={post.feeling} /></b><i>{ago(post.timestamp)}{nearLabelFor(post) ? ` · near ${nearLabelFor(post)}` : ''}</i></span>
              </header>
              {post.content && !pollQuestionIsTheContent(post) ? <p className="vb-cpost-text">{post.content}</p> : null}
              {videoUrlOf(post) ? (
                <PostVideo
                  post={post} className="vb-cpost-media" src={videoUrlOf(post)}
                  poster={bestPostImg(post) || undefined} controls loop={false}
                />
              ) : (() => {
                const imgs = (post.media || []).filter((m) => m.type === 'image');
                if (!imgs.length && post.imageUrl) imgs.push({ url: post.imageUrl, id: 'legacy' });
                return imgs.length ? (
                  <div className={`vb-cpost-imgs n${Math.min(imgs.length, 4)}`}>
                    {imgs.slice(0, 4).map((m, i) => <SafeImg key={m.id || i} src={bestImg(m)} loading="lazy" />)}
                  </div>
                ) : null;
              })()}
              <div className="vb-cpost-meta">
                {likeCountOf(post)} like{likeCountOf(post) === 1 ? '' : 's'} · {commentCountOf(post)} comment{commentCountOf(post) === 1 ? '' : 's'}
              </div>
            </div>
          </div>
        {/* 💬 ONE THREAD COMPONENT, THREE SURFACES. This markup used to exist
            three times, byte for byte — so replies, deletion and the "@name"
            prefix would have been three implementations to keep in step. */}
        <VibeComments
          post={post} me={me} myPages={myPages} jumpTo={panel.commentId || null}
          onAdd={(text, replyingTo) => addComment(post, me, text, replyingTo)}
          onDelete={(c) => onDeleteComment && onDeleteComment(post, c)}
          onProfile={onOpenUser}
        />
        </>
      )}

      {/* 👥 THE FOLLOWER LIST TAKES OVER THE PANE. It sits before the profile
          branch so its `&&` short-circuits that one — rendering both and hiding
          the profile with CSS would keep its posts query alive underneath. */}
      {panel.type === 'profile' && panel.user && showFollowers && isPagePane && (
        <FollowersPane
          page={meta}
          myUid={me.uid}
          onBack={() => setShowFollowers(false)}
          onOpenUser={onOpenUser}
        />
      )}

      {panel.type === 'profile' && panel.user && !(showFollowers && isPagePane) && (
        <div className="vb-right-body prof">
          {/* ── cover, exactly like the app: the user's coverImage (or the app's
                 default art), the avatar overlapping its lower edge ── */}
          <div className="vb-cover">
            {/* 🟥 A PAGE WITH NO COVER DRAWS THE BAND ITSELF, no <img> at all.
                It used to fall back to the phone's Unsplash default — a remote
                photo on someone else's CDN, which is why an unset page cover
                could sit there as a strip of nothing while it failed to load.
                The gradient is the same one the app paints behind a page, so
                the two now agree, and it cannot fail. */}
            {(isPagePane ? meta?.coverImage : (meta?.coverImage || DEFAULT_COVER)) ? (
              <SafeImg
                src={isPagePane ? meta.coverImage : (meta?.coverImage || DEFAULT_COVER)}
                label="Cover unavailable"
              />
            ) : null}
            {/* 🟢 A PAGE YOU OWN CAN HAVE ITS COVER CHANGED HERE TOO. Until now
                only a personal profile could, so a page was stuck with whatever
                the band drew by default — on both platforms. */}
            {/* ⚙️ THE SETTINGS GEAR OWNS THE TOP-RIGHT CORNER NOW.
                🟥 THE CHANGE-COVER BUTTON WAS SITTING THERE, which is the one
                   corner a profile's settings belong in — the phone has had a
                   gear in exactly that position for as long as the privacy
                   setting has existed, and the browser had neither the gear nor
                   the setting. Cover moves to the corner below it: it is the
                   band's own control, so it belongs on the band's own edge, and
                   the avatar owns the bottom-LEFT so there is no collision. */}
            {isMine && (
              <button
                className={`vb-cover-gear ${showSettings ? 'on' : ''}`}
                title="Profile settings"
                aria-label="Profile settings"
                aria-expanded={showSettings}
                onClick={() => setShowSettings((v) => !v)}
              >
                <Gear width={16} height={16} />
              </button>
            )}
            {(isPagePane ? meta?.ownerUid === me.uid && !!meta?.id : panel.user.uid === me.uid) && (
              <>
                {/* 🟢 a round icon rather than a labelled pill: the label lives
                    in the tooltip, where it costs no width on a narrow pane. */}
                <button
                  className="vb-cover-edit" disabled={coverBusy}
                  title={coverBusy ? 'Uploading…' : 'Change cover'}
                  aria-label="Change cover"
                  onClick={() => coverRef.current?.click()}
                >
                  {coverBusy ? <Spinner width={15} height={15} /> : <ImageIcon width={15} height={15} />}
                </button>
                <input
                  ref={coverRef} type="file" accept="image/*" hidden
                  onChange={async (e) => {
                    const f = e.target.files?.[0]; e.target.value = '';
                    if (!f) return;
                    setCoverBusy(true);
                    try {
                      const url = isPagePane
                        ? await setPageCover(meta.id, f)
                        : await setCoverImage(me.uid, f);
                      setMeta((m) => ({ ...m, coverImage: url }));
                    } catch {}
                    setCoverBusy(false);
                  }}
                />
              </>
            )}
            <span className="vb-cover-ava">
              <Pic src={isPagePane ? (meta?.profileImage || panel.user.image) : (meta?.profileImage || panel.user.image)} name={panel.user.name} size={86} />
              {/* 🟢 A PAGE'S PHOTO IS CHANGEABLE FROM THE PAGE ITSELF. It could
                  only be set when the page was created, or by walking back
                  through the wizard on the phone — so the picture people
                  actually see was the hardest thing about a page to change. */}
              {isPagePane && meta?.ownerUid === me.uid && meta?.id && (
                <>
                  <button
                    className="vb-ava-edit" disabled={picBusy}
                    title={picBusy ? 'Uploading…' : 'Change page photo'}
                    aria-label="Change page photo"
                    onClick={() => picRef.current?.click()}
                  >
                    {picBusy ? <Spinner width={13} height={13} /> : <Camera width={13} height={13} />}
                  </button>
                  <input
                    ref={picRef} type="file" accept="image/*" hidden
                    onChange={async (e) => {
                      const f = e.target.files?.[0]; e.target.value = '';
                      if (!f) return;
                      setPicBusy(true);
                      try {
                        const url = await setPageImage(meta.id, f);
                        setMeta((m) => ({ ...m, profileImage: url }));
                      } catch {}
                      setPicBusy(false);
                    }}
                  />
                </>
              )}
              {/* 🔒 PUBLIC OR PRIVATE, ON THE PICTURE — the phone draws exactly
                  this badge, in exactly these two colours, and only ever on
                  YOUR OWN profile. It is a reminder of your own setting, not a
                  label other people get to read off your account. */}
              {isMine && (() => {
                const b = privacyBadge(meta?.isPrivate);
                return (
                  <span className={`vb-ava-privacy ${b.icon}`} title={b.label} aria-label={b.label}>
                    {b.icon === 'lock'
                      ? <Lock width={14} height={14} />
                      : <Globe width={14} height={14} />}
                  </span>
                );
              })()}
            </span>
          </div>

          {/* ⚙️ THE SETTINGS SHEET. Inline under the cover rather than a modal
              over the pane: the right pane is already the app's "one thing at a
              time" surface, and a dialog on top of it would have to re-explain
              its own close button. */}
          {isMine && showSettings && (
            <div className="vb-prof-settings">
              <b>Profile privacy</b>
              <i>Choose who can see your vibes on your profile.</i>
              {[['everyone', 'Everyone', Globe], ['private', 'Private (friends only)', Lock]].map(([key, label, Icon]) => {
                const on = privacyOf(meta) === key;
                return (
                  <button
                    key={key}
                    type="button"
                    className={`vb-priv-opt ${on ? 'on' : ''}`}
                    aria-pressed={on}
                    onClick={() => {
                      const next = privacyValue(key);
                      /* 🟢 SHOWN IMMEDIATELY, THEN WRITTEN. `meta` is a fetched
                         snapshot, not a live listener, so waiting for the write
                         to come back would leave the button you just pressed
                         looking unpressed for a round trip. */
                      setMeta((m) => ({ ...m, isPrivate: next }));
                      setProfilePrivacy(me.uid, next).catch(() => {});
                    }}
                  >
                    <Icon width={16} height={16} />
                    <span>{label}</span>
                    {on && <Check width={15} height={15} />}
                  </button>
                );
              })}
            </div>
          )}

          <div className="vb-prof-id">
            <b>
              {(isPagePane ? meta?.name : meta?.fullName) || panel.user.name || 'Allo user'}
              {!isPagePane && panel.user.uid ? <SupporterBadge uid={panel.user.uid} size={15} /> : null}
              {/* ✔️ read off the PAGE document, never off the name — a badge a
                  display name could earn is not a badge */}
              {isPagePane && isOfficialPage(meta) && (
                <span className="official-badge" title="Official Allo account">
                  <Verified width={15} height={15} />
                </span>
              )}
              {isPagePane && <em className="vb-pagechip">{meta?.category || 'Page'}</em>}
            </b>
            {!isPagePane && (meta?.username ? <i>@{meta.username}</i> : null)}
            {(isPagePane ? meta?.bio : meta?.bio) ? <p className="vb-prof-bio">{meta.bio}</p> : null}
            {/* 🪪 A PERSON'S OWN OPTIONAL ANSWERS, in the same row of facts a
                page gets. Only the public ones reach this component, because
                only the public ones are on the document it was read from. */}
            {/* 🪪 PROFILE DETAILS — work and school with their logos, places, the
                bio; each line with its own audience, and the server hands this
                viewer only their cut (ui/ProfileDetailsKit, services/profileDetails) */}
            {!isPagePane && panel.user?.uid && (
              <ProfileDetailsCard uid={panel.user.uid} isOwner={isMine} onSeeAll={() => setDetailsSheet('view')} onEdit={() => setDetailsSheet('edit')} />
            )}
            {!isPagePane && panel.user?.uid && detailsSheet && (
              <ProfileDetailsSheet uid={panel.user.uid} isOwner={isMine} startEditing={detailsSheet === 'edit'} name={meta?.name || panel.user?.name} onClose={() => setDetailsSheet(null)} />
            )}

            {/* 🟢 WHAT THIS ACCOUNT ACTUALLY IS, in four numbers. A profile that
                opens on a wall of squares makes you count them to find out
                whether anyone posts here; counting is the reader's job only
                when nobody did it for them. */}
            {/* 🟥 THE FOURTH NUMBER WAS SHAPED UNLIKE THE OTHER THREE. Three were
                inline and baseline-aligned ("1 posts"); the followers one was a
                button laid out as a centred column, at a different size, so it
                sat visibly off the line its neighbours shared. A row of counts
                where one count is built differently reads as a mistake however
                carefully the rest is spaced.
                🟢 SO ALL FOUR ARE ONE CELL SHAPE — number over label, centred,
                   evenly divided — and the clickable one differs in nothing but
                   its colour and its cursor. `<span>` and `<button>` are given
                   the same class rather than styled separately, which is what
                   stops them drifting apart again. */}
            <div className="vb-prof-stats">
              <span className="vb-stat"><b>{profilePosts === null ? '—' : profilePosts.length}</b><i>posts</i></span>
              <span className="vb-stat"><b>{profilePosts === null ? '—' : profilePosts.filter((x) => kindOf(x) === 'photo').length}</b><i>photos</i></span>
              <span className="vb-stat"><b>{profilePosts === null ? '—' : profilePosts.filter((x) => kindOf(x) === 'video').length}</b><i>videos</i></span>
              {/* 🟥 THE LIST'S LENGTH, NOT THE STORED COUNTER. `followersCount`
                  is written with increment(), and one that ran twice leaves a
                  number nobody can reconcile — a page whose counter had drifted
                  showed the wrong figure forever while the list beside it was
                  right.
                  🟢 AND IT OPENS. A count nobody can click is a dead end, and
                  this is the obvious thing to click. Disabled at zero: opening
                  an empty list to be told "no followers yet" is a worse answer
                  than the one already on screen. */}
              {isPagePane && (() => {
                const n = followerCountOf(meta);
                return (
                  <button
                    type="button"
                    className={`vb-stat vb-stat-open ${n === 0 ? 'flat' : ''}`}
                    disabled={n === 0}
                    onClick={() => setShowFollowers(true)}
                  >
                    <b>{n}</b><i>follower{n === 1 ? '' : 's'}</i>
                  </button>
                );
              })()}
            </div>

            {isPagePane ? (
              <>
                {/* real icons, chips, and a website that is a link rather than a
                    line of text with an emoji glued to the front of it */}
                <div className="vb-prof-facts">
                  {meta?.website ? (
                    <a
                      className="vb-fact"
                      href={/^https?:/i.test(meta.website) ? meta.website : `https://${meta.website}`}
                      target="_blank" rel="noreferrer"
                    >
                      <LinkIcon width={13} height={13} />{String(meta.website).replace(/^https?:\/\//i, '')}
                    </a>
                  ) : null}
                  {meta?.location ? <span className="vb-fact"><MapPin width={13} height={13} />{meta.location}</span> : null}
                  {meta?.phone ? <span className="vb-fact"><PhoneIcon width={13} height={13} />{meta.phone}</span> : null}
                  {meta?.hours ? <span className="vb-fact"><Timer width={13} height={13} />{meta.hours}</span> : null}
                  <ProfileFacts fields={meta?.profileFields} />
                </div>
                <div className="vb-prof-cta">
                  {meta?.ownerUid !== me.uid && meta?.id && (
                    <button
                      /* 🟥 NOT `lg-go`. That is the product's primary action button —
                         a filled blue pill that grows a coloured shadow on hover.
                         Following a page is not the most important thing on the
                         screen and should not be the loudest thing on it. */
                      className={`vb-follow ${amIFollowing(meta, me.uid) ? 'on' : ''}`}
                      /* 🟢 THE SHARED TOGGLE, so the browser and the phone write
                         the same two fields in the same operation. The local
                         patch stays here because this pane has no listener on
                         the page document — but it patches BOTH fields, because
                         the rules refuse a write that moves one without the
                         other and a UI that showed otherwise would be lying. */
                      onClick={async () => {
                        const on = amIFollowing(meta, me.uid);
                        const r = await toggleFollowPage(meta.id, me.uid, on);
                        if (!r.ok) return;
                        setMeta((m) => ({
                          ...m,
                          followers: on
                            ? (m.followers || []).filter((u) => u !== me.uid)
                            : [...(m.followers || []), me.uid],
                          followersCount: (m.followersCount || 0) + (on ? -1 : 1),
                        }));
                      }}
                    >
                      {amIFollowing(meta, me.uid) ? 'Following' : 'Follow'}
                    </button>
                  )}
                </div>
              </>
            ) : (
              /* 🔒 A LOCKED ACCOUNT HAS NO BUTTON, AND IS SAID SO PLAINLY. The
                 alternative — a button that fails when pressed — teaches people
                 to press it again. The service checks this too, and so does the
                 rule; this is only the half that is polite. */
              panel.user.uid !== me.uid && (
                acceptsFriendRequests(meta) ? (
                  <button
                    className="lg-go slim"
                    onClick={() => sendFriendRequest(me, panel.user).catch(() => {})}
                  >
                    <PersonAdd2 width={15} height={15} /> Add friend
                  </button>
                ) : (
                  <span className="vb-prof-note">They are not accepting friend requests</span>
                )
              )
            )}
          </div>

          {/* 🟥 "PAGES GET THE FEED; PEOPLE KEEP THE GRID" WAS THE WRONG CALL, and
              it is reversed here. The reasoning was that a page is a publication
              and a person's profile is a place to find one photo among many —
              true of both, which is exactly why both want both. A person with
              four posts had no way to read them in order; a page with two
              hundred pictures had the grid only as a second thought.
              🟢 One pane, one toggle, whoever the profile belongs to. It falls
              back to the grid alone when no card renderer was handed in, so a
              caller that has not been updated still shows something. */}
          {isMine && dashOpen ? (
            <CreatorDashboard onClose={() => setDashOpen(false)} onOpenPost={(id) => onOpenPostId && onOpenPostId(id)} />
          ) : (
            <>
          {isMine && <DashboardStrip onOpen={() => setDashOpen(true)} />}
          {isMine && <MemoriesStrip uid={me.uid} onOpen={(pp) => onOpenPost && onOpenPost(pp)} />}
          {renderPost
            ? <PostsPane posts={profilePosts} renderPost={renderPost} onOpen={(pp) => onOpenPost && onOpenPost(pp)}
                taggedOf={!isPagePane ? panel.user?.uid : null} myUid={me.uid} savedIds={isMine ? (me.savedPosts || []) : null} />
            : <ProfilePosts posts={profilePosts} onOpen={(pp) => onOpenPost && onOpenPost(pp)} />}
            </>
          )}
        </div>
      )}
    </aside>
  );
}

/* ── the reels player ─────────────────────────────────────────────────────────
   Full-height black pane in the squeeze slot. Every video post in the feed becomes
   a vertically scroll-snapped reel — the one in view plays, the rest pause. Each
   reel carries the poster's identity, like / comment / share / save on a right
   rail, and the caption over a bottom gradient. Comments and Share open as
   overlays INSIDE the pane. */

const videoUrlOf = (p) => (p?.media || []).find((m) => m.type === 'video')?.url || p?.videoUrl || null;

/* 🟢 A STABLE KEY FOR A ROW THAT IS NOT A POST. React keys the feed by post id;
   a shelf keyed by its index would be re-mounted — and its rail scrolled back to
   the start — every time the feed above it changed length. */
const REELS_ROW_ID = '__reels_row__';

/* How wide the reels pane must be for a 9:16 clip to reach top and bottom.
   REEL_RAIL mirrors --vb-rail-w in vibes.css — the two gutters are real width
   and have to be paid for. Capped so the feed beside it stays a feed: past that
   the arithmetic loses and a short band comes back, which is better than a
   column of nothing. */
const REEL_RAIL = 66;
const REEL_ASPECT = 9 / 16;
function reelsFitWidth(paneHeight) {
  const h = paneHeight || window.innerHeight || 800;
  const want = Math.round(h * REEL_ASPECT) + REEL_RAIL * 2;
  const hi = Math.min(Math.round(window.innerWidth * 0.66), window.innerWidth - 520);
  return Math.max(360, Math.min(want, Math.max(360, hi)));
}

function Reel({ p: rawP, me, saved, onLike, onComments, onShare, onSave, onProfile, onTag, mute, follow }) {
  /* 😍 my unconfirmed reaction shown here too (reactionLive) */
  const p = useLiveReactions(rawP);
  /* ➕ THE "+" ON THE AVATAR — follow the page or add the person without leaving
     the reel. A follow that went through shows a tick for a moment, then the
     badge goes (the screen's own state now says "hidden"). A page post asks
     once whether I already follow that page. */
  const followState = follow ? follow.stateOf(p) : 'hidden';
  const [followDone, setFollowDone] = useState(false);
  const [followBusy, setFollowBusy] = useState(false);
  const followTimer = useRef(0);
  useEffect(() => () => clearTimeout(followTimer.current), []);
  useEffect(() => { if (follow && p.pageId) follow.see(p); }, [p.pageId]);   // eslint-disable-line react-hooks/exhaustive-deps
  const onFollowClick = async (e) => {
    e.stopPropagation();
    if (followBusy || followDone || !follow) return;
    setFollowBusy(true);
    let ok = false;
    try { ok = (await follow.act(p)) === true; } catch { ok = false; }
    setFollowBusy(false);
    if (!ok) return;
    setFollowDone(true);
    clearTimeout(followTimer.current);
    followTimer.current = setTimeout(() => setFollowDone(false), REEL_FOLLOW_DONE_MS);
  };
  const showFollow = followState === 'add' || followDone;
  const [rxSheet, setRxSheet] = useState(false);
  /* ❤️ past 500 likers the post's array cannot answer — the shared store can */
  const liked = useLikedByMe(p, me.uid);
  /* 🔢 the four numbers under the rail's icons — one formatter, shared with the
     phone (services/postGrid), so the same clip reads the same on both */
  const counts = railCounts(p);
  /* ⚠️ / 🏷 read out of the document by the rules the phone and the admin
     server use — services/videoTags. Neither was typed by anybody. */
  const safety = postSafetyKind(p);
  const orphans = orphanHashtags(p);
  return (
    /* ══ THE REEL'S SHAPE ══════════════════════════════════════════════════
       Three columns: a black gutter, the video, and the gutter the buttons
       live in.

       🟥 THE BUTTONS ARE NEXT TO THE VIDEO, NOT ON IT. Floating them over the
          picture meant every one of them sat on somebody's face, and each
          needed a drop shadow to stay legible against whatever was moving
          underneath. Narrowing the video by ~70px on each side costs a little
          picture and buys a black column the icons read against cleanly — and
          the counts that used to hang underneath in white are gone with it. */
    <section className="vb-reel">
      <div className="vb-reel-stage">
        {/* 🟢 the post's OWN soundtrack, not the camera audio — PostVideo carries the
            added track and slaves it to this video's clock, the way the phone does.
            Play/pause on view and on click live in there too. */}
        <PostVideo
          post={p}
          src={videoUrlOf(p)}
          poster={bestPostImg(p) || undefined}
          autoPlayInView
          volumeScope="reels"
          holdToFast
          /* fill the pane rather than letterbox: up to ~28% off the sides is
             allowed, past that (a landscape clip) bars are the lesser evil */
          fitTolerance={0.28}
          onClick={(e) => { const v = e.currentTarget; if (v.paused) v.play().catch(() => {}); else v.pause(); }}
          onWatched={(post) => viewTracker.viewed(post)}
        />
        {/* 🎨 the filter, the stickers and the drawn-on caption — the browser
            had never rendered any of them; see ui/Overlay.jsx */}
        <Overlay post={p} me={me} interactive />
        {/* ©️ the AI label, top-left of the picture — the same pill the feed
            card and the post pane draw */}
        <AiBadge post={p} size="md" className="vb-on-media vb-reel-ai" />
        {/* bottom identity + caption */}
        <div className="vb-reel-foot">
          {/* ©️ ABOVE EVERYTHING ELSE IN THE STRIP, like the safety notice: a
              clip with its sound removed says so before it says anything else,
              and the author's Dispute is right there. */}
          <MutedNotice post={p} myUid={me?.uid} onExplain={mute?.explain} onOpenChat={mute?.openChat} onDisputed={mute?.disputed} />
          {/* ⚠️ FIRST IN THE STRIP. Beneath a clamped caption it would be a
              warning behind a "more" button, which is not a warning. */}
          <SafetyNotice kind={safety} />
          {/* 📊 THE AUTHOR'S OWN NUMBERS, and only the author's. Above the
              name, so a long caption can never push it off the picture — the
              same position the phone gives it. */}
          {hasAuthorResults(p, me?.uid) && <ReelResults post={p} />}
          {/* 🟥 THE REEL BYLINE WAS THE ONE THAT FORGOT `pageId`. Every other
              byline in this file spreads it for a page post — the feed card, the
              post pane, the search row, the share embed — and this one did not,
              so clicking Allo inside a reel opened the PERSONAL branch and
              offered "Add friend" for an account that is a page and cannot be
              friended. It also drew the owner's picture rather than the page's,
              and no verified badge at all. */}
          <button
            className="vb-who light"
            onClick={() => onProfile({
              uid: p.userId,
              name: p.userName,
              image: p.pageImage || p.userImage,
              ...(p.authorType === 'page' ? { pageId: p.pageId } : {}),
            })}
          >
            <Pic src={p.authorType === 'page' ? (p.pageImage || p.userImage) : p.userImage} name={p.userName} size={30} />
            <b>
              {p.userName || 'Allo user'}
              {p.authorType !== 'page' && p.userId ? <SupporterBadge uid={p.userId} size={13} /> : null}
              {p.authorType === 'page' && p.pageId === OFFICIAL_PAGE_ID && (
                <span className="official-badge" title="Official Allo account">
                  <Verified width={13} height={13} />
                </span>
              )}
            </b>
            <i>· {ago(p.timestamp)}</i>
          </button>
          {/* 📝 THE CAPTION, WITH A WAY TO READ THE REST OF IT.
              🟥 IT USED TO BE A BARE `<p>` UNDER A `max-height: 84px`, so a long
                 story was cut off at four lines with nothing to say so and no
                 way to reach the ending. Two lines and a "more" — and the tags
                 inside the sentence are links. */}
          <ReelCaption text={p.content} onTag={onTag} />
          {/* 🏷 whatever the caption did not already show — usually nothing */}
          <ReelTags tags={orphans} onTag={onTag} />
          <MusicLine p={p} light />
          {/* 👁 VIEWS, ON A VIDEO, FOR EVERYONE — the same rule the grid tiles
              follow and the phone's reels player already had. It is NOT the
              owner-only number on a feed card: that one is somebody's private
              analytics, this is the number a visitor came to a clip to see. */}
          <div className="vb-reel-views">
            <Eye2 width={13} height={13} />
            {tileViews(p)}
          </div>
        </div>
      </div>

      {/* 🟢 the speaker sits in the LEFT gutter, not on the picture — same reason
          the buttons moved out of it */}
      <VolumeControl scope="reels" className="vb-vol-reel" place="right" />

      {/* the action rail, in the black gutter beside the picture */}
      <div className="vb-rail">
        {/* the rail avatar opens the same thing the byline does, so it carries
            the same object — a page here and a person there would be two
            different destinations for one author */}
        <div className="vb-rail-who">
        <button
          className="vb-rail-btn who"
          title={p.userName}
          onClick={() => onProfile({
            uid: p.userId,
            name: p.userName,
            image: p.pageImage || p.userImage,
            ...(p.authorType === 'page' ? { pageId: p.pageId } : {}),
          })}
        >
          <Pic src={p.userImage} name={p.userName} size={38} />
        </button>
        {showFollow && (
          <button
            type="button"
            className={`vb-follow-badge ${followDone ? 'done' : ''}`}
            onClick={onFollowClick}
            disabled={followBusy}
            aria-label={p.pageId ? 'Follow' : 'Add friend'}
            title={p.pageId ? 'Follow' : 'Add friend'}
          >
            {followDone ? '✓' : '+'}
          </button>
        )}
        </div>
        {/* 🔢 THE NUMBER UNDER THE ICON, as the phone's reel has always had.
            A rail of four unlabelled glyphs tells you nothing about the clip you
            are watching — and the counts are the one thing a reel is judged by.
            `commentCountOf` is the shared rule (the array first, the stored
            counter second), so this cannot disagree with the comments sheet. */}
        {/* 😍 THE FEED'S BUTTON: click likes, hover (or hold and slide) picks a
            kind from the tray that opens to the left of the rail. The number
            under it opens who reacted. */}
        <div className="vb-rail-rx">
          <ReactHeart p={rawP} me={me} liked={liked} onLike={onLike} placement="left" dark className="vb-rail-btn" size={25} />
          <button type="button" className="vb-rail-count" onClick={() => setRxSheet(true)} disabled={!likeCountOf(p)}
            title={`${likeCountOf(p)} reaction${likeCountOf(p) === 1 ? '' : 's'}`}><b>{counts.likes}</b></button>
          {rxSheet && <ReactionsSheet post={p} me={me} onClose={() => setRxSheet(false)} onProfile={(u) => { setRxSheet(false); onProfile && onProfile(u); }} />}
        </div>
        <button
          className="vb-rail-btn" onClick={() => onComments(p)}
          title={`${commentCountOf(p)} comment${commentCountOf(p) === 1 ? '' : 's'}`}
          aria-label="Comments"
        >
          {/* 💬 filled when there is something to read */}
          {counts.comments ? <CommentFill width={24} height={24} /> : <Comment width={24} height={24} />}
          <b>{counts.comments}</b>
        </button>
        <button className="vb-rail-btn" onClick={() => onShare(p)} title="Share" aria-label="Share">
          <Forward width={24} height={24} />
          <b>{counts.shares}</b>
        </button>
        <button
          className={`vb-rail-btn ${saved ? 'saved' : ''}`} onClick={() => onSave(p, saved)}
          title={saved ? 'Saved' : 'Save'} aria-label={saved ? 'Unsave' : 'Save'}
        >
          {saved ? <BookmarkFill width={23} height={23} /> : <Bookmark width={23} height={23} />}
          <b>{counts.saves}</b>
        </button>
      </div>
    </section>
  );
}

/* ══ A PROFILE'S POSTS ═════════════════════════════════════════════════════
   🟥 THE OLD GRID ONLY UNDERSTOOD ONE KIND OF POST. It drew `p.imageUrl` and,
      failing that, sixty characters of grey caption — so a video showed a
      caption, a link showed a caption, a styled text post lost its background,
      and an album lost every picture after the first. Half of a profile was a
      wall of grey rectangles.

   Every kind now draws as itself, in one square so the grid still reads as a
   grid: a photo is the photo, a video is its poster with a play badge, a LINK
   is a small link card with its site and headline, and a TEXT post wears the
   background it was written on. Nothing needs a caption to be recognisable. */
function ProfileTile({ p, onOpen }) {
  /* 🟢 THE SHARED RULE, NOT THIS FILE'S OWN. `kindOf` in vibeSearch answers the
     same question for the search filters and always did; the phone had no
     equivalent at all, which is how its grid ended up drawing a poll as a text
     post. Both tiles now ask services/postGrid — the module they already share
     for thumbnails and view counts — so neither can drift again. */
  const kind = postKind(p);
  const img = bestPostImg(p);
  const count = (p.media || []).length || (p.imageUrl ? 1 : 0);
  const likes = likeCountOf(p);
  const comments = commentCountOf(p);
  const banner = p.linkImage && !/s2\/favicons|favicon|apple-touch-icon/i.test(p.linkImage) ? p.linkImage : null;
  const css = kind === 'text' ? textPostCss(p.textStyle) : null;
  /* 🟢 THE CLIP IS ITS OWN THUMBNAIL. Nothing in this product ever stored a
     poster frame, so `bestPostImg` has nothing to return for a video and every
     video tile was a grey square. `preload="metadata"` plus a `#t=` fragment
     asks the browser for the opening bytes and the frame at that timestamp —
     one range request, no upload pipeline, and it is a picture of the actual
     video rather than a guess at one. `0.1` rather than `0`: the very first
     frame of a phone recording is frequently black. */
  const clip = kind === 'video' && !img
    ? ((p.media || []).find((m) => m?.type === 'video')?.url || p.videoUrl || null)
    : null;
  /* 👁️ 🟥 SHOWN TO EVERYONE, WHICH IS A DELIBERATE DEPARTURE. The feed card
     shows views to the post's OWNER alone — that is a rule about somebody's own
     analytics and it stays. A view count under a video in a grid is a different
     thing: it is what every video product shows and what a visitor came to the
     profile to see. Videos only — a number on every square is wallpaper. */
  const views = showsViews(p) ? compactViews(tileViews(p)) : null;
  const poll = kind === 'poll' ? pollPreview(p) : null;

  return (
    <button className={`vb-tile k-${kind}`} onClick={() => onOpen(p)} title={p.content || p.pollQuestion || p.linkTitle || ''}>
      {kind === 'photo' || kind === 'video' ? (
        <>
          {img
            ? <SafeImg src={img} kind="inline" loading="lazy" />
            : clip
              ? <video src={`${clip}#t=0.1`} preload="metadata" muted playsInline tabIndex={-1} />
              : <span className="vb-tile-empty">{KIND_ICON[kind]}</span>}
          {kind === 'video' && <span className="vb-tile-play"><Play width={13} height={13} /></span>}
          {count > 1 && <span className="vb-tile-badge">{count}</span>}
          {views !== null && (
            <span className="vb-tile-views"><Eye2 width={11} height={11} />{views}</span>
          )}
          {p.content ? <span className="vb-tile-cap">{p.content}</span> : null}
        </>
      ) : kind === 'poll' ? (
        /* 📊 the question and the answers — a poll tile that shows neither is
           indistinguishable from an empty post, which is exactly what it was.

           🟥 NO BARS HERE, DELIBERATELY. Everywhere else a poll refuses to draw
              a result until you have answered it, because seeing what the
              majority already thinks changes what people pick. A tile is not
              exempt: a filled bar in a 150px square leaks the same thing the
              feed card is careful not to. The rows are flat; the only number is
              the turnout, which the card shows before you vote too. */
        <span className="vb-tile-poll">
          <b>{poll.question || 'Poll'}</b>
          <span className="vb-tile-poll-bars">
            {poll.options.map((o) => (
              <span className="vb-tile-poll-bar" key={o.id}>
                <span>{o.text}</span>
              </span>
            ))}
          </span>
          <span className="vb-tile-poll-n">
            {poll.more > 0 ? `+${poll.more} more · ` : ''}
            {poll.voters === 1 ? '1 vote' : `${poll.voters} votes`}
          </span>
        </span>
      ) : kind === 'link' ? (
        <span className="vb-tile-link">
          <span className="vb-tile-link-top">
            {banner
              ? <SafeImg src={banner} kind="inline" loading="lazy" />
              : <span className="vb-tile-link-glyph"><LinkIcon width={20} height={20} /></span>}
          </span>
          <span className="vb-tile-link-body">
            <em>{p.linkDomain || 'link'}</em>
            <b>{p.linkTitle || p.content || p.linkUrl}</b>
          </span>
        </span>
      ) : (
        /* the card it was actually written on, at tile scale — the stored style
           is ids, so this is the same catalogue the composer and feed use */
        <span className="vb-tile-text" style={css?.wrap}>
          <span style={{ color: css?.text?.color, fontWeight: p.textStyle?.bold ? 800 : 650 }}>
            {String(p.content || '').slice(0, 140)}
          </span>
        </span>
      )}

      {/* the numbers stay out of the way until you go looking for them */}
      <span className="vb-tile-meta">
        <i><HeartFill width={12} height={12} />{likes}</i>
        <i><Comment width={12} height={12} />{comments}</i>
      </span>
    </button>
  );
}

/**
 * A profile's posts, as the feed draws them, with the grid one click away.
 *
 * 🟢 THE LIST IS THE DEFAULT because posts are written to be read in order; the
 *    grid is how you find one picture among two hundred. Both stay, on a page
 *    and on a person alike — the two were split for a while and neither half
 *    was better for it.
 */
function PostsPane({ posts, renderPost, onOpen, taggedOf = null, myUid = null, savedIds = null }) {
  const [view, setView] = useState('list');
  /* 🔖 SAVED — my own page only: every kind of post I saved, newest save first */
  const [saved, setSaved] = useState(null);
  const savedKey = (savedIds || []).join(',');
  useEffect(() => {
    if (view !== 'saved' || !savedIds) return undefined;
    let live = true;
    setSaved(null);
    fetchPostsByIds(savedIds.slice().reverse()).then((r) => live && setSaved(r)).catch(() => live && setSaved([]));
    return () => { live = false; };
  }, [view, savedKey]); // eslint-disable-line react-hooks/exhaustive-deps
  /* 🏷 TAGGED — the posts this person is shown in (tagsApproved is server-written,
     so only tags they allowed). Asked for when the chip is first clicked. */
  const [tagged, setTagged] = useState(null);
  useEffect(() => { setTagged(null); setView('list'); }, [taggedOf]);
  useEffect(() => {
    if (view !== 'tagged' || !taggedOf || tagged !== null) return undefined;
    let live = true;
    fetchTaggedPosts(taggedOf, myUid).then((r) => live && setTagged(r)).catch(() => live && setTagged([]));
    return () => { live = false; };
  }, [view, taggedOf, myUid, tagged]);
  if (posts === null) return <div className="ct-empty subtle"><span>Loading posts…</span></div>;
  return (
    <>
      <div className="vb-chips vb-prof-tabs">
        <button className={`vb-chip ${view === 'list' ? 'on' : ''}`} onClick={() => setView('list')}>Posts<i>{posts.length}</i></button>
        <button className={`vb-chip ${view === 'grid' ? 'on' : ''}`} onClick={() => setView('grid')}>Grid</button>
        {taggedOf && <button className={`vb-chip ${view === 'tagged' ? 'on' : ''}`} onClick={() => setView('tagged')}><Tag width={12} height={12} /> Tagged</button>}
        {savedIds && <button className={`vb-chip ${view === 'saved' ? 'on' : ''}`} onClick={() => setView('saved')}><Bookmark width={12} height={12} /> Saved<i>{savedIds.length}</i></button>}
      </div>
      {view === 'saved'
        ? (saved === null
          ? <div className="ct-empty subtle"><span>Loading…</span></div>
          : !saved.length
            ? <div className="ct-empty subtle"><span>Posts you save appear here — only you can see them.</span></div>
            : <div className="vb-pagefeed">{saved.map((p) => renderPost(p))}</div>)
        : view === 'tagged'
        ? (tagged === null
          ? <div className="ct-empty subtle"><span>Loading…</span></div>
          : !tagged.length
            ? <div className="ct-empty subtle"><span>No tagged posts yet.</span></div>
            : <div className="vb-pagefeed">{tagged.map((p) => renderPost(p))}</div>)
        : !posts.length
          ? <div className="ct-empty subtle"><span>No posts yet.</span></div>
          : view === 'list'
            ? <div className="vb-pagefeed">{posts.map((p) => renderPost(p))}</div>
            : <ProfilePosts posts={posts} onOpen={onOpen} />}
    </>
  );
}

function ProfilePosts({ posts, onOpen }) {
  const [tab, setTab] = useState('all');
  const counts = useMemo(() => {
    const c = { all: posts?.length || 0 };
    (posts || []).forEach((p) => { const k = kindOf(p); c[k] = (c[k] || 0) + 1; });
    return c;
  }, [posts]);
  const shown = useMemo(
    () => (tab === 'all' ? (posts || []) : (posts || []).filter((p) => kindOf(p) === tab)),
    [posts, tab],
  );
  // a tab that empties itself when the list reloads would strand you on nothing
  useEffect(() => { if (tab !== 'all' && !counts[tab]) setTab('all'); }, [counts, tab]);

  if (posts === null) return <div className="ct-empty subtle"><span>Loading posts…</span></div>;
  if (!posts.length) return <div className="ct-empty subtle"><span>No posts yet.</span></div>;

  return (
    <>
      <div className="vb-chips vb-prof-tabs">
        {KINDS.filter((k) => k.id === 'all' || counts[k.id]).map((k) => (
          <button
            key={k.id}
            className={`vb-chip ${tab === k.id ? 'on' : ''}`}
            onClick={() => setTab(k.id)}
          >
            {k.id === 'all' ? 'All' : k.name}<i>{counts[k.id] || 0}</i>
          </button>
        ))}
      </div>
      <div className="vb-tiles">
        {shown.map((p) => <ProfileTile key={p.id} p={p} onOpen={onOpen} />)}
      </div>
    </>
  );
}

/* ══ SEARCH ════════════════════════════════════════════════════════════════
   The matched words are marked up rather than merely present: at a glance you
   want to see WHY a row came back, especially when the hit is in a link's title
   or an author's name rather than in the caption you were reading. */
function Hi({ text, q }) {
  const parts = useMemo(() => highlightParts(text, q), [text, q]);
  return <>{parts.map((p, i) => (p.hit ? <mark key={i}>{p.text}</mark> : <span key={i}>{p.text}</span>))}</>;
}

const KIND_ICON = {
  video: <VideoIcon width={12} height={12} />,
  photo: <ImageIcon width={12} height={12} />,
  poll: <Poll width={12} height={12} />,
  link: <LinkIcon width={12} height={12} />,
  text: <DocText width={12} height={12} />,
};

/** One hit. Deliberately a compact row and not a full post card: a result list
 *  is for choosing, and ten cards is a feed you have to scroll rather than a
 *  set of answers you can compare. */
function SearchRow({ p, q, onOpen, onProfile }) {
  const thumb = bestPostImg(p);
  const title = p.linkTitle || p.content || p.linkDomain
    || (p._kind === 'video' ? 'Video' : p._kind === 'photo' ? 'Photo' : 'Post');
  return (
    <div className="vb-sr">
      <button className="vb-sr-open" onClick={() => onOpen(p)}>
        <span className="vb-sr-thumb">
          {thumb
            ? <SafeImg src={thumb} kind="inline" loading="lazy" />
            : <span className={`vb-sr-blank k-${p._kind}`}>{KIND_ICON[p._kind]}</span>}
          {p._kind === 'video' && <span className="vb-sr-play"><Play width={9} height={9} /></span>}
        </span>
        <span className="vb-sr-main">
          <b><Hi text={String(title).slice(0, 160)} q={q} /></b>
          {p.linkTitle && p.content ? <em><Hi text={p.content.slice(0, 120)} q={q} /></em> : null}
          <i>
            <span className="vb-sr-kind">{KIND_ICON[p._kind]}</span>
            {p.linkDomain ? <><Hi text={p.linkDomain} q={q} /> · </> : null}
            {ago(p.timestamp)}
          </i>
        </span>
      </button>
      <button
        className="vb-sr-who"
        title={p.userName}
        onClick={() => onProfile({ uid: p.userId, name: p.userName, image: p.userImage, ...(p.authorType === 'page' ? { pageId: p.pageId } : {}) })}
      >
        <Pic src={p.userImage} name={p.userName} size={26} />
        <span><Hi text={p.userName || 'Allo user'} q={q} /></span>
      </button>
    </div>
  );
}

function SearchResults({ q, kind, setKind, counts, results, people, loading, onOpen, onProfile, tag, tagCount }) {
  return (
    <div className="vb-results">
      {/* 🏷 A TAG SAYS HOW BIG IT IS, the way every other product does: the name,
          then how many videos carry it — an exact number from an aggregate, not
          the length of the page that happened to come back.
          🟢 A DASH UNTIL IT LANDS. Printing "0 videos" while the round trip is
             in flight states something false about somebody's tag. */}
      {tag ? (
        <div className="vb-tag-head">
          <b>{`#${tag}`}</b>
          <i>{tagCount === null ? '—' : `${tagCount} ${tagCount === 1 ? 'video' : 'videos'}`}</i>
        </div>
      ) : null}
      <div className="vb-chips">
        {KINDS.map((k) => (
          <button
            key={k.id}
            className={`vb-chip ${kind === k.id ? 'on' : ''}`}
            onClick={() => setKind(k.id)}
            disabled={k.id !== 'all' && !counts[k.id]}
          >
            {k.name}
            {counts[k.id] ? <i>{counts[k.id]}</i> : null}
          </button>
        ))}
      </div>

      {people.length > 0 && (
        <div className="vb-sr-people">
          {people.map((u) => (
            <button key={u.uid} className="vb-sr-person" onClick={() => onProfile(u)}>
              <Pic src={u.image} name={u.name} size={34} />
              <b><Hi text={u.name} q={q} /></b>
            </button>
          ))}
        </div>
      )}

      {loading && (
        <div className="vb-sr-note"><Spinner width={15} height={15} /> Searching Vibes…</div>
      )}

      {!loading && results.length === 0 && (
        <div className="ct-empty">
          <span>
            Nothing matches “{q.trim()}”.
            {/* 🟥 SAY WHAT WAS SEARCHED. This looks at recent posts, not all of
                history — a search that quietly stops at a horizon and never
                admits it is a search you learn not to trust. */}
            <br />
            <small>Vibes searches recent posts — captions, links, people, places and tracks.</small>
          </span>
        </div>
      )}

      {results.map((p) => (
        <SearchRow key={p.id} p={p} q={q} onOpen={onOpen} onProfile={onProfile} />
      ))}
    </div>
  );
}

function ReelComments({ post, me, myPages, onClose, onDeleteComment, onProfile }) {
  return (
    <div className="vb-overlay" onClick={onClose}>
      <div className="vb-sheet" onClick={(e) => e.stopPropagation()}>
        {/* 💬 the count, the order and the way out all belong to one place:
            the thread's own header below, and the overlay's click-away. */}
        <div className="vb-sheet-body">
        </div>
        {/* 💬 ONE THREAD COMPONENT, THREE SURFACES. This markup used to exist
            three times, byte for byte — so replies, deletion and the "@name"
            prefix would have been three implementations to keep in step. */}
        <VibeComments
          post={post} me={me} myPages={myPages}
          onAdd={(text, replyingTo) => addComment(post, me, text, replyingTo)}
          onDelete={(c) => onDeleteComment && onDeleteComment(post, c)}
          onProfile={onProfile}
        />
      </div>
    </div>
  );
}

/** Share a reel into a chat — recent chats first, or search anyone by name. Sends the
 *  app's own `shared_post` card, so it renders identically on the phones. */
/* ══ SHARE TO… ═════════════════════════════════════════════════════════════
   Rebuilt to the app's own structure (renderShareModalContent), because the flat
   avatar → name → send list read as a wall of rows with no shape to it:

     • a real preview of the post being shared, at the top (PostMediaHero)
     • two tabs — Recent chats / Search users — instead of one mixed list
     • Note to Self pinned first, with the NOTE glyph and the verified check

   🟥 TWO BUGS THIS FIXES, both from the old markup:
      1. Note to Self said "Allo user". `nameOf` walked participants looking for
         someone who isn't me — in a self-chat there is nobody, so it fell to the
         default. It is now recognised (isNoteToSelf) and pinned, never derived.
      2. One row's avatar was grey while the rest were not. The chat rows used
         <Avatar> (whose placeholder is the grey-blue gradient) and the people
         rows used <Pic> (solid blue) — two components, two placeholders, in one
         list. Everything here now draws through ChatAvatar/Avatar, one look. */

/** The post's own media, as the sheet's header — the app shows this so you can
 *  see WHAT you're sending before you pick who to send it to. */
/* ══ THE POST, QUOTED ════════════════════════════════════════════════════════
   🟥 THE SHARE SHEET SHOWED A CROPPED STRIP OF THE PHOTO AND NOTHING ELSE. You
      were about to put somebody's post on your own feed and the sheet showed
      you neither who wrote it, nor where they were, nor what the poll said, nor
      whether anyone had answered it. A preview that omits the byline is a
      preview of the media, not of the post.

   🟢 SO IT IS THE POST, cut out of the feed: the same byline with the same
      page-aware identity, the same words, the same poll, the same media, and
      the same three numbers — in the order the feed card draws them. The rules
      it reads come from services/postGrid, so the phone's copy of this and this
      one cannot become two different quotations of one post. */
function SharePostCard({ post, onProfile }) {
  const a = authorOf(post);
  const meta = previewMeta(post);
  const counts = postCounts(post);
  const kind = postKind(post);
  const poll = kind === 'poll' ? pollPreview(post, 4) : null;
  const imgs = (post?.media || []).filter((m) => m?.type === 'image');
  const vid = (post?.media || []).find((m) => m?.type === 'video') || (post?.videoUrl ? { url: post.videoUrl } : null);
  if (!imgs.length && post?.imageUrl && !vid) imgs.push({ url: post.imageUrl, id: 'legacy' });
  const hidden = Math.max(0, imgs.length - 4);

  return (
    <article className="vb-spc">
      <header>
        {/* 🟥 THE PAGE IS THE AUTHOR ON A PAGE POST — its name and its picture,
            never the publishing account's. Sharing is the one place credit is
            the entire point, so this reads `authorOf` rather than the fields. */}
        <button
          type="button" className="vb-spc-who"
          onClick={() => onProfile?.({ uid: post.userId, name: a.name, image: a.image, ...(a.pageId ? { pageId: a.pageId } : {}) })}
        >
          <Pic src={a.image} name={a.name} size={38} />
          <span>
            <b>
              {a.name}
              {a.pageId === OFFICIAL_PAGE_ID && (
                <span className="official-badge" title="Official Allo account"><Verified width={13} height={13} /></span>
              )}
            </b>
            <i>
              {ago(post.timestamp)}
              {nearLabelFor(post) ? ` · near ${nearLabelFor(post)}` : ''}
            </i>
          </span>
        </button>
        {meta.audience === 'friends' && (
          <span className="vb-spc-aud" title="Friends only"><GroupIcon width={13} height={13} /></span>
        )}
      </header>

      {/* the words, on the card they were written on when there is one */}
      {post.content ? (
        isStyledText(post)
          ? <TextPostCard style={post.textStyle} text={post.content} />
          : <p className="vb-spc-text">{post.content}</p>
      ) : null}

      {/* 📊 THE POLL, ABOVE ANY MEDIA — the question is the post, which is the
          order the feed card uses for the same reason.
          🟥 FLAT ROWS, NEVER FILLED. Everywhere else a poll hides its split
             until you have answered it; a preview inside a share sheet is not
             the place to leak it. The turnout is the only number. */}
      {poll && (
        <div className="vb-spc-poll">
          <b>{poll.question || 'Poll'}</b>
          {poll.options.map((o) => <span key={o.id}>{o.text}</span>)}
          <i>
            {poll.more > 0 ? `+${poll.more} more · ` : ''}
            {poll.voters === 1 ? '1 vote' : `${poll.voters} votes`}
          </i>
        </div>
      )}

      {post.linkUrl ? <LinkCard p={post} /> : null}

      {/* 🟥 A POSTER, NOT A PLAYER. A sheet that starts playing a clip behind a
          caption box is a sheet nobody can write in. */}
      {vid ? (
        <div className="vb-spc-media vb-spc-vid">
          <SafeImg src={bestPostImg(post) || undefined} loading="lazy" label="Video" />
          <span className="vb-spc-play"><Play width={15} height={15} /></span>
        </div>
      ) : imgs.length ? (
        <div className={`vb-spc-media vb-imgs n${Math.min(imgs.length, 4)}`}>
          {imgs.slice(0, 4).map((m, i) => <SafeImg key={m.id || i} src={bestImg(m)} loading="lazy" />)}
          {hidden > 0 && <span className="vb-img-more">+{hidden}</span>}
        </div>
      ) : null}

      {/* 🟢 THE THREE NUMBERS, because "is this worth sharing?" is partly "has
          anybody else thought so". Read through `postCounts`, which knows that
          shares live in an array on old documents and a counter on new ones. */}
      <footer className="vb-spc-counts">
        <i><HeartFill width={13} height={13} />{counts.likes}</i>
        <i><Comment width={13} height={13} />{counts.comments}</i>
        <i><Forward width={13} height={13} />{counts.shares}</i>
      </footer>
    </article>
  );
}

function ShareRow({ label, sub, avatar, state, onClick, verified }) {
  return (
    <button className="vb-srow" onClick={onClick} disabled={state === 'ok' || state === 'sending'}>
      {avatar}
      <span className="vb-srow-who">
        <b>{label}{verified && <Verified width={13} height={13} />}</b>
        <i>{sub}</i>
      </span>
      <span className={`vb-sendtag ${state || ''}`}>
        {state === 'ok' ? <Check width={15} height={15} />
          : state === 'sending' ? <span className="vb-sending" />
            : state === 'err' ? 'Retry'
              : <Send width={15} height={15} />}
      </span>
    </button>
  );
}

function ShareSheet({ post, chats, me, myUid, onClose, onShared }) {
  /* ⭐ TWO DESTINATIONS, AND THE FEED IS THE FIRST ONE. Sharing onward to your own
     feed is the loud, public action and the one people come to this sheet for;
     sending to one chat is the quiet one. Facebook orders them the same way, and
     the chat list keeps working exactly as it did underneath. */
  const [dest, setDest] = useState('feed');    // feed | chat
  const [caption, setCaption] = useState('');
  const [posting, setPosting] = useState(false);
  const [postErr, setPostErr] = useState('');
  const [tab, setTab] = useState('chats');     // chats | search
  const [q, setQ] = useState('');
  const [found, setFound] = useState(null);
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState({});        // chatId → 'sending' | 'ok' | 'err'

  useEffect(() => {
    let live = true;
    const t = q.trim();
    if (tab !== 'search' || t.length < 2) { setFound(null); setBusy(false); return undefined; }
    setBusy(true);
    const timer = setTimeout(() => {
      searchUsers(t, myUid)
        .then((r) => { if (live) { setFound(r || []); setBusy(false); } })
        .catch(() => { if (live) { setFound([]); setBusy(false); } });
    }, 250);
    return () => { live = false; clearTimeout(timer); };
  }, [q, myUid, tab]);

  const selfChatId = [myUid, myUid].join('_');
  const noteChat = useMemo(
    () => (chats || []).find((c) => isNoteToSelf(c, myUid))
      || { id: selfChatId, participants: [myUid], isGroup: false },
    [chats, myUid, selfChatId],
  );

  // the recents, minus Note to Self (it is pinned above) and the AI bot, which
  // the app also filters out of this list
  const recent = useMemo(() => (chats || [])
    .filter((c) => !isNoteToSelf(c, myUid))
    .filter((c) => c.id !== 'allo-ai-bot' && !(c.participants || []).includes('allo-ai-bot'))
    /* 🟥 NEVER A GUEST CHAT. A vibe post is Allo content — it opens a profile, a
       comment thread and a feed the guest has no account for, and the post would
       outlive the temporary chat it was sent into. The share sheet simply does
       not offer them; a disabled row would only invite the question. */
    .filter((c) => !isGuestChat(c))
    /* 💬 NOR THE OFFICIAL ALLO ACCOUNT. Sharing a vibe post there writes a
       message straight into the support thread, past /api/support/submit — so
       it reaches the ticket queue as a post card with no question attached,
       and can land while the composer is meant to be locked. Same treatment as
       a guest chat: not offered at all, rather than offered and refused. */
    .filter((c) => !isOfficialChat(c))
    .slice(0, 30), [chats, myUid]);

  const sendTo = async (chat) => {
    if (sent[chat.id] === 'ok' || sent[chat.id] === 'sending') return;
    /* 🚫 NOT ACROSS A BLOCK — the same door as the forward picker. */
    const peer = chat?.isGroup ? null : otherParticipant(chat, myUid);
    if (peer && peer !== myUid) {
      const nm = chat?.participantNames?.[peer] || 'this person';
      if (iBlockedNow(peer)) {
        if (!window.confirm(`You blocked ${nm}. Unblock and send?`)) return;
        try { await unblockUser(myUid, peer); } catch { window.alert('Could not unblock. Please try again.'); return; }
      } else if (blockedMeNow(peer)) {
        window.alert(`You can’t send messages to ${nm} right now.`);
        return;
      }
    }
    setSent((s) => ({ ...s, [chat.id]: 'sending' }));
    try {
      await shareVibePost({ post, chat, myUid, myName: me.fullName || me.username || 'Web' });
      setSent((s) => ({ ...s, [chat.id]: 'ok' }));
      recordShare(post, me).catch(() => {});   // the app's share counters + the owner's notification
      feedSignals.shared(post.id);             // …and the recommender's copy of the same act
    } catch {
      setSent((s) => ({ ...s, [chat.id]: 'err' }));
    }
  };
  const sendToUser = (u) => {
    const ids = [myUid, u.uid].sort();
    sendTo({
      id: ids.join('_'), participants: ids, isGroup: false,
      participantNames: { [u.uid]: u.fullName || u.username },
    });
  };

  const shareToFeed = async () => {
    if (posting) return;
    setPosting(true); setPostErr('');
    try {
      /* `me` already carries uid — same object createPost is handed, so a share
         and an original post are authored by exactly the same identity. */
      await sharePostToFeed({ post, me, caption });
      onShared?.();
      onClose();
    } catch (e) {
      /* 🟥 A FAILED SHARE MUST SAY SO. Closing the sheet on an error would look
         exactly like success, and the person would only find out by scrolling
         their own feed and not finding it. */
      setPostErr(e?.message || 'Could not share that. Try again.');
      setPosting(false);
    }
  };

  return (
    <div className="vb-overlay" onClick={onClose}>
      <div className="vb-sheet vb-share" onClick={(e) => e.stopPropagation()}>
        <div className="vb-sheet-head">
          <b>Share post</b>
          <button className="icon-btn" onClick={onClose} aria-label="Close"><X /></button>
        </div>

        <div className="vb-share-dest" role="tablist">
          <button role="tab" aria-selected={dest === 'feed'} className={dest === 'feed' ? 'on' : ''}
            onClick={() => setDest('feed')}>Share as post</button>
          <button role="tab" aria-selected={dest === 'chat'} className={dest === 'chat' ? 'on' : ''}
            onClick={() => setDest('chat')}>Send to a chat</button>
        </div>

        <SharePostCard post={post} />

        {dest === 'feed' ? (
          <>
            <div className="vb-share-compose">
              <textarea
                autoFocus value={caption} maxLength={MAX_SHARE_CAPTION}
                placeholder="Say something about this…"
                onChange={(e) => setCaption(e.target.value)}
              />
            </div>
            <div className="vb-share-go">
              <span className="grow">
                {postErr
                  ? postErr
                  : /* 🟢 Say whose post it will still say it is — the single most
                       common worry about re-sharing is that it looks like you wrote it. */
                    `Posted to your feed. ${post.userName || 'The original author'} stays credited.`}
              </span>
              <button className="btn primary" disabled={posting} onClick={shareToFeed}>
                {posting ? 'Sharing…' : 'Share now'}
              </button>
            </div>
          </>
        ) : (
        <>
        <div className="vb-sp-tabs">
          <button className={tab === 'chats' ? 'on' : ''} onClick={() => setTab('chats')}>Recent chats</button>
          <button className={tab === 'search' ? 'on' : ''} onClick={() => setTab('search')}>Search people</button>
        </div>

        {tab === 'search' && (
          <div className="vb-sheet-search">
            <input
              autoFocus value={q} placeholder="Name, username, phone or email"
              onChange={(e) => setQ(e.target.value)}
            />
          </div>
        )}

        <div className="vb-sheet-body">
          {tab === 'chats' ? (
            <>
              {/* pinned, always, exactly like the phone */}
              <ShareRow
                label="Note to Self" verified sub="Message yourself — notes, links, files"
                avatar={<span className="vb-srow-note"><DocText width={18} height={18} /></span>}
                state={sent[noteChat.id]}
                onClick={() => sendTo(noteChat)}
              />
              {recent.length > 0 && <div className="ap-label">Recent chats</div>}
              {recent.length === 0 && <div className="ci-empty">No chats yet — search for someone instead.</div>}
              {recent.map((c) => (
                <ShareRow
                  key={c.id}
                  label={<ChatTitle chat={c} myUid={myUid} />}
                  sub={c.isGroup ? 'Group' : 'Chat'}
                  avatar={<ChatAvatar chat={c} myUid={myUid} size="sm" />}
                  state={sent[c.id]}
                  onClick={() => sendTo(c)}
                />
              ))}
            </>
          ) : (
            <>
              {busy && <div className="ci-empty">Searching…</div>}
              {!busy && q.trim().length >= 2 && found && found.length === 0 && (
                <div className="ci-empty">Nobody found by that name, username, phone or email</div>
              )}
              {!busy && q.trim().length < 2 && (
                <div className="ci-empty">Type at least two characters to search.</div>
              )}
              {(found || []).map((u) => {
                const cid = [myUid, u.uid].sort().join('_');
                return (
                  <ShareRow
                    key={u.uid}
                    label={resolveName(u)}
                    sub={u.username ? `@${u.username}` : (u.phoneE164 || u.phone || u.email || '')}
                    avatar={<Avatar profile={u} size="sm" myUid={myUid} />}
                    state={sent[cid]}
                    onClick={() => sendToUser(u)}
                  />
                );
              })}
            </>
          )}
        </div>
        </>
        )}
      </div>
    </div>
  );
}

function ReelsPane({ startPost, feed, me, myPages, chats, myUid, savedIds, onClose, onLike, onSave, onProfile, onDrag, onDeleteComment, onTag, mute, archiveFilter, follow }) {
  const [overlay, setOverlay] = useState(null); // {type:'comments'|'share', postId}
  const scRef = useRef(null);
  /* 💡 THE SCREEN STAYS ON WHILE THE REELS ARE OPEN — the browser's Wake Lock,
     the phone's expo-keep-awake. A tab that is hidden loses the lock (the
     browser's rule), so it is taken again when the tab comes back. Browsers
     without the API simply skip it. */
  useEffect(() => {
    const wl = typeof navigator !== 'undefined' ? navigator.wakeLock : null;
    if (!wl || typeof wl.request !== 'function') return undefined;
    let lock = null;
    let gone = false;
    const take = () => {
      if (gone || document.visibilityState !== 'visible') return;
      wl.request('screen').then((l) => { if (gone) { l.release().catch(() => {}); } else { lock = l; } }).catch(() => {});
    };
    take();
    document.addEventListener('visibilitychange', take);
    return () => {
      gone = true;
      document.removeEventListener('visibilitychange', take);
      if (lock) { lock.release().catch(() => {}); lock = null; }
    };
  }, []);

  /* ══ 🎯 THE REELS SURFACE IS ITS OWN RANKING ══════════════════════════════
     🟥 THIS PANE USED TO BE "WHICHEVER VIDEOS HAPPENED TO BE IN THE FEED" —
        ranked, if at all, by the MAIN FEED's weights, which lead on recency and
        on people you follow. The reels ranking leans on the session vector
        instead, which is the whole "show me another one like that" behaviour.
        Same corpus, a different question, and this pane never asked it.

     🟢 THE SEED IS STILL LOCAL, so the video that was clicked plays instantly
        and the pane is never empty while a request is in flight. The ranked
        reels are appended behind it. */
  const [reelIds, setReelIds] = useState([]);
  const [ranked, setRanked] = useState([]);
  const pending = useRef(false);
  const ended = useRef(false);
  /* 📚 after the ranker: the archive walk, videos only (services/feedPaging) */
  const archCursor = useRef(null);
  const archEnded = useRef(false);

  /* 🎬 🟥 THE SEED WAS EVERY VIDEO IN THE FEED, in the feed's order, so the reels
     around the clicked one were the feed's and not the reels ranking's. The clicked
     clip alone now; the rest is ranked (services/feedPaging, reelsSeed). */
  const seed = useMemo(() => reelsSeed(startPost && videoUrlOf(startPost) ? startPost : null), [startPost]);

  /* 🟢 LIVE, LIKE THE FEED. A one-off fetch would leave every like and comment
     count on a ranked reel frozen at the moment it was fetched. */
  useEffect(() => {
    if (!reelIds.length) return undefined;
    return feedApi.watchRanked(reelIds, setRanked);
  }, [reelIds]);

  const vids = useMemo(() => {
    const out = [];
    const seen = new Set();
    for (const p of [...seed, ...ranked]) {
      if (!p || seen.has(p.id)) continue;
      seen.add(p.id);
      out.push(p);
    }
    return out;
  }, [seed, ranked]);

  const loadMore = useCallback(async () => {
    const have = vids.map((p) => p.id);
    /* 🟥 ZERO, BECAUSE THE SENTINEL COMING INTO VIEW *IS* THE HEADROOM CHECK.
       Passing the list's length instead would be true for the first page and
       false for every one after it — the pane would stop at twelve clips and
       look exactly like a working feed that had run out. */
    const source = feedSource({ ranked: true, rankedExhausted: ended.current, archiveEnded: archEnded.current });
    if (!shouldFetchMore({
      ranked: source !== 'none', inFlight: pending.current, exhausted: false,
      remaining: 0, batchSize: 6,
    })) return;
    pending.current = true;
    try {
      if (source === 'ranked') {
        const next = await feedApi.rankedPage('reels', { size: 12, exclude: have }).catch(() => null);
        const { fresh, exhausted } = mergeNewPage({
          have, incoming: (next || []).map((r) => ({ id: r.id })),
        });
        if (!exhausted) {
          setReelIds((prev) => [...prev, ...fresh.map((p) => p.id).filter((id) => !prev.includes(id))]);
          return;
        }
        /* 🟥 "THE RANKER HAS NO MORE REELS" USED TO END THE PANE — after the
           videos already in the feed, with the platform full of others. It is
           remembered, and the archive takes over. */
        ended.current = true;
      }
      const f = (archiveFilter && archiveFilter()) || {};
      /* 🎬 a stretch of photo posts used to return nothing without ending the
         archive — and nothing new means the sentinel never fires again. Read on. */
      const older = await readUntilSome(() => feedApi.readArchive(archCursor, archEnded, {
        have: [...have, ...reelIds], want: 6, videoOnly: true,
        blocked: f.blocked, hidden: f.hidden, friends: f.friends, me: myUid,
      }), () => archEnded.current);
      if (older.length) {
        setReelIds((prev) => [...prev, ...older.map((p) => p.id).filter((id) => !prev.includes(id))]);
      }
    } catch { /* a page that did not arrive is a shorter pane; the next scroll asks again */ } finally {
      pending.current = false;
    }
  }, [vids, reelIds, archiveFilter, myUid]);

  /* 🟥 THE FIRST RANKED PAGE IS ASKED FOR ON OPEN, not when the reader reaches
     the end. The seed is usually three or four clips — without this the pane
     runs out before any request could have arrived. */
  useEffect(() => { loadMore(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const endRef = useRef(null);
  useEffect(() => {
    const node = endRef.current;
    if (!node || typeof IntersectionObserver === 'undefined') return undefined;
    const io = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) loadMore();
    }, { root: scRef.current, rootMargin: '200% 0px' });
    io.observe(node);
    return () => io.disconnect();
  }, [loadMore]);

  // open ON the reel that was clicked
  useEffect(() => {
    const i = vids.findIndex((p) => p.id === startPost?.id);
    if (i > 0 && scRef.current?.children[i]) scRef.current.children[i].scrollIntoView();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const livePost = overlay ? (feed.find((x) => x.id === overlay.postId) || vids.find((x) => x.id === overlay.postId)) : null;

  return (
    <aside className="vb-right reels">
      {/* 🟥 THIS SEAM ONLY GOES ONE WAY. Drag it left and the reel gets bigger;
          it will not go back below the width it started at, because a reel that
          can be squeezed is a reel you can accidentally squeeze into a strip
          while reaching for the scrollbar. Bigger is the only direction anyone
          drags a video in. */}
      <div className="vb-grip" title="Drag left to make the reel bigger" onPointerDown={onDrag} />
      <button className="vb-reel-close icon-btn" onClick={onClose} aria-label="Close"><X /></button>
      <div className="vb-reels" ref={scRef}>
        {vids.map((p) => (
          <Reel
            key={p.id} p={p} me={me}
            saved={savedIds.includes(p.id)}
            onLike={onLike} onSave={onSave} onProfile={onProfile} onTag={onTag} mute={mute} follow={follow}
            onComments={(post) => setOverlay({ type: 'comments', postId: post.id })}
            onShare={(post) => setOverlay({ type: 'share', postId: post.id })}
          />
        ))}
        {vids.length === 0 && <div className="ct-empty"><span>No videos in the feed yet.</span></div>}
        {/* two screens before the end, so the next reels are already loaded */}
        <div ref={endRef} style={{ height: 1 }} />
      </div>
      {overlay?.type === 'comments' && livePost && (
        <ReelComments post={livePost} me={me} myPages={myPages} onClose={() => setOverlay(null)} onDeleteComment={onDeleteComment} onProfile={(u) => { setOverlay(null); if (onProfile) onProfile(u); }} />
      )}
      {overlay?.type === 'share' && livePost && (
        <ShareSheet post={livePost} chats={chats} me={me} myUid={myUid} onClose={() => setOverlay(null)} />
      )}
    </aside>
  );
}

/* ══════════════════════════════════════════════════════════════════════════ */
/* ══ ⭐ MODERATION OUTCOME ═════════════════════════════════════════════════
   What happened to a report, shown to the people who deserve to hear it: the
   reporters (`report_update`) and, when the post came down, its author
   (`post_action`). The web twin of the app's ModerationOutcomeModal.

   🟥 THE NOTIFICATION IS THE ONLY COPY THAT SURVIVES. A removed post is deleted
   outright, so this card never loads the post — it draws the text, image and
   author name that were frozen onto the notification at the moment of the
   decision. Fetching by postId here would show an empty card in exactly the case
   the feature exists for.

   🟥 AND NOT ONE WORD COMES FROM THE SERVER. The document holds an outcome; the
   sentence is chosen here, in the reader's language (see modStrings.js). */
function ModerationOutcome({ n, onClose, onViewPost }) {
  if (!n) return null;

  const isComment = n.type === 'comment_action';
  const isAuthor = n.type === 'post_action' || isComment;
  const removed = n.outcome === 'removed';
  const accent = isAuthor ? '#FF6B6B' : (removed ? '#5B8DEF' : '#3BB273');

  /* 🟥 "We received reports about your post" IS A LIE WHEN NOBODY REPORTED IT.
     A moderator browsing Content can remove a post nobody flagged; saying they
     were reported invents an accuser they will go looking for. The server
     states the fact on the document — this side does not deduce it. Absent on
     everything written before reasons existed, and all of those DID come from
     the report queue, so `!== false` is the correct reading of a missing value. */
  const fromReports = n.fromReports !== false;

  const title = mt(isComment
    ? 'vibe_comment_action_title'
    : (isAuthor ? 'vibe_post_action_title' : 'vibe_report_outcome_title'));

  const body = mt(isComment
    ? 'vibe_comment_action_removed'
    : isAuthor
      ? (fromReports ? 'vibe_post_action_removed' : 'vibe_post_action_removed_noreports')
      : (removed ? 'vibe_report_outcome_removed' : 'vibe_report_outcome_kept'));

  /* verbatim custom text, or a translated preset — never a key handed to a
     lookup that would return the key back */
  const why = reasonLabel(n);

  const hasPreview = !!(n.postContent || n.postImageUrl || n.postAuthorName);
  const isVideo = /\.(mp4|mov)(\?|$)/i.test(String(n.postImageUrl || ''));

  /* 🟥 THE CARD SETS ITS OWN DIRECTION. This is the only localised surface on an
     otherwise English-only web app, so nothing above it ever sets `dir` — an
     Arabic paragraph would render left-aligned with its punctuation stranded on
     the wrong side. The CSS has the [dir="rtl"] rules; this is what turns them on. */
  const dir = pickLang() === 'ar' ? 'rtl' : 'ltr';

  return (
    <div className="vb-fixed-share" onClick={onClose}>
      <div className="vb-sheet vb-modout" dir={dir} onClick={(e) => e.stopPropagation()}>
        <div className="vb-sheet-head">
          <b style={{ color: accent }}>{title}</b>
          <button className="icon-btn" onClick={onClose}><X /></button>
        </div>

        <div className="vb-sheet-body">
          {hasPreview && (
            <div className="vb-modout-post" style={{ borderColor: accent + '55' }}>
              {!!n.postImageUrl && !isVideo && (
                <SafeImg src={n.postImageUrl} alt="" className="vb-modout-img" />
              )}
              <div className="vb-modout-meta">
                {!!n.postAuthorName && <b>{n.postAuthorName}</b>}
                {!!n.postContent && <p>{n.postContent}</p>}
                {removed && <span className="vb-modout-gone">{mt('vibe_ui_post_removed')}</span>}
              </div>
            </div>
          )}

          <p className="vb-modout-body">{body}</p>

          {!!why && (
            <p className="vb-modout-reason" style={{ color: accent }}>
              {mt('vibe_mod_reason_label', undefined, { reason: why })}
            </p>
          )}

          {/* the comment as it was — by now it is out of the post's array and
              this is the only copy left anywhere */}
          {isComment && !!n.commentContent && (
            <div className="vb-modout-quote">
              <span className="sub">{mt('vibe_mod_your_comment')}</span>
              <p>{n.commentContent}</p>
            </div>
          )}

          {/* 🟢 a KEPT post still exists, so there is somewhere to go */}
          {!removed && !!n.postId && (
            <button className="vb-modout-view" onClick={() => onViewPost(n.postId)}>
              {mt('vibe_report_view_post')}
            </button>
          )}
        </div>

        <div className="vb-sheet-foot">
          <button className="lg-go" style={{ background: accent }} onClick={onClose}>
            {mt('vibe_ui_i_understand')}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── the status picker — the app's check-in / traveling sheet, web edition ────
   Same two tabs and same stamped shape ({type:'check-in',location} or
   {type:'traveling',from,to}). Place search runs on OpenStreetMap's Nominatim
   (free, no key) and "current location" uses the browser's geolocation +
   reverse geocode — the web stand-ins for the app's Google/native ladder. */
function StatusPicker({ onPick, onClose }) {
  const [type, setType] = useState('check-in');
  const [location, setLocation] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [field, setField] = useState('location');
  const [preds, setPreds] = useState([]);
  const [locBusy, setLocBusy] = useState(false);
  const tRef = useRef(null);

  const put = (f, v) => { if (f === 'location') setLocation(v); else if (f === 'from') setFrom(v); else setTo(v); };

  const search = (text, f) => {
    put(f, text); setField(f);
    if (tRef.current) clearTimeout(tRef.current);
    if (text.trim().length <= 2) { setPreds([]); return; }
    tRef.current = setTimeout(async () => {
      try {
        const r = await fetch(`https://nominatim.openstreetmap.org/search?format=jsonv2&addressdetails=1&limit=6&q=${encodeURIComponent(text)}`);
        const j = await r.json();
        const seen = new Set(); const out = [];
        for (const x of j) {
          const a = x.address || {};
          const city = a.city || a.town || a.village || a.hamlet || x.name;
          const label = [city, a.state || a.region, (!a.state && a.country) ? a.country : null].filter(Boolean).join(', ');
          if (label && !seen.has(label)) { seen.add(label); out.push(label); }
        }
        setPreds(out);
      } catch { setPreds([]); }
    }, 450);
  };

  const locateMe = (f) => {
    if (!navigator.geolocation || locBusy) return;
    setLocBusy(true);
    navigator.geolocation.getCurrentPosition(async (pos) => {
      try {
        const r = await fetch(`https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${pos.coords.latitude}&lon=${pos.coords.longitude}`);
        const j = await r.json(); const a = j.address || {};
        const label = [a.city || a.town || a.village, a.state || a.region].filter(Boolean).join(', ') || j.name || '';
        if (label) { put(f, label); setPreds([]); }
      } catch {}
      setLocBusy(false);
    }, () => setLocBusy(false), { timeout: 9000 });
  };

  const valid = type === 'check-in' ? !!location.trim() : (!!from.trim() && !!to.trim());
  const done = () => {
    if (!valid) return;
    onPick(type === 'check-in'
      ? { type: 'check-in', location: location.trim() }
      : { type: 'traveling', from: from.trim(), to: to.trim() });
    onClose();
  };

  const placeField = (f, value, placeholder) => (
    <div className="vb-sp-field">
      <span className="lg-input">
        <input value={value} placeholder={placeholder} onChange={(e) => search(e.target.value, f)} onFocus={() => setField(f)} />
      </span>
      <button className="icon-btn" title="Use my current location" onClick={() => locateMe(f)}>
        <Crosshair width={16} height={16} />
      </button>
      {field === f && preds.length > 0 && (
        <div className="vb-sp-preds">
          {preds.map((d) => (
            <button key={d} onClick={() => { put(f, d); setPreds([]); }}><MapPin width={13} height={13} /> {d}</button>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <div className="vb-fixed-share" onClick={onClose}>
      <div className="vb-sheet vb-statuspick" onClick={(e) => e.stopPropagation()}>
        <div className="vb-sheet-head"><b>Add a status</b>
          <button className="icon-btn" onClick={onClose}><X /></button></div>
        <div className="vb-sp-tabs">
          <button className={type === 'check-in' ? 'on' : ''} onClick={() => { setType('check-in'); setPreds([]); }}>
            <MapPin width={15} height={15} /> Check in
          </button>
          <button className={type === 'traveling' ? 'on' : ''} onClick={() => { setType('traveling'); setPreds([]); }}>
            <Plane width={15} height={15} /> Traveling
          </button>
        </div>
        <div className="vb-sheet-body form">
          {type === 'check-in'
            ? placeField('location', location, 'Search places…')
            : (
              <>
                {placeField('from', from, 'From…')}
                {placeField('to', to, 'To…')}
              </>
            )}
          {locBusy && <div className="vb-sp-busy">Finding your location…</div>}
        </div>
        <div className="vb-sheet-foot">
          <button className="lg-go" disabled={!valid} onClick={done}>Set status</button>
        </div>
      </div>
    </div>
  );
}

/* ── the page wizard, web edition — same fields + categories as the app ───── */
/* ══ CREATE A PAGE ═════════════════════════════════════════════════════════
   Rebuilt. The old one was a stack of identical grey inputs in a dark box: no
   hierarchy, no sense of what the page would look like, and a <select> for the
   category that hid every option behind a click. This version leads with a real
   preview of the page you're making, groups the fields by what they're for, and
   puts the categories out in the open as chips so choosing one is a glance. */
function PageMaker({ me, onClose }) {
  const [name, setName] = useState('');
  const [category, setCategory] = useState('business');
  const [bio, setBio] = useState('');
  const [website, setWebsite] = useState('');
  const [location, setLocation] = useState('');
  const [hours, setHours] = useState('');
  const [phone, setPhone] = useState('');
  const [photo, setPhoto] = useState(null);
  /* 🪪 THE IDENTITY ANSWERS, ASKED ONLY WHERE THEY MAKE SENSE. A restaurant is
     not asked its pronouns. `values` holds the answers and `held` remembers
     which of them are being kept off the page — the two travel together,
     because saved apart the one that gets saved is the value. */
  const [values, setValues] = useState({});
  const [held, setHeld] = useState({});
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const picRef = useRef(null);
  const photoUrl = useMemo(() => (photo ? URL.createObjectURL(photo) : null), [photo]);
  useEffect(() => () => { if (photoUrl) URL.revokeObjectURL(photoUrl); }, [photoUrl]);

  const cap = (c) => String(c || '').charAt(0).toUpperCase() + String(c || '').slice(1);

  const go = async () => {
    if (!name.trim() || busy) return;
    setBusy(true); setErr('');
    try {
      const asked = identityFor(category);
      const fields = {}; const hidden = {};
      asked.forEach((k) => {
        const v = String(values[k] || '').trim();
        if (!v) return;
        if (held[k]) hidden[k] = v; else fields[k] = v;
      });
      await createPage({
        me, name, category, bio, photoFile: photo, website, location, hours, phone,
        fields, held: hidden,
      });
      onClose();
    } catch {
      setErr('Could not create the page — try again.');
      setBusy(false);
    }
  };

  return (
    <div className="vb-fixed-share" onClick={onClose}>
      <div className="vb-sheet vb-pagemaker" onClick={(e) => e.stopPropagation()}>
        <div className="vb-sheet-head">
          <b>Create a page</b>
          <button className="icon-btn" onClick={onClose} aria-label="Close"><X /></button>
        </div>

        <div className="vb-sheet-body pm">
          {/* ── the page, as it will look. Filling the form fills this in, so the
                 fields stop being a checklist and start being a thing. ── */}
          <div className="pm-preview">
            <button className="pm-avatar" onClick={() => picRef.current?.click()} title="Choose a photo">
              {photoUrl
                ? <img src={photoUrl} alt="" />
                : <span className="pm-avatar-ph">{(name.trim()[0] || '?').toUpperCase()}</span>}
              <span className="pm-avatar-edit"><Camera width={13} height={13} /></span>
            </button>
            <div className="pm-preview-txt">
              <b>{name.trim() || 'Your page name'}</b>
              <em>{cap(category)}</em>
              <i>{bio.trim() || 'A line about the page'}</i>
            </div>
          </div>
          <input
            ref={picRef} type="file" accept="image/*" hidden
            onChange={(e) => { setPhoto(e.target.files?.[0] || null); e.target.value = ''; }}
          />

          {/* ── the two things a page can't exist without ── */}
          <div className="pm-group">
            <div className="pm-field">
              <label htmlFor="pm-name">Page name<u>required</u></label>
              <input
                id="pm-name" value={name} maxLength={60} autoFocus
                placeholder="What is the page called?"
                onChange={(e) => setName(e.target.value)}
              />
              <span className="pm-count">{name.length}/60</span>
            </div>

            {/* chips, not a <select>: every option visible, one click to pick */}
            <div className="pm-field">
              <label>Category</label>
              <div className="pm-chips">
                {PAGE_CATEGORIES.map((c) => (
                  <button
                    key={c} type="button"
                    className={`pm-chip ${category === c ? 'on' : ''}`}
                    onClick={() => setCategory(c)}
                  >
                    {category === c && <Check width={11} height={11} />}{cap(c)}
                  </button>
                ))}
              </div>
            </div>

            <div className="pm-field">
              <label htmlFor="pm-bio">Bio</label>
              <textarea
                id="pm-bio" value={bio} maxLength={160} rows={2}
                placeholder="One line people will read before they follow"
                onChange={(e) => setBio(e.target.value)}
              />
              <span className="pm-count">{bio.length}/160</span>
            </div>
          </div>

          {/* ── everything below is optional, and says so once instead of four times ── */}
          <div className="pm-group">
            <div className="pm-grouphead">Contact &amp; details <span>optional</span></div>
            <div className="pm-grid">
              <div className="pm-field">
                <label htmlFor="pm-web"><LinkIcon width={12} height={12} /> Website</label>
                <input id="pm-web" value={website} placeholder="example.com" onChange={(e) => setWebsite(e.target.value)} />
              </div>
              <div className="pm-field">
                <label htmlFor="pm-loc"><MapPin width={12} height={12} /> Location</label>
                <input id="pm-loc" value={location} placeholder="City" onChange={(e) => setLocation(e.target.value)} />
              </div>
              <div className="pm-field">
                <label htmlFor="pm-hours"><Timer width={12} height={12} /> Hours</label>
                <input id="pm-hours" value={hours} placeholder="9–5, Mon–Fri" onChange={(e) => setHours(e.target.value)} />
              </div>
              <div className="pm-field">
                <label htmlFor="pm-phone"><PhoneIcon width={12} height={12} /> Phone</label>
                <input id="pm-phone" value={phone} placeholder="+1 …" onChange={(e) => setPhone(e.target.value)} />
              </div>
            </div>
          </div>

          {/* 🪪 ONLY FOR THE PAGES THAT ARE A PERSON. The same four categories the
              phone asks, from the same list, so a page created on either client
              is asked the same things. */}
          {identityFor(category).length > 0 && (
            <div className="pm-group">
              <div className="pm-grouphead">About them <span>optional</span></div>
              {identityFor(category).map((k) => {
                const f = fieldByKey(k);
                const v = values[k] || '';
                const isHeld = !!held[k];
                return (
                  <div className="pm-field" key={k}>
                    <div className="pp-field-h">
                      <label htmlFor={`pm-${k}`}>{fieldLabel(k)}</label>
                      {!!String(v).trim() && (
                        <button type="button" className="pp-eye" onClick={() => setHeld((h) => ({ ...h, [k]: !isHeld }))}>
                          {isHeld ? <EyeOff width={13} height={13} /> : <Eye width={13} height={13} />}
                          {fieldsL(isHeld ? 'hidden' : 'visible')}
                        </button>
                      )}
                    </div>
                    {f?.input === 'choice' ? (
                      <div className="ci-chips">
                        {(f.options || []).map((id) => (
                          <button
                            key={id} type="button"
                            className={`ci-chip ${v === id ? 'on' : ''}`}
                            onClick={() => setValues((x) => ({ ...x, [k]: v === id ? '' : id }))}
                          >
                            {optionLabel(id)}
                          </button>
                        ))}
                      </div>
                    ) : (
                      <input
                        id={`pm-${k}`} value={v} autoComplete="off"
                        placeholder={fieldLabel(k)}
                        onChange={(e) => setValues((x) => ({ ...x, [k]: e.target.value }))}
                      />
                    )}
                    {isHeld && !!String(v).trim() && <span className="pm-count">{fieldsL('only_you')}</span>}
                  </div>
                );
              })}
            </div>
          )}

          {err && <div className="vb-pm-err">{err}</div>}
        </div>

        <div className="vb-sheet-foot pm-foot">
          <span className="pm-hint">
            {name.trim() ? 'You can edit all of this later.' : 'A name is all you need to start.'}
          </span>
          <button className="lg-go" disabled={!name.trim() || busy} onClick={go}>
            {busy ? 'Creating…' : 'Create page'}
          </button>
        </div>
      </div>
    </div>
  );
}

/* 🟢 STABLE IDS FOR THE COMPOSER'S ANSWER ROWS. Keyed by index instead, a row
   re-keys whenever one above it is removed and React moves the caret out of the
   box the person is typing in. */
let _pollSeq = 0;
const blankPollOption = () => ({ id: `po${(_pollSeq += 1)}`, text: '' });

/* 👁️ 🟥 THE BROWSER NEVER COUNTED A VIEW: someone reading Vibes on the web was
   invisible to every creator's numbers. Now it reports exactly what the phone
   does (services/viewTracker.ts, generated): on screen → an impression, on
   screen 1.5 s → a view. The server keeps the tally. */
const viewTracker = createViewTracker({ send: sendViewEvents, me: () => viewAuth.currentUser?.uid || null });
/* …and a tab in the background is not being looked at */
if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', () => { if (document.hidden) viewTracker.clear(); });
}

export default function VibesScreen({ myUid, myProfile, chats = [], sharedPostId, onSharedPostViewed, navRail, onListScroll, onOpenChatId }) {
  const [self, setSelf] = useState(null);
  /* 📊 THE COMPOSER'S POLL. `null` until the Poll tool is opened, so an unopened
     poll cannot accidentally attach itself to an ordinary post. */
  const [poll, setPoll] = useState(null);
  /* 🏷 the people this post will tag, and the picker */
  const [tagged, setTagged] = useState([]);
  /* 🏷 tag requests answered from the bell, before the server's own mark arrives */
  const [tagAnswered, setTagAnswered] = useState({});
  const [tagPick, setTagPick] = useState(false);
  const [feed, setFeed] = useState([]);

  /* 📊 A VOTE LANDED. The card paints itself, but the feed is re-rendered from
     `feed` on every snapshot — so a poll answered and then scrolled past would
     come back showing the counts from before the vote until the next snapshot
     arrived. Patch the row.
     🟥 A PATCH, NOT A REPLACEMENT. Spreading a whole post object over the row
     would drop what the feed computed onto it (`_score`, the resolved media
     variants); merging only the poll fields leaves the row intact. */
  const onPollVoted = useCallback((postId, patch) => {
    setFeed((f) => f.map((x) => (x && x.id === postId ? { ...x, ...patch } : x)));
  }, []);
  const [friends, setFriends] = useState({ friends: [], pendingIn: [], pendingOut: [] });
  const [notifs, setNotifs] = useState([]);
  /* ⭐ the moderation notification whose outcome card is open, or null. Holds the
     WHOLE notification: for a removed post the frozen preview riding on it is the
     only copy of that post left anywhere. */
  const [modOutcome, setModOutcome] = useState(null);
  /* ©️ WHAT THE MUTE SHEET WAS OPENED WITH — a bell row or the live post */
  const [mutedSheet, setMutedSheet] = useState(null);
  /* the dispute IS a conversation: open the official chat, the same way Help does */
  const openAlloChat = useCallback((chatId) => {
    if (chatId) { onOpenChatId?.(chatId); return; }
    openSupport().then((o) => onOpenChatId?.(o.chatId)).catch(() => {});
  }, [onOpenChatId]);
  /* a dispute sent from any surface patches the post every surface reads */
  const patchDispute = useCallback((postId, dispute) => {
    setFeed((f) => f.map((x) => (x && x.id === postId ? { ...x, audioCheck: { ...(x.audioCheck || {}), dispute } } : x)));
  }, []);
  /* ©️ THE SCREEN OWNS THE SHEET; the banners live two components down (PostPane
     inside RightPane, Reel inside ReelsPane), so the three things a banner can
     ask for travel as one prop. */
  const muteActions = useMemo(() => ({ explain: (p) => setMutedSheet({ post: p }), openChat: openAlloChat, disputed: patchDispute }), [openAlloChat, patchDispute]);
  const [myPosts, setMyPosts] = useState([]);
  /* 📊 the server's account numbers — people reached counted once across every post */
  const [acctInsights, setAcctInsights] = useState(null);
  useEffect(() => {
    if (!myUid) return undefined;
    let live = true;
    fetchInsights(null).then((r) => { if (live && r) setAcctInsights(r); }).catch(() => {});
    return () => { live = false; };
  }, [myUid, myPosts.length]);
  const [myPages, setMyPages] = useState([]);

  /* 💬 REMOVING A COMMENT. Whether the button was there at all is decided by
     `canDeleteComment` inside the thread component; this performs it.
     🟥 THE CONFIRM NAMES THE REPLIES IT WILL TAKE WITH IT. A parent's replies
     answer something that will no longer exist, and their own authors cannot
     remove them — they own the reply, not the parent. Sweeping them is right;
     doing it without saying so is not. */
  const deleteCommentHere = useCallback(async (post, comment) => {
    /* the replies it takes with it — from the old array AND the loaded pages */
    const n = repliesUnder([...(post?.comments || []), ...commentPager.allFor(post?.id)], comment.id);
    const msg = n > 0
      ? `Delete this comment and the ${n} ${n === 1 ? 'reply' : 'replies'} to it?`
      : 'Delete this comment?';
    // eslint-disable-next-line no-alert, no-restricted-globals
    if (!window.confirm(msg)) return;
    try {
      const removed = await deleteComment(post.id, comment.id);
      const gone = new Set((removed || []).map(String));
      /* the feed holds its own copy; patch the row rather than waiting for the
         snapshot, and patch only the two fields that changed — the count by
         the shared rule, since a paged post's array is not its total */
      setFeed((f) => f.map((x) => {
        if (!(x && x.id === post.id)) return x;
        const comments = (x.comments || []).filter((c) => !(c && gone.has(String(c.id))));
        /* 📌 a pinned comment that goes takes its pin with it (the server clears it too) */
        const unpin = x.pinnedCommentId && gone.has(String(x.pinnedCommentId)) ? { pinnedCommentId: null } : {};
        return { ...x, comments, commentsCount: localCountAfter(x, comments, -gone.size), ...unpin };
      }));
      /* …and the thread's pages */
      commentPager.drop(post.id, Array.from(gone));
    } catch (e) {
      /* a refusal is said; a failure the snapshot corrects */
      // eslint-disable-next-line no-alert
      if (e && e.code) window.alert('That comment could not be deleted.');
    }
  }, []);
  const [leftMode, setLeftMode] = useState('me');   // me | friends | notifs | saved
  const [worldOpen, setWorldOpen] = useState(false);  // 🌍 the world map, full screen
  /* 🟢 phone only: the analytics column is a slide-over there rather than a
     third of the screen. Desktop never sets it — the column is always visible
     and mobile.css gives this class no meaning above 820px. */
  const [leftOpen, setLeftOpen] = useState(false);

  const [panel, setPanel] = useState(null);          // {type:'post'|'comments'|'reels'|'profile', ...}
  /* 🟥 A PANE OPENING MUST CLOSE THE PANEL. On a phone both are full-screen, so
     leaving the panel open behind a reel means closing the reel drops you back
     into a slide-over you never asked for — and on the way there the two stack,
     each with its own close button. One thing at a time. */
  useEffect(() => { if (panel) setLeftOpen(false); }, [panel]);
  /* 🟢 THE RIGHT PANE'S WIDTH, IN PIXELS. 0 means "whatever the stylesheet says",
     which is how a double-click resets it. Persisted, because a width you dragged
     to look at a photo is a preference, not a one-off. Written as a CSS variable
     on the shell so the grid does the layout and nothing is positioned in JS. */
  const [rightW, setRightW] = useState(() => {
    const v = Number(localStorage.getItem('allo:vb-right-w'));
    return Number.isFinite(v) && v > 0 ? v : 0;
  });
  useEffect(() => {
    try {
      if (rightW > 0) localStorage.setItem('allo:vb-right-w', String(rightW));
      else localStorage.removeItem('allo:vb-right-w');
    } catch {}
  }, [rightW]);

  /* ══ THE REELS PANE SIZES ITSELF ═══════════════════════════════════════════
     🟥 A REEL'S HEIGHT IS SET BY THE WINDOW, SO ITS WIDTH IS NOT A FREE CHOICE.
        Give a 9:16 clip a 400px column in a 900px-tall window and it can only be
        506px of that height — the other 400px are black bands above and below
        it. The fix is not a different object-fit, it is arithmetic: the pane has
        to be at least (height × 9/16) wide, plus the two gutters the buttons
        live in, or the bands are unavoidable.

        So the pane opens at that width instead of at a number someone picked.
        It has its own remembered width, separate from the comments/photo pane —
        widening for a video should not silently widen a text pane too — and the
        drag can only ever add to it. */
  const [reelsW, setReelsW] = useState(() => {
    const v = Number(localStorage.getItem('allo:vb-reels-w'));
    return Number.isFinite(v) && v > 0 ? v : 0;
  });
  useEffect(() => {
    try {
      if (reelsW > 0) localStorage.setItem('allo:vb-reels-w', String(reelsW));
      else localStorage.removeItem('allo:vb-reels-w');
    } catch {}
  }, [reelsW]);

  /* measured off the shell, not the window: the pane is as tall as the layout
     gives it, and guessing that from innerHeight would size the pane for a
     height it never has */
  const shellRef = useRef(null);
  const shellReady = !!self;   // a dep array cannot hold an expression
  const [reelsFit, setReelsFit] = useState(() => reelsFitWidth());
  useEffect(() => {
    const on = () => setReelsFit(reelsFitWidth(shellRef.current?.getBoundingClientRect().height));
    window.addEventListener('resize', on);
    on();
    let ro;
    if (typeof ResizeObserver !== 'undefined' && shellRef.current) {
      ro = new ResizeObserver(on);
      ro.observe(shellRef.current);
    }
    return () => {
      window.removeEventListener('resize', on);
      if (ro) ro.disconnect();
    };
    // re-attached once the shell itself is on screen — until Vibes has loaded
    // there is only the "Opening Vibes…" placeholder to measure
  }, [shellReady]);
  // never below what the arithmetic asks for, however narrow it was left
  const reelsWidth = Math.max(reelsW, reelsFit);

  /* 🟢 THE RAIL HIDES AS YOU TRAVEL DOWN THE ANALYTICS COLUMN and comes back the
     moment you head up — the same behaviour the chat list has, reported the same
     way. Near the top it always shows, so a short list never leaves it hidden. */
  const leftScrollRef = useRef(0);
  const onLeftScroll = (e) => {
    const el = e.currentTarget;
    const d = el.scrollTop - leftScrollRef.current;
    leftScrollRef.current = el.scrollTop;
    if (Math.abs(d) < 4) return;                 // ignore trackpad jitter
    if (el.scrollTop < 40) onListScroll?.('up');
    else onListScroll?.(d > 0 ? 'down' : 'up');
  };

  const startRightDrag = (e, { growOnly = false } = {}) => {
    e.preventDefault();
    const startX = e.clientX;
    // measure the live width so the first drag doesn't jump from the CSS default
    const el = e.currentTarget.closest('.vb-right');
    const measured = el?.getBoundingClientRect().width || 400;
    /* 🟢 growOnly is the reels seam: it moves the reels pane's OWN width, and
       its floor is wherever this drag started, so it can only ever give the
       video more room — including never dropping under the fit width. */
    const setW = growOnly ? setReelsW : setRightW;
    const startW = growOnly ? (reelsWidth || measured) : (rightW > 0 ? rightW : measured);
    const lo = growOnly ? Math.max(320, Math.round(startW)) : 320;
    const hi = Math.max(lo, Math.min(920, Math.round(window.innerWidth * 0.62)));
    // dragging LEFT widens the right pane, so the delta is inverted
    const move = (ev) => setW(Math.max(lo, Math.min(hi, startW + (startX - ev.clientX))));
    const up = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  };
  const [savedList, setSavedList] = useState(null);
  const [draft, setDraft] = useState('');
  const [files, setFiles] = useState([]);
  /* 🎨 ONE EDIT RECORD PER FILE, kept beside `files` rather than inside it.
     A File is a host object — assigning fields to it is not portable and does
     not survive being handed to `uploadBytes` — so the edits travel alongside,
     indexed the same way, and `removeAt` below keeps the two in step. */
  const [edits, setEdits] = useState([]);
  const [editing, setEditing] = useState(false);
  const [audience, setAudience] = useState('everyone');
  const [posting, setPosting] = useState(false);
  const [postAs, setPostAs] = useState('me');        // 'me' | a page id
  const [postStatus, setPostStatus] = useState(null); // {type:'check-in',location} | {type:'traveling',from,to}
  const [commentsOn, setCommentsOn] = useState(true);
  /* ©️ "MADE WITH AI" — the author's word, written on the post as `aiDeclared`.
     The server also reads the file's own provenance on upload; either earns the
     badge, neither removes it. See services/contentRights. */
  const [aiDeclared, setAiDeclared] = useState(false);
  const [statusPick, setStatusPick] = useState(false);
  /* ✍️ "Create post" (CreatePostDialog): open or not, how the author feels, a song
     for a post with no video, and the draft "Save as draft" keeps */
  const [composeOpen, setComposeOpen] = useState(false);
  const [feeling, setFeeling] = useState(null);
  const [music, setMusic] = useState(null);
  const [composeNotice, setComposeNotice] = useState(null);
  const [draftTick, setDraftTick] = useState(0);
  useEffect(() => subscribeDraft(() => setDraftTick((n) => n + 1)), []);
  useEffect(() => { if (myUid) loadDraft(myUid).catch(() => {}); }, [myUid]);
  /* the dialog's "Your draft is back" — a moment, then gone */
  const noticeTimer = useRef(null);
  useEffect(() => () => clearTimeout(noticeTimer.current), []);
  /* 🟢 THE BACKGROUND OFFERS ITSELF. There is no "Aa" button any more: start
     typing something short and the card appears on a suggested background;
     write past a sentence or two, or attach a photo, and it steps back out of
     the way. `styleOff` is the one thing the user says explicitly — picking the
     None swatch — and it is remembered until the draft is cleared. */
  const [textStyle, setTextStyle] = useState({ ...DEFAULT_TEXT_STYLE, bg: SUGGESTED_BG });
  const [styleOff, setStyleOff] = useState(false);
  const [shareFor, setShareFor] = useState(null);    // post being shared from the feed
  /* 🟥 UP HERE WITH THE OTHER STATE, NOT BESIDE THE FUNCTION THAT USES IT. This
     component has THREE early returns below — the registration gate, the underage
     gate, and the still-loading gate — and a hook declared after them is skipped
     on those renders, which changes the hook ORDER between renders. React's
     rules-of-hooks lint caught it; parsing the file could not, because it is
     perfectly valid JavaScript. See openPostById for what it does. */
  const [embedMiss, setEmbedMiss] = useState(false);
  const [pageMaker, setPageMaker] = useState(false); // the create-page overlay
  const [regDob, setRegDob] = useState('');
  const [regGender, setRegGender] = useState('');
  const fileRef = useRef(null);
  const blockedRef = useRef(new Set());
  /* posts I hid, and authors I asked to see less of — read by the feed scorer.
     Refs, not state: the listener is created once and must not be torn down and
     rebuilt every time one of these changes. */
  const hiddenRef = useRef(new Set());
  const lessLikeRef = useRef(new Set());
  const friendsRef = useRef(new Set());
  /* 🔒 a friends-only post opened by link is still friends-only */
  /* 🏷 …and to the people tagged in it (services/postTags — the same answer the feed and search give) */
  const mayOpen = (p) => mayOpenFriendsPost(p, myUid, (u) => friendsRef.current.has(u));

  /* ══ SEARCH STATE ════════════════════════════════════════════════════════
     The corpus is fetched once, the first time someone actually searches —
     nobody should pay 400 documents for opening Vibes. `qLive` is what the box
     shows; `q` is what the ranking runs on, a beat behind, so typing does not
     re-rank on every keystroke. */
  const [qLive, setQLive] = useState('');
  const [q, setQ] = useState('');
  const [searchKind, setSearchKind] = useState('all');
  const [corpus, setCorpus] = useState(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const searching = qLive.trim().length > 0;

  useEffect(() => {
    const id = setTimeout(() => setQ(qLive), 170);
    return () => clearTimeout(id);
  }, [qLive]);

  /* 🔎 THE SERVER ANSWERS FIRST (searchRoutes.js) — privacy decided there, people
     from the whole directory. The local corpus is only the fallback. */
  const [srv, setSrv] = useState(null);
  useEffect(() => {
    if (!q.trim()) { setSrv(null); return undefined; }
    let live = true;
    serverSearch(q, 'all').then((r) => { if (live) setSrv(r ? { q, ...r } : { q, failed: true }); });
    return () => { live = false; };
  }, [q]);
  const srvNow = srv && srv.q === q ? srv : null;

  useEffect(() => {
    if (!q.trim() || corpus || !(srvNow && srvNow.failed)) return undefined;
    let live = true;
    setSearchLoading(true);
    loadSearchCorpus()
      .then((list) => { if (live) setCorpus(list); })
      .catch(() => { if (live) setCorpus([]); })
      .finally(() => { if (live) setSearchLoading(false); });
    return () => { live = false; };
  }, [q, corpus, srvNow]);

  /* 🏷 A TAG IS ANSWERED BY THE SERVER, NOT BY THE CORPUS. See
     loadHashtagPosts — the corpus is 400 recent posts, and a tag with more than
     that would show a handful of clips under a heading claiming hundreds. */
  /* 🏷 the tags the draft would be filed under, if it is a video. `files` is
     what the picker is holding — the same test createPost makes against what
     actually reached Storage, one step earlier. */
  const composerTags = useMemo(
    () => (files.some((f) => /^video\//.test(f.type)) ? parseHashtags(draft) : []),
    [files, draft],
  );

  const searchTag = useMemo(() => hashtagOfQuery(q), [q]);
  const [tagHits, setTagHits] = useState(null);
  const [tagCount, setTagCount] = useState(null);
  useEffect(() => {
    if (!searchTag) { setTagHits(null); setTagCount(null); return undefined; }
    let live = true;
    setTagHits(null); setTagCount(null);
    loadHashtagPosts(searchTag)
      .then(({ posts, count }) => {
        if (!live) return;
        setTagHits(posts.filter(
          (p) => !blockedRef.current.has(p.userId) && !hiddenRef.current.has(p.id),
        ));
        setTagCount(count);
      })
      .catch(() => { if (live) { setTagHits([]); setTagCount(0); } });
    return () => { live = false; };
  }, [searchTag]);

  /* every hit, unfiltered — the chips need the counts, and filtering a ranked
     list is cheaper than ranking once per chip */
  const hits = useMemo(() => {
    if (!q.trim()) return [];
    if (searchTag) return (tagHits || []).map((p) => ({ ...p, _kind: kindOf(p) }));
    if (srvNow && !srvNow.failed) {
      return srvNow.posts
        .filter((p) => !blockedRef.current.has(p.userId) && !hiddenRef.current.has(p.id))
        .map((p) => ({ ...p, _kind: kindOf(p) }));
    }
    if (!corpus) return [];
    const visible = corpus.filter(
      (p) => !blockedRef.current.has(p.userId) && !hiddenRef.current.has(p.id),
    );
    return searchPosts(visible, q, 'all', { friends: friendsRef.current });
  }, [q, corpus, searchTag, tagHits, srvNow]);

  const searchCounts = useMemo(() => {
    const c = { all: hits.length };
    hits.forEach((p) => { c[p._kind] = (c[p._kind] || 0) + 1; });
    return c;
  }, [hits]);
  const searchHits = useMemo(
    () => (searchKind === 'all' ? hits : hits.filter((p) => p._kind === searchKind)),
    [hits, searchKind],
  );
  const searchPeople = useMemo(
    () => (srvNow && !srvNow.failed && !searchTag ? srvNow.people.filter((p) => !blockedRef.current.has(p.uid)) : peopleFrom(hits)),
    [hits, srvNow, searchTag],
  );
  // a filter that survives a new query would silently hide its results
  useEffect(() => { setSearchKind('all'); }, [q]);


  const me = useMemo(() => ({
    uid: myUid,
    email: myProfile?.email || '',
    fullName: self?.fullName || myProfile?.fullName || '',
    username: self?.username || myProfile?.username || '',
    profileImage: self?.profileImage || myProfile?.profileImage || '',
    /* 🔖 what I saved — the menu's Save/Unsave and my Saved tab read it */
    savedPosts: self?.savedPosts || [],
  }), [myUid, myProfile, self]);
  /* ── ➕ THE REELS' FOLLOW BADGE ────────────────────────────────────────
     A page is followed; a person is added as a friend. A page's state is read
     once per page, the first time one of its reels is on screen. */
  const [reelPageFollows, setReelPageFollows] = useState({});
  const reelPageAsked = useRef(new Set());
  const reelFollow = useMemo(() => {
    const friendSet = new Set((friends.friends || []).map((x) => x.uid));
    const sentSet = new Set((friends.pendingOut || []).map((r) => r.toUserId));
    return {
      stateOf: (p) => reelFollowState({
        myUid,
        authorUid: p?.userId,
        pageId: p?.pageId,
        pageFollowed: p?.pageId ? reelPageFollows[p.pageId] : null,
        isFriend: !!p?.userId && friendSet.has(p.userId),
        requestSent: !!p?.userId && sentSet.has(p.userId),
      }),
      see: (p) => {
        const pid = p?.pageId;
        if (!pid || !myUid || reelPageAsked.current.has(pid)) return;
        reelPageAsked.current.add(pid);
        pageFollowedBy(pid, myUid).then((known) => {   // my own page counts as followed — it never asks
          if (known === null) return;
          setReelPageFollows((prev) => (prev[pid] === known ? prev : { ...prev, [pid]: known }));
        }).catch(() => { reelPageAsked.current.delete(pid); });
      },
      act: async (p) => {
        if (!myUid || !p) return false;
        if (p.pageId) {
          const r = await toggleFollowPage(p.pageId, myUid, false);
          if (r.ok) setReelPageFollows((prev) => ({ ...prev, [p.pageId]: true }));
          return !!r.ok;
        }
        return (await sendFriendRequest(me, { uid: p.userId, name: p.userName })) === true;
      },
    };
  }, [myUid, me, friends, reelPageFollows]);
  /* ✏️ the post being edited */
  const [editingPost, setEditingPost] = useState(null);
  useEffect(() => { editPostHandler = (p) => setEditingPost(p); return () => { editPostHandler = null; }; }, []);

  useEffect(() => watchVibeSelf(myUid, (s) => {
    setSelf(s);
    if (s) {
      blockedRef.current = s.blocked;
      hiddenRef.current = s.hiddenPosts;
      lessLikeRef.current = s.lessLike;
    }
  }), [myUid]);
  useEffect(() => watchFriends(myUid, (f) => { setFriends(f); friendsRef.current = new Set(f.friends.map((x) => x.uid)); }), [myUid]);
  const registered = !!self?.registered && !self?.underage;
  /* 🟥 THE RANKED FEED, AND WHY IT IS TWO SUBSCRIPTIONS RATHER THAN ONE.
     The server knows the whole corpus and this person's interest vectors; this
     screen knows the fifty newest posts in the product. So it asks first — and
     `rankedPage` returns null on a network failure, on an empty page, and
     deliberately when the console's kill switch is off. In every one of those
     cases `watchFeed` below runs exactly as it always has, live, unchanged.

     🟢 AND THE RANKED FEED IS STILL LIVE. `watchRanked` subscribes to the
     chosen ids rather than fetching them once, so a like or a comment lands on
     a ranked page the same way it lands on the old one. Ranking changed the
     ORDER of the feed, not what a feed is. */
  const [rankedIds, setRankedIds] = useState(null);   // null = not asked / say no
  const [feedGen, setFeedGen] = useState(0);         // bumped by pull-to-refresh
  /* 🟢 WHAT IS ON THE SCREEN, READABLE FROM A CALLBACK THAT DOES NOT RE-BIND.
     `doRefresh` is created once and handed to the gesture, so it cannot read
     `rankedIds` — and the refresh has to tell the server what it is looking at
     or it gets the same page back. A ref is the only shape that survives that. */
  const rankedIdsRef = useRef(null);
  useEffect(() => { rankedIdsRef.current = rankedIds; }, [rankedIds]);
  useEffect(() => {
    if (!registered) return undefined;
    let live = true;
    /* 🟢 THE SAME REQUEST SHAPE THE PHONE USES. `pageRequest('first')` sends no
       exclusions; `pageRequest('refresh')` sends none either AND asks the server
       to relax the seen ring — which is the difference between "refresh" and
       "the next page", and the difference the phone's refresh was missing. */
    const req = pageRequest(feedGen === 0 ? 'first' : 'refresh', rankedIdsRef.current || []);
    feedApi.rankedPage('feed', req).then((page) => {
      if (live) setRankedIds(page ? page.map((r) => r.id) : null);
    });
    return () => { live = false; };
  }, [myUid, registered, feedGen]);


  /* ══ 🔁 THE NEXT PAGE ═════════════════════════════════════════════════════
     🟥 A RANKED PAGE IS TWENTY POSTS AND THE OLD QUERY WAS FIFTY. Without this
        the browser's feed simply STOPS after twenty — which is a worse product
        than the query ranking replaced, and it reads as "the algorithm broke my
        feed" rather than as a missing fetch.

     🟢 IDS ARE APPENDED, NOT REPLACED, so `watchRanked` keeps the listeners it
        already has for the first page and adds one chunk for the new ids. The
        reader's position does not move and nothing already on screen re-renders
        from scratch. */
  const morePending = useRef(false);
  const feedEndedRef = useRef(false);
  /* ══ 📚 AFTER THE RANKER: THE ARCHIVE ═════════════════════════════════════
     🟥 "MY FEED STOPS, AND THE PLATFORM HAS MORE POSTS." An exhausted ranker
        was the end of the feed, and an unranked feed was the fifty newest with
        no next page at all. Both now continue into the archive — the next
        older posts, newest first, until the collection itself ends — and those
        posts are watched live like every other card. See services/feedPaging. */
  const archiveCursorRef = useRef(null);
  const archiveEndedRef = useRef(false);
  const [archiveIds, setArchiveIds] = useState([]);
  const [archivePosts, setArchivePosts] = useState([]);
  useEffect(() => {
    if (!archiveIds.length) { setArchivePosts([]); return undefined; }
    return feedApi.watchRanked(archiveIds, (posts) => {
      const blocked = blockedRef.current || new Set();
      const hidden = hiddenRef.current || new Set();
      setArchivePosts(posts.filter((p) => !blocked.has(p.userId) && !hidden.has(p.id)));
    });
  }, [archiveIds]);
  const feedAll = useMemo(() => {
    if (!archivePosts.length) return feed;
    const ids = new Set(feed.map((p) => p.id));
    return [...feed, ...archivePosts.filter((p) => !ids.has(p.id))];
  }, [feed, archivePosts]);
  const archiveFilter = useCallback(() => ({
    blocked: [...(blockedRef.current || [])],
    hidden: [...(hiddenRef.current || [])],
    friends: [...(friendsRef.current || [])],
  }), []);

  /* ══ 🎬 THE REELS SHELF ═══════════════════════════════════════════════════
     🟥 THE BROWSER'S FEED WAS POSTS AND NOTHING ELSE. The phone has had this
        strip since the ranker shipped, and it is where most people find video
        at all — a video post scrolls past like any other card, a shelf of them
        asks to be looked at.

     🟥 AND ITS POSITION HAS TO BE RANDOM WITHOUT MOVING. `feed` here is a LIVE
        array: it is rebuilt every time anybody likes a post, votes in a poll or
        pages another chunk in. A `Math.random()` in the render would be redrawn
        on every one of those and the shelf would walk up and down the column
        while the reader is looking at it. The seed is drawn once per feed and
        the position derived from it — random across refreshes, fixed while you
        read. See services/reelsRow.js. */
  const [reelIds, setReelIds] = useState(null);
  const [reels, setReels] = useState([]);
  const [reelsSeed, setReelsSeed] = useState(() => newReelsSeed());

  /* 🟢 ITS OWN SURFACE, ITS OWN WEIGHTS. Taking whichever videos happened to
     land in the main feed's page ranks the shelf by the FEED's weights —
     recency-led, author-led — when the whole point of the reels ranking is that
     it leans on the session vector instead. Same corpus, different question.
     `refresh: true` because a shelf of recommendations that only ever offers
     what you have not seen runs out on exactly the accounts that watch most. */
  useEffect(() => {
    if (!registered) return undefined;
    let live = true;
    feedApi.rankedPage('reels', { size: 12, refresh: true }).then((page) => {
      if (live) setReelIds(page ? page.map((r) => r.id) : null);
    });
    return () => { live = false; };
  }, [registered, myUid, feedGen]);

  /* 🟢 WATCHED, NOT FETCHED ONCE — the same treatment the feed gets. A clip
     deleted while somebody is looking at the shelf leaves it, and a view count
     under a tile is the live one. */
  useEffect(() => {
    if (!reelIds?.length) { setReels([]); return undefined; }
    return feedApi.watchRanked(reelIds, (posts) => {
      const blocked = blockedRef.current || new Set();
      const hidden = hiddenRef.current || new Set();
      /* 🟥 A SHELF OF REELS IS A SHELF OF VIDEOS. The reels surface ranks by the
         session vector, not by media type, so anything without a clip has to be
         filtered here or the row draws grey squares. */
      const usable = posts.filter((p) => videoUrlOf(p)
        && !blocked.has(p.userId) && !hidden.has(p.id));
      setReels(enoughReels(usable) ? usable.slice(0, REELS_MAX) : []);
    });
  }, [reelIds]);

  const feedWithReels = useMemo(() => {
    /* 🟥 THE SHELF IS PLACED IN THE FEED, NOT PREPENDED TO IT. `withReelsRow`
       refuses to insert nothing and keeps the index inside the list — past the
       end, `splice` appends silently, so a short feed used to turn the shelf
       into a footer, which is the "always in one place" the randomness exists to
       avoid arrived at from the other direction. */
    if (!enoughReels(reels)) return feedAll;
    return withReelsRow(feedAll, { id: REELS_ROW_ID, __reels: true }, reelsSeed);
  }, [feedAll, reels, reelsSeed]);

  /* ══ 🔁 PULL TO REFRESH ══════════════════════════════════════════════════
     🟥 THERE WAS NO WAY TO REFRESH THIS FEED. No gesture, no button, no
        re-fetch on focus — only the load-more sentinel, which appends and never
        replaces. The phone has had the gesture since the beginning.

     🟥 AND A REFRESH THAT KEPT THE PAGING STATE WOULD INHERIT THE PHONE'S BUG:
        `feedEndedRef` is what stops the feed asking again once the ranker has
        run out, and a refresh that left it set would load one page and stop. */
  const doRefresh = useCallback(async () => {
    feedEndedRef.current = false;
    morePending.current = false;
    /* the archive starts again from the newest post */
    archiveCursorRef.current = null;
    archiveEndedRef.current = false;
    setArchiveIds([]);
    /* 🟢 A NEW SHELF TOO, in new clips and a new position — a pull that left the
       strip where it was, showing what it was showing, would be a refresh that
       visibly refreshed only part of the screen. */
    setReelsSeed(newReelsSeed());
    /* 🟥 AND IT SENDS WHAT IT IS SHOWING. A refresh that excluded nothing asked
       the ranker the identical question and got the identical ten posts —
       "it pulls to refresh but the posts do not change". */
    const req = pageRequest('refresh', rankedIdsRef.current || []);
    const page = await feedApi.rankedPage('feed', req);
    /* 🟢 REPLACED, NOT APPENDED — that is what makes it a refresh. A null page
       means the ranker is off, and the unranked feed below is already live, so
       there is nothing to replace it with. */
    if (page) setRankedIds(page.map((r) => r.id));
    else setFeedGen((g) => g + 1);
  }, []);
  const pull = usePullToRefresh(doRefresh, true);

  const loadMoreFeed = useCallback(async () => {
    const source = feedSource({
      ranked: !!rankedIds, rankedExhausted: feedEndedRef.current, archiveEnded: archiveEndedRef.current,
    });
    if (!shouldFetchMore({
      ranked: source !== 'none',
      inFlight: morePending.current,
      exhausted: false,
      /* the browser renders the whole list, so what is left to show is what has
         not been scrolled past — approximated by how much is below the sentinel,
         which is zero by the time this fires */
      remaining: 0,
      batchSize: 8,
    })) return;
    morePending.current = true;
    try {
      if (source === 'ranked') {
        const next = await feedApi.rankedPage('feed', { exclude: rankedIds });
        const { fresh, exhausted } = mergeNewPage({
          have: rankedIds,
          incoming: (next || []).map((r) => ({ id: r.id })),
        });
        if (!exhausted) {
          setRankedIds((prev) => [...(prev || []), ...fresh.map((p) => p.id)]);
          return;
        }
        /* 🟢 AN EMPTY ANSWER IS FINAL FOR THE RANKER, and saying so is what stops
           us asking it again on every scroll event — but it is not the end of
           the feed: the archive carries on from here. */
        feedEndedRef.current = true;
      }
      const older = await feedApi.readArchive(archiveCursorRef, archiveEndedRef, {
        have: [...feed.map((p) => p.id), ...archiveIds], want: 8, ...archiveFilter(), me: myUid,
      });
      if (older.length) {
        setArchiveIds((prev) => [...prev, ...older.map((p) => p.id).filter((id) => !prev.includes(id))]);
      }
    } catch { /* a page that did not arrive is a shorter feed; the next scroll asks again */ } finally {
      morePending.current = false;
    }
  }, [rankedIds, feed, archiveIds, archiveFilter, myUid]);

  /* the sentinel below the last card — one observer, and it asks a screen early
     so the next posts are usually already there when the reader arrives */
  const feedEndRef = useRef(null);
  useEffect(() => {
    const node = feedEndRef.current;
    if (!node || typeof IntersectionObserver === 'undefined') return undefined;
    const io = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) loadMoreFeed();
    }, { rootMargin: '800px' });
    io.observe(node);
    return () => io.disconnect();
  }, [rankedIds, loadMoreFeed]);

  useEffect(() => {
    if (!registered) return undefined;
    const subs = [
      rankedIds
        ? feedApi.watchRanked(rankedIds, (posts) => {
          /* the block and hidden lists are applied here too: the server has
             them, but it read them a moment ago and this reference is now */
          const blocked = blockedRef.current || new Set();
          const hidden = hiddenRef.current || new Set();
          setFeed(posts.filter((p) => !blocked.has(p.userId) && !hidden.has(p.id)));
        })
        : watchFeed(
          myUid, () => blockedRef.current, () => friendsRef.current, setFeed,
          () => hiddenRef.current, () => lessLikeRef.current,
        ),
      watchNotifications(myUid, setNotifs),
      watchMyPosts(myUid, setMyPosts),
      watchMyPages(myUid, setMyPages),
    ];
    return () => subs.forEach((u) => u && u());
  }, [myUid, registered, rankedIds]);

  /* ══ 👀 HOW LONG A POST WAS ACTUALLY ON SCREEN ═══════════════════════════
     🟥 THIS IS THE MAIN FEED'S STRONGEST SIGNAL AND NOTHING WAS RECORDING IT.
        Likes are sparse, polite and strategic; how long somebody stopped is
        neither, and it is the only thing a photo post can tell us at all.

     🟢 ONE OBSERVER FOR THE WHOLE FEED, re-attached when the list changes.
        Fifty observers with fifty closures over posts that re-render on every
        like is the version of this that gets removed six months later for
        making scrolling stutter. */
  useEffect(() => {
    if (!registered || typeof IntersectionObserver === 'undefined') return undefined;
    const visible = new Set();
    const io = new IntersectionObserver((entries) => {
      for (const e of entries) {
        const id = e.target.getAttribute('data-post-id');
        if (!id) continue;
        if (e.isIntersecting) visible.add(id); else visible.delete(id);
      }
      feedSignals.dwell.setVisible([...visible]);
      /* 👁️ the same set, for the creators' numbers */
      try {
        const byId = new Map((feed || []).map((x) => [x && x.id, x]));
        viewTracker.visible([...visible].map((id) => byId.get(id)).filter(Boolean));
      } catch { /* never break a scroll */ }
    }, { threshold: 0.5 });
    const nodes = document.querySelectorAll('.vb-feed [data-post-id]');
    nodes.forEach((n) => io.observe(n));
    return () => {
      io.disconnect();
      /* 🟥 LEAVING ENDS EVERY OPEN MEASUREMENT AND SENDS IT. A dwell that is
         never closed is a dwell that never happened — and the longest ones, the
         post somebody sat and read before navigating away, are exactly the ones
         that would be lost. */
      feedSignals.dwell.clear();
      viewTracker.clear();
    };
  }, [registered, feed]);

  useEffect(() => {
    let live = true;
    if (leftMode !== 'saved') { setSavedList(null); return () => { live = false; }; }
    // 🟥 THE STUCK SPINNER. `null` is the loading state, and this used to fall to
    //    `setSavedList(null)` whenever savedPosts was EMPTY — so someone with
    //    nothing saved sat on "Loading…" for ever, waiting for a fetch that was
    //    never going to happen. An empty list is an answer, not a pending state.
    if (!self?.savedPosts?.length) { setSavedList([]); return () => { live = false; }; }
    fetchPostsByIds(self.savedPosts)
      .then((r) => live && setSavedList(r || []))
      .catch(() => live && setSavedList([]));   // a failed fetch is also an answer
    return () => { live = false; };
  }, [leftMode, self?.savedPosts]);

  // 🟢 a shared_post bubble was clicked in a chat — open that post right here:
  //    videos land in the reels player, everything else in comments
  useEffect(() => {
    if (!registered || !sharedPostId) return undefined;
    let live = true;
    const id = typeof sharedPostId === 'object' ? sharedPostId.postId : sharedPostId;
    if (!id) { onSharedPostViewed && onSharedPostViewed(); return undefined; }
    fetchPostsByIds([id]).then((r) => {
      if (!live) return;
      const p = r && r[0];
      if (p && mayOpen(p)) setPanel(videoUrlOf(p) ? { type: 'reels', post: p } : { type: 'comments', post: p });
      onSharedPostViewed && onSharedPostViewed();
    }).catch(() => { onSharedPostViewed && onSharedPostViewed(); });
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [registered, sharedPostId]);

  /* ── the gate ── */
  if (self && !self.registered) {
    return (
      <div className="vibes-shell gate">
        <div className="pair-card2 lg-card vb-gate">
          <h3>Set up Vibes</h3>
          <div className="csub">A couple of details before the feed opens — same as on your phone.</div>
          <label className="lg-field"><span className="lg-label">Date of birth</span>
            <span className="lg-input"><input type="date" value={regDob} onChange={(e) => setRegDob(e.target.value)} /></span></label>
          <label className="lg-field"><span className="lg-label">Gender</span>
            <span className="lg-input">
              <select value={regGender} onChange={(e) => setRegGender(e.target.value)}>
                <option value="">Choose…</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
                <option value="other">Other</option>
                <option value="prefer_not">Prefer not to say</option>
              </select>
            </span></label>
          <button
            className="lg-go" disabled={!regDob || !regGender}
            onClick={() => completeVibeRegistration(myUid, regDob, regGender).catch((e) => {
              /* ⚖️ the server's answer: under 13 the account is closed; a date already set is locked */
              if (e && e.code === 'underage') { window.alert("Sorry, you can't use Allo. Based on the information you gave, you aren't eligible to use an Allo account."); viewAuth.signOut().catch(() => {}); }
              else if (e && e.code === 'dob_locked') window.alert('Your date of birth is already set. To correct it, contact support.');
              else window.alert("Couldn't save that. Please try again.");
            })}
          >
            Open Vibes
          </button>
        </div>
      </div>
    );
  }
  if (self?.underage) {
    // 🟢 THE COUNTDOWN, NOT JUST A "NO". The phone's underage screen shows the exact
    //    date Vibes opens (dob + LEGAL_AGE) and how long that is — so the web says
    //    the same thing rather than leaving someone guessing.
    const unlock = vibesUnlockDate(self.dob);
    const daysLeft = unlock ? Math.max(0, Math.ceil((unlock.getTime() - Date.now()) / 864e5)) : null;
    return (
      <div className="vibes-shell gate">
        <div className="pair-card2 lg-card vb-gate">
          <h3>Vibes opens at {LEGAL_AGE}</h3>
          <div className="csub">
            The date of birth on your account puts Vibes out of reach for now — the same rule
            your phone applies.
          </div>
          {unlock && (
            <div className="vb-gate-when">
              <b>{unlock.toLocaleDateString(undefined, { day: 'numeric', month: 'long', year: 'numeric' })}</b>
              <i>
                {daysLeft === 0 ? 'Opens today' : `${daysLeft} day${daysLeft === 1 ? '' : 's'} to go`}
              </i>
            </div>
          )}
        </div>
      </div>
    );
  }
  if (!self) return <div className="vibes-shell gate"><div className="ct-empty"><span>Opening Vibes…</span></div></div>;

  /* which width the shell is actually laying out with right now */
  const paneW = panel?.type === 'reels' ? reelsWidth : rightW;

  /* ── derived analytics: everything is my own posts, counted ── */
  /* 🟥 THIS USED TO BE A HAND-ROLLED REDUCE, AND IT WAS WRONG — it is why the
     browser said "reached 6" while the phone said 3 for the same account. It
     summed `reachedUsers.length` per post, so one person who looked at three of
     your posts counted three times, and the owner's own uid (left in the ledger
     by posts made before the record-time guard) counted too. It also summed the
     raw `viewsCount` counter, which the phone abandoned because it re-counts
     replays. accountStats() is a port of the app's own effect: ledger-first,
     deduped, owner-free, and reach is the SIZE OF THE UNION across posts.
     See services/vibeStats.js for the full account of what was wrong. */
  const A = accountStats(myPosts);
  if (acctInsights && Number(acctInsights.numbers?.reach) > A.reach) A.reach = Number(acctInsights.numbers.reach);
  const unreadNotifs = notifs.filter((n) => !n.read).length;
  /* one derivation of the ban, shared with the app's table — see
     services/vibeModeration.js. Firestore rules refuse the write regardless;
     this is so the UI says why instead of failing silently. */
  const ban = banState(self);
  const banned = ban.banned;

  /* 🟢 open = hovering, OR focused, OR there is something in it worth keeping.
     The third clause is the important one: collapsing on mouse-out while
     someone is mid-sentence would throw the controls away under their hands. */
  /* 🟥 A DRAFT NO LONGER HOLDS THE COMPOSER OPEN. It used to, so that it could
     not collapse mid-sentence — but focus already covers that, and the side
     effect was that a half-written thought left the big styled card sitting
     open on the feed forever. Attachments and a status still hold it, because
     those are genuinely hard to get back if the panel folds away. */

  // eligible: short, no media. Silent and reversible in both directions.
  const styleEligible = canStyleText(draft, files.length > 0, !!poll);
  const styleOn = composeOpen && styleEligible && !styleOff;

  /* 💬 `commentId` is a bell row's landing — the thread scrolls to and flashes it */
  const openComments = (p, commentId = null) => setPanel({ type: 'comments', post: p, ...(commentId ? { commentId: String(commentId) } : {}) });
  /* 🟢 A SEPARATE MODE FROM COMMENTS. Comments is a thread with the post as a
     header; this is the post itself — the picture at pane size, arrows through a
     carousel, a download button — with the thread underneath so a reply still
     costs no navigation. */
  const openPost = (p, index = 0) => setPanel({ type: 'post', post: p, index });
  /* ⭐ OPEN THE ORIGINAL BEHIND A SHARE. The embed holds a SNAPSHOT — enough to
     paint, deliberately not enough to be trusted — so the tap reads the live post
     and opens ITS comment thread. That is the difference the person asked for:
     the outer card's comments belong to whoever shared it, this opens the
     original author's.

     🟥 AND IT MUST HANDLE THE POST BEING GONE. A snapshot outlives the thing it
     copied; tapping a deleted original has to say so rather than doing nothing,
     which is indistinguishable from a broken button. Same wording as the inline
     tombstone, so a share whose original vanishes reads the same either way. */
  const openPostById = (id) => {
    if (!id) return;
    /* 🟥 ALWAYS A READ, NEVER THE FEED COPY. Serving this from the loaded feed
       would be faster and would quietly reintroduce the exact staleness the
       snapshot already has — and the feed holds 50 posts, so an original older
       than that is a miss anyway. One read, live, every time. */
    fetchPostsByIds([id])
      .then((r) => {
        const p = r && r[0];
        if (p && mayOpen(p)) setPanel(videoUrlOf(p) ? { type: 'reels', post: p } : { type: 'comments', post: p });
        else setEmbedMiss(true);
      })
      .catch(() => setEmbedMiss(true));
  };
  const openInsights = (p) => setPanel({ type: 'insights', post: p });
  const openVideo = (p) => setPanel({ type: 'reels', post: p });
  const openProfile = (u) => setPanel({ type: 'profile', user: u });

  /* 🟥 THE QUESTION IS THE POST'S TEXT. A poll card needs a heading, and asking
     for a question in one box and a caption in another produces posts with two
     of them — so the draft IS the question, which is also what the phone does.
     🟥 A PLAIN CONST, NOT useMemo. This component has an early return above it,
     so a hook here is a hook that runs on some renders and not others — React's
     rule about call order, and a real crash rather than a lint opinion. The work
     is three string trims; memoising it was never worth a hook anyway. */
  const pollFields = poll
    ? buildPollFields({ question: draft, options: poll.options, multiple: poll.multiple }, Date.now())
    : null;

  /* ✍️ "Create post" — open (bringing a saved draft back into an empty composer),
     and close through "Save as draft / Discard post" */
  const savedDraft = draftTick >= 0 ? peekDraft(myUid) : null;
  const composeHasContent = draftWorthKeeping({ text: draft, media: files.map(() => ({ uri: 'x', type: 'image' })), poll, tags: tagged, status: postStatus, feeling, music });
  const flashNotice = (msg) => {
    setComposeNotice(msg);
    clearTimeout(noticeTimer.current);
    noticeTimer.current = setTimeout(() => setComposeNotice(null), 2400);
  };
  const resetComposer = () => {
    setDraft(''); setFiles([]); setEdits([]); setPostStatus(null); setCommentsOn(true); setPoll(null); setAiDeclared(false); setTagged([]);
    setFeeling(null); setMusic(null); setAudience('everyone'); setPostAs('me');
    setStyleOff(false); setTextStyle({ ...DEFAULT_TEXT_STYLE, bg: SUGGESTED_BG });
  };
  const openComposer = async (withPhoto) => {
    if (banned) return;
    setComposeOpen(true);
    if (withPhoto) { fileRef.current?.click(); return; }
    if (composeHasContent) return;
    const got = await loadDraft(myUid);
    if (!got) return;
    const d = got.draft;
    setDraft(d.text || '');
    setFiles(got.files || []);
    setEdits((got.files || []).map((_, i) => (got.edits && got.edits[i]) || blankEdit()));
    setPoll(d.poll); setTagged(d.tags || []); setPostStatus(d.status); setFeeling(d.feeling); setMusic(d.music);
    if (d.textStyle) setTextStyle(d.textStyle);
    setStyleOff(!!d.styleOff);
    setAudience(d.audience); setCommentsOn(d.comments); setAiDeclared(d.ai);
    setPostAs(d.pageId && myPages.some((pg) => pg.id === d.pageId) ? d.pageId : 'me');
    flashNotice(composeL('draft_back'));
  };
  const leaveComposer = (how) => {
    if (posting) return;
    if (how === 'save') {
      saveDraft(myUid, {
        text: draft, textStyle, styleOff, poll, tags: tagged.map((x) => ({ uid: x.uid, name: x.name, ...(x.image ? { image: x.image } : {}) })),
        status: postStatus, feeling, audience, comments: commentsOn, ai: aiDeclared, music, pageId: postAs !== 'me' ? postAs : null,
      }, files, edits).catch(() => {});
    } else if (how === 'discard') {
      clearDraft(myUid).catch(() => {});
    }
    setComposeOpen(false);
    resetComposer();
  };

  const submitPost = async () => {
    /* 🟥 I ALMOST BLANKED THIS FOR POLLS, AND IT WOULD HAVE COST MORE THAN IT FIXED.
       The duplicate question in `content` is what the doubled render came from —
       but `content` is also what createPost feeds to tagsForPost and
       safetyForPost, and what the grid tile shows as its title. Blanking it would
       have made a poll untagged, unscanned by moderation and nameless in the grid
       to fix a cosmetic repeat.

       🟢 So the document keeps both fields, which is right — a poll SHOULD be
          searchable and moderated by its question — and the renderers decline to
          draw the caption when it is only the question again
          (pollQuestionIsTheContent). That also heals every poll already posted,
          which a composer-side fix never could. */
    const text = draft.trim();
    /* 🟢 a poll is postable on its own: the question is the words */
    if ((!text && !files.length && !pollFields) || posting) return;
    // 🟢 belt and braces: the rules reject a banned account's write anyway, but
    //    letting the request go and fail would look like a bug rather than a ban
    if (banned) return;
    setPosting(true);
    try {
      const asPage = postAs !== 'me' ? myPages.find((pg) => pg.id === postAs) || null : null;
      await createPost({
        me, text, files, audience, asPage, status: postStatus, commentsEnabled: commentsOn,
        aiDeclared,
        /* 🎨 what the editor did to the first item — the phone stamps the post
           from `mediaItems[0]` the same way, so a browser that stamped it from
           some other item would produce posts the phone renders differently. */
        mediaEdits: edits,
        // only sent for a text-only post — a background behind a photo caption
        // would fight the photo and win
        textStyle: styleOn ? textStyle : null,
        poll: pollFields,
        tagged,
        feeling,
        music,
      });
      setDraft(''); setFiles([]); setEdits([]); setPostStatus(null); setCommentsOn(true); setPoll(null); setAiDeclared(false); setTagged([]);
      setStyleOff(false); setTextStyle({ ...DEFAULT_TEXT_STYLE, bg: SUGGESTED_BG });
      setFeeling(null); setMusic(null); setComposeOpen(false);
      clearDraft(myUid).catch(() => {});
      // the thing you just wrote has to be findable — drop the cached corpus so
      // the next search fetches a world that includes it
      invalidateSearchCorpus(); setCorpus(null);
    } catch { /* the feed listener shows it when it lands */ }
    setPosting(false);
  };

  // 🟢 picked media: videos are checked against the app's 1-minute rule before
  //    they're allowed in — everything else just joins the tray (max 4).
  const addPicked = async (picked) => {
    const ok = [];
    for (const f of picked) {
      if (f.type?.startsWith('video/')) {
        const fits = await new Promise((res) => {
          const v = document.createElement('video');
          v.preload = 'metadata';
          v.onloadedmetadata = () => { const d = v.duration; URL.revokeObjectURL(v.src); res(!Number.isFinite(d) || d <= 61); };
          v.onerror = () => { URL.revokeObjectURL(v.src); res(true); };
          v.src = URL.createObjectURL(f);
        });
        if (!fits) { alert('Videos can be up to 1 minute long.'); continue; }
      }
      ok.push(f);
    }
    // no need to turn anything off here: canStyleText() already refuses a draft
    // with media, so the card steps aside on its own
    if (!ok.length) return;
    setFiles((prev) => [...prev, ...ok].slice(0, 4));
    setEdits((prev) => [...prev, ...ok.map(() => blankEdit())].slice(0, 4));
    /* 🟢 THE EDITOR OPENS ON PICK, the way the phone's does. Making it a button
       people have to find would leave the whole feature undiscovered on the
       client that never had it — and "Done" with nothing changed costs one tap
       and writes exactly the post an unedited pick would have written. */
    setEditing(true);
  };

  /* 🟥 REMOVING A FILE REMOVES ITS EDITS. Dropping only the file leaves every
     later item wearing the edits of the one before it. */
  const removeAt = (n) => {
    setFiles((prev) => prev.filter((_, k) => k !== n));
    setEdits((prev) => prev.filter((_, k) => k !== n));
  };

  return (
    <div
      ref={shellRef}
      className={`vibes-shell ${panel ? 'squeezed' : ''} ${leftOpen ? 'left-open' : ''}`}
      /* reels bring their own width — see reelsFitWidth() */
      style={paneW > 0 ? { '--vb-right-w': `${paneW}px` } : undefined}
    >
      {/* 🟢 tapping anywhere off the panel closes it — the same gesture every
          sheet in the app answers to. Rendered always but inert on a desktop,
          where mobile.css gives it no size. */}
      <button
        className="vb-left-scrim"
        aria-label="Close"
        tabIndex={leftOpen ? 0 : -1}
        onClick={() => setLeftOpen(false)}
      />

      {/* ══ left: my vibe ══ */}
      <aside className="vb-left">
        <div className="vb-left-head">
          {/* 👤 🟥 NOTHING OPENED YOUR OWN PAGE — your name and picture here were
              just a label. They are the way to your page now, as on the phone. */}
          <button className="vb-left-me" title="My page" onClick={() => { setLeftOpen(false); openProfile({ uid: myUid, name: me.fullName, image: me.profileImage }); }}>
            <Pic src={me.profileImage} name={me.fullName} size={40} />
            <div className="vb-left-name"><b>{me.fullName || 'Me'}</b><i>@{me.username}</i></div>
          </button>
          <button className={`icon-btn ${leftMode === 'notifs' ? 'lit' : ''}`} title="Notifications"
            onClick={() => setLeftMode(leftMode === 'notifs' ? 'me' : 'notifs')}>
            <Bell width={18} height={18} />
            {unreadNotifs > 0 && <span className="vb-badge">{unreadNotifs}</span>}
          </button>
          <button className={`icon-btn ${leftMode === 'friends' ? 'lit' : ''}`} title="Friends"
            onClick={() => setLeftMode(leftMode === 'friends' ? 'me' : 'friends')}>
            <GroupIcon width={18} height={18} />
            {friends.pendingIn.length > 0 && <span className="vb-badge">{friends.pendingIn.length}</span>}
          </button>
        </div>

        <div className="vb-left-body" onScroll={onLeftScroll}>
          {leftMode === 'me' && (
            <>
              <WorldMapCard onOpen={() => setWorldOpen(true)} />
              <div className="vb-sec">
                <div className="ap-label">Account status</div>
                <div className={`vb-status ${banned ? 'bad' : self.strikes ? 'warn' : ''}`}>
                  <ShieldCheck width={16} height={16} />
                  {banned
                    ? (ban.permanent
                      ? 'Restricted — this account can no longer post'
                      : `Restricted — you can post again on ${ban.until.toLocaleDateString(undefined, { day: 'numeric', month: 'long' })}`)
                    : self.strikes ? `${self.strikes} strike${self.strikes === 1 ? '' : 's'} on record`
                      : 'In good standing'}
                </div>
              </div>
              <div className="vb-sec">
                <div className="ap-label">Your vibe</div>
                <div className="vb-stats">
                  <div><b>{A.posts}</b><span>Posts</span></div>
                  {/* impressions = non-video posts; video views = video posts.
                      The phone splits them the same way, so the two agree. */}
                  <div><b>{A.impressions}</b><span>Impressions</span></div>
                  <div><b>{A.videoViews}</b><span>Video views</span></div>
                  <div><b>{A.reach}</b><span>Accounts reached</span></div>
                  <div className={A.reports ? 'bad' : ''}><b>{A.reports}</b><span>Reports</span></div>
                </div>
                <div className="vb-stats-note">
                  One person counts once, however often they look — and never you.
                </div>
              </div>
              <div className="vb-sec">
                <div className="ap-label">Your pages</div>
                {myPages.map((pg) => (
                  <button
                    className="vb-row wide" key={pg.id}
                    onClick={() => openProfile({ uid: pg.ownerUid, name: pg.name, image: pg.profileImage, pageId: pg.id })}
                  >
                    <Pic src={pg.profileImage} name={pg.name} size={32} />
                    <div><b>{pg.name}</b><i>{pg.category || 'Page'} · {pg.followersCount || 0} followers</i></div>
                  </button>
                ))}
                <button className="vb-navrow" onClick={() => setPageMaker(true)}>
                  <PersonAdd2 width={16} height={16} /> Create a page
                </button>
              </div>
              <div className="vb-sec">
                <button className="vb-navrow" onClick={() => setLeftMode('saved')}>
                  <Bookmark width={16} height={16} /> Saved videos <b>{self.savedPosts.length}</b>
                </button>
              </div>
            </>
          )}

          {leftMode === 'saved' && (() => {
            /* 🟥 THERE WAS NO WAY OUT OF HERE. Friends and Notifications are
               toggled by the header icons, so clicking the lit icon returns you.
               Saved is entered from a row in the analytics list and had no
               matching exit — once you were in, you were in. Hence the back
               button, which is now the first thing in the panel. */
            /* 🟢 VIDEOS ONLY — the bookmark only ever appears on a reel, so a
               photo can never enter this list. The All/Photos/Videos tabs I
               added were answering a question the product doesn't ask; a photo
               tab that can only ever be empty is worse than no tab. */
            const shown = savedList ? savedList.filter((p) => !!videoUrlOf(p)) : [];
            return (
              <div className="vb-sec">
                <button className="vb-back" onClick={() => setLeftMode('me')}>
                  <Back width={15} height={15} /> Your vibe
                </button>

                <div className="ap-label">Saved videos{savedList ? ` · ${shown.length}` : ''}</div>

                {savedList === null ? (
                  <div className="ct-empty subtle"><span>Loading…</span></div>
                ) : shown.length === 0 ? (
                  <div className="ct-empty subtle">
                    <span>No saved videos yet — tap the bookmark on a reel.</span>
                  </div>
                ) : shown.map((p) => (
                  <button
                    className="vb-row wide" key={p.id}
                    onClick={() => (videoUrlOf(p) ? openVideo(p) : openPost(p, 0))}
                  >
                    {bestPostImg(p)
                      ? <SafeImg className="vb-mini" src={bestPostImg(p)} kind="inline" />
                      : <Pic src={p.userImage} name={p.userName} size={32} />}
                    <div>
                      <b>{p.userName}</b>
                      <i>{(p.content || (videoUrlOf(p) ? 'Video' : 'Photo')).slice(0, 46)}</i>
                    </div>
                    {videoUrlOf(p) && <span className="vb-saved-kind"><VideoIcon width={13} height={13} /></span>}
                  </button>
                ))}
              </div>
            );
          })()}

          {leftMode === 'friends' && (
            <div className="vb-sec">
              {friends.pendingIn.length > 0 && <div className="ap-label">Requests</div>}
              {friends.pendingIn.map((r) => (
                <div className="vb-row" key={r.id}>
                  <Pic src={r.fromUserImage} name={r.fromUserName} size={32} />
                  <div><b>{r.fromUserName}</b><i>wants to be friends</i></div>
                  <button className="icon-btn ok" title="Accept" onClick={() => acceptFriendRequest(r, me).catch(() => {})}><Check width={16} height={16} /></button>
                  <button className="icon-btn" title="Decline" onClick={() => declineFriendRequest(r.id).catch(() => {})}><X width={16} height={16} /></button>
                </div>
              ))}
              <div className="ap-label">Friends · {friends.friends.length}</div>
              {friends.friends.length === 0 && <div className="ct-empty subtle"><span>No friends yet — add people from their posts.</span></div>}
              {friends.friends.map((f) => (
                <button className="vb-row wide" key={f.uid} onClick={() => openProfile({ uid: f.uid, name: f.name, image: f.image })}>
                  <Pic src={f.image} name={f.name} size={32} />
                  <div><b>{f.name || 'Allo user'}</b></div>
                </button>
              ))}
            </div>
          )}

          {leftMode === 'notifs' && (
            <div className="vb-sec">
              <div className="ap-label">Notifications</div>
              {notifs.length === 0 && <div className="ct-empty subtle"><span>Nothing yet.</span></div>}
              {notifs.slice(0, 40).map((n) => {
                /* ⭐ MODERATION OUTCOMES get their own row and their own tap.
                   🟥 They must NOT fall through to the generic onPress below,
                   which calls openComments({ id: n.postId }) — for a REMOVED post
                   that document is gone, so the reporter would land on an empty
                   comments sheet. That dead end is what this feature replaces. */
                /* ©️ THE SOUND WAS REMOVED — its own row, its own tap (the sheet) */
                if (n.type === 'post_muted') {
                  return (
                    <button
                      className={`vb-row wide ${n.read ? '' : 'unread'}`} key={n.id}
                      onClick={() => { markNotificationRead(n.id).catch(() => {}); setMutedSheet({ notif: n }); }}
                    >
                      <span className="vb-modout-dot" style={{ background: '#D6454522', color: '#D64545' }}>🔇</span>
                      <div>
                        <b style={{ color: '#D64545' }}>{mt('vibe_ui_allo_team')}</b>
                        <i>{mt(RIGHTS_STR.mutedNotif)}{n.track?.title ? ` · ♪ ${n.track.title}` : ''}</i>
                      </div>
                    </button>
                  );
                }
                if (n.type === 'report_update' || n.type === 'post_action' || n.type === 'comment_action') {
                  const isAuthor = n.type === 'post_action' || n.type === 'comment_action';
                  const accent = isAuthor ? '#FF6B6B' : (n.outcome === 'removed' ? '#5B8DEF' : '#3BB273');
                  return (
                    <button
                      className={`vb-row wide ${n.read ? '' : 'unread'}`} key={n.id}
                      onClick={() => {
                        markNotificationRead(n.id).catch(() => {});
                        setModOutcome(n);
                      }}
                    >
                      <span className="vb-modout-dot" style={{ background: accent + '22', color: accent }}>
                        {isAuthor ? '!' : '✓'}
                      </span>
                      <div>
                        <b style={{ color: accent }}>{mt('vibe_ui_allo_team')}</b>
                        <i>{mt(n.type === 'comment_action'
                          ? 'vibe_notif_comment_action'
                          : (isAuthor ? 'vibe_notif_post_action' : 'vibe_notif_report_update'))}</i>
                      </div>
                    </button>
                  );
                }
                /* 🎂 a friend's birthday: the row opens the person */
                if (n.type === 'birthday') {
                  return (
                    <button className={`vb-row wide ${n.read ? '' : 'unread'}`} key={n.id}
                      onClick={() => { markNotificationRead(n.id).catch(() => {}); openProfile({ uid: n.fromUserId, name: n.fromUserName, image: n.fromUserImage }); }}>
                      <Pic src={n.fromUserImage} name={n.fromUserName} size={32} />
                      <div>
                        <b>{n.fromUserName || 'A friend'}</b>
                        <i><Cake width={12} height={12} /> has a birthday today — wish them a happy birthday!</i>
                      </div>
                    </button>
                  );
                }
                /* 🏷 a tag waiting for a yes — answered right here */
                if (n.type === 'tag_request') {
                  const done = tagAnswered[n.id] || n.handled;
                  const answer = async (e, yes) => {
                    e.stopPropagation();
                    setTagAnswered((m) => ({ ...m, [n.id]: yes ? 'approved' : 'declined' }));
                    markNotificationRead(n.id).catch(() => {});
                    const r = await respondTag(n.postId, yes);
                    if (!r || !r.ok) {
                      if (r && r.status === 409) setTagAnswered((m) => ({ ...m, [n.id]: r.state === 'approved' || r.state === 'declined' ? r.state : 'gone' }));
                      else setTagAnswered((m) => { const x = { ...m }; delete x[n.id]; return x; });
                    }
                  };
                  return (
                    <div className={`vb-row wide ${n.read ? '' : 'unread'}`} key={n.id} role="button" tabIndex={0}
                      onClick={() => { markNotificationRead(n.id).catch(() => {}); if (n.postId) openComments({ id: n.postId, comments: [], userId: '' }, null); }}>
                      <Pic src={n.fromUserImage} name={n.fromUserName} size={32} />
                      <div>
                        <b>{n.fromUserName || 'Allo'}</b>
                        <i>wants to tag you in a post</i>
                        {done ? (
                          <i>{done === 'approved' ? 'You approved this tag' : done === 'declined' ? 'You declined this tag' : 'This tag is no longer waiting'}</i>
                        ) : (
                          <span className="vb-tagreq">
                            <button className="yes" onClick={(e) => answer(e, true)}>Approve</button>
                            <button onClick={(e) => answer(e, false)}>Decline</button>
                          </span>
                        )}
                      </div>
                    </div>
                  );
                }
                return (
                  <button
                    className={`vb-row wide ${n.read ? '' : 'unread'}`} key={n.id}
                    onClick={() => { markNotificationRead(n.id).catch(() => {}); if (n.postId) openComments({ id: n.postId, comments: [], userId: '' }, n.commentId || null); }}
                  >
                    <Pic src={n.fromUserImage} name={n.fromUserName} size={32} />
                    <div>
                      <b>{n.fromUserName || 'Allo'}</b>
                      <i>
                        {/* 💬 a reply names what it answered, because the row is
                            otherwise a stray line of somebody else's text */}
                        {n.type === 'like' ? 'liked your post'
                          : n.type === 'reply' ? `replied to your comment: ${n.commentContent || ''}`
                            /* 💬 "@someone" — the same words as the phone's row and push */
                            : n.type === 'mention' ? `${MENTION_NOTIF_TEXT}: ${n.commentContent || ''}`
                            : n.type === 'comment' ? `commented: ${n.commentContent || ''}`
                            : n.type === 'tag' ? 'tagged you in a post'
                            : n.type === 'story_reply' ? `replied to your story: ${n.commentContent || ''}`
                            : n.type === 'story_reaction' ? `reacted ${n.emoji || ''} to your story`
                              : n.type === 'friend_request' ? 'sent a friend request'
                                : n.type === 'friend_request_accepted' ? 'accepted your request'
                                  : n.type === 'strike' ? (n.violationReason || 'Content notice') : 'notification'}
                      </i>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* 🟢 THE RAIL LIVES HERE IN VIBES. App owns the component (it knows the
            current view and the badge counts) and hands it down, but it is
            rendered INSIDE the analytics column so it floats over that pane
            rather than over the middle of the whole screen — and it disappears
            with the column on narrow windows instead of hovering over nothing. */}
        {navRail}
      </aside>

      {/* ══ centre: the feed ══ */}
      <main className="vb-feed" ref={pull.attach}>
        {/* 🔁 the pull indicator. It is INSIDE the scroller and at the top of
            it, so it occupies the space the finger has opened rather than
            floating over the first post. Height is the pull itself, which is
            what makes the gesture feel attached to the content. */}
        <div className="vb-pull" style={{ height: pull.busy ? 44 : pull.distance }} aria-hidden={!pull.busy}>
          <span className={`vb-pull-dot ${pull.busy ? 'spin' : ''} ${pull.armed ? 'armed' : ''}`} />
        </div>
        {/* ══ THE TOP BAR ═══════════════════════════════════════════════════
            Spans the feed column — which means it starts where the analytics
            column ends and runs to the edge of the window, and squeezes with
            the feed when a pane opens on the right, because it IS part of the
            feed column rather than something floated over the whole shell.

            🟢 THE MARK NEVER PLAYS HERE. `still` parks the Lottie on its final
               frame: in the chat list the draw-on is a greeting, but a toolbar
               that twitches every time the page re-renders is a distraction. */}
        <div className="vb-topbar">
          {/* 🟢 THE WAY BACK TO "MY VIBE" ON A PHONE. The analytics column is a
              permanent 300px pane on a desktop; on a phone it becomes a panel
              that slides in, and this is the only thing that can open it. It is
              the person's own avatar rather than a hamburger, because that is
              what the panel contains — their status, their friends, their
              notifications and their numbers. Hidden above 820px by mobile.css,
              where the column is simply there. */}
          <button
            className="vb-mine-btn"
            onClick={() => setLeftOpen(true)}
            aria-label="My vibe, friends and notifications"
            title="My vibe"
          >
            <Pic src={me.profileImage} name={me.fullName} size={30} />
            {(unreadNotifs > 0 || friends.pendingIn.length > 0) && <span className="vb-badge sm" />}
          </button>
          <span className="vb-topbrand"><BrandLottie size={80} bare still /></span>
          <div className={`vb-searchbox ${searching ? 'on' : ''}`}>
            <Search width={16} height={16} />
            <input
              value={qLive}
              onChange={(e) => setQLive(e.target.value)}
              placeholder="Search vibes"
              aria-label="Search vibes"
              spellCheck={false}
              onKeyDown={(e) => { if (e.key === 'Escape') { setQLive(''); e.currentTarget.blur(); } }}
            />
            {searching && (
              <button className="vb-search-x" onClick={() => setQLive('')} aria-label="Clear search">
                <X width={14} height={14} />
              </button>
            )}
          </div>
          {/* 🟥 A GESTURE THE DEVICE DOES NOT HAVE IS NOT AN AFFORDANCE. Pull to
              refresh is the right thing on a phone and does not exist on a
              laptop — no touch events, nothing to pull — so on the machine most
              people read this client on there was no way to refresh the feed at
              all, which is what was reported. The wheel handler in
              usePullToRefresh covers the trackpad; this covers everybody, and it
              is the only control here that says out loud that the feed CAN be
              refreshed.
              🟢 IT SPINS WHILE THE PULL RUNS, so the button and the gesture are
                 visibly the same action rather than two things that happen to do
                 the same thing. */}
          <button
            className={`vb-refresh ${pull.busy ? 'spin' : ''}`}
            onClick={() => pull.refresh()}
            disabled={pull.busy}
            aria-label="Refresh the feed"
            title="Refresh"
          >
            <Refresh width={17} height={17} />
          </button>
        </div>

        {searching ? (
          <SearchResults
            q={qLive} kind={searchKind} setKind={setSearchKind}
            tag={searchTag} tagCount={tagCount}
            counts={searchCounts} results={searchHits} people={searchPeople}
            loading={searchTag
              ? tagHits === null
              : (!srvNow || (srvNow.failed && (searchLoading || (!corpus && !!q.trim()))))}
            onOpen={(p) => (videoUrlOf(p) ? openVideo(p) : openPost(p, 0))}
            onProfile={openProfile}
          />
        ) : (<>
        {/* ✍️ THE TOP OF THE FEED, AS FACEBOOK LAYS IT OUT — your picture, "What's on
            your mind?" and a photo button, ABOVE the stories, then a rule between
            the stories and the posts. Clicking opens "Create post"
            (CreatePostDialog), the phone's full-screen composer as a dialog; the
            old inline composer that grew on hover is gone. */}
        <div className={`vb-compose-card ${banned ? 'off' : ''}`}>
          <button type="button" className="vb-compose-me" onClick={() => openProfile({ uid: myUid, name: me.fullName, image: me.profileImage })} aria-label="My page">
            <Pic src={me.profileImage} name={me.fullName} size={40} />
          </button>
          <button type="button" className="vb-compose-pill" disabled={banned} onClick={() => openComposer(false)}>
            {banned ? 'Posting is paused on this account'
              : savedDraft ? <><b className="vb-compose-draft">{composeL('draft')}:</b> <i>{draftPreview(savedDraft)}</i></>
                : `What's on your mind${me.fullName ? `, ${String(me.fullName).split(' ')[0]}` : ''}?`}
          </button>
          <button type="button" className="vb-compose-photo" disabled={banned} onClick={() => openComposer(true)} title="Photo/video" aria-label="Photo/video">
            <ImageIcon width={22} height={22} />
          </button>
        </div>
        {/* 📸 STORIES — friends' (and mine) for 24 hours; "Create story" uses the same editor */}
        <StoriesBar me={me} onProfile={openProfile} />
        <div className="vb-stories-sep" role="separator" />

        {composeOpen && (
          <CreatePostDialog
            me={me}
            author={postAs === 'me'
              ? { name: me.fullName || me.username || 'Me', image: me.profileImage }
              : { name: (myPages.find((pg) => pg.id === postAs) || {}).name || me.fullName, image: (myPages.find((pg) => pg.id === postAs) || {}).profileImage || me.profileImage }}
            pages={myPages}
            postAsId={postAs}
            onPostAs={setPostAs}
            editor={(
              <>
            <TextPostCard
              editable
              style={styleOn ? textStyle : { ...textStyle, bg: 'plain' }}
              value={draft}
              onChange={setDraft}
              disabled={banned}
              /* 🟢 the one text box IS the poll's question while a poll is attached,
                 so it stops inviting a status update. The app does the same. */
              placeholder={banned ? 'Posting is paused on this account'
                : poll ? 'Poll question' : 'Share a vibe…'}
            />
            {/* 🏷 WHAT THIS POST WILL BE FILED UNDER, BEFORE IT IS POSTED.
                🟥 A TAG YOU ONLY SEE AFTERWARDS IS A TAG YOU CANNOT FIX. The
                   parser has rules — a hash has to start a word, a number is
                   not a tag, a link's fragment is not a tag, twelve is the cap
                   — and none of them are guessable. Showing the result while
                   you type is how they stop being surprises.
                🟢 VIDEOS ONLY, matching what actually gets written. Chips under
                   a photo draft would promise an index the post never joins. */}
            {composerTags.length > 0 && (
              <div className="vb-comp-tags">
                {composerTags.map((tag) => (
                  <span key={tag} className="vb-comp-tag">{`#${tag}`}</span>
                ))}
              </div>
            )}
              </>
            )}
            extras={(
              <div className="cp-extras">
              {files.length > 0 && (
                <>
                  {/* 🟢 A WAY BACK IN, ON THE TRAY. The editor opens on pick, but
                      somebody who closed it — or who wants to change one thing
                      after writing the caption — needs a door that is not
                      "remove the file and add it again". */}
                  <MediaTray
                    files={files} onRemove={removeAt}
                    onEdit={() => setEditing(true)} edited={edits.some(isEdited)}
                  />
                </>
              )}

              {/* the bar stays while the draft QUALIFIES, even after None was
                  picked — otherwise turning a background off would also remove
                  the only way to turn one back on */}
              {styleEligible && (
                <TextStyleBar
                  style={styleOff ? { ...textStyle, bg: 'plain' } : textStyle}
                  disabled={banned}
                  onChange={(next) => {
                    if (next.bg === 'plain') { setStyleOff(true); return; }
                    setStyleOff(false);
                    setTextStyle(next);
                  }}
                />
              )}

              {/* 📊 THE POLL EDITOR. The question is the draft above — a poll card
                  has one heading, and a second box for it produces posts with
                  two. Answers are ids from the moment they exist so typing in a
                  row never re-keys it under the person's cursor. */}
              {poll && (
                <div className="vb-comp-poll">
                  {poll.options.map((o, i) => (
                    <div key={o.id} className="vb-comp-pollrow">
                      <span>{i + 1}</span>
                      <input
                        value={o.text} maxLength={100} placeholder={`Answer ${i + 1}`}
                        onChange={(e) => setPoll((p) => ({
                          ...p, options: p.options.map((x) => (x.id === o.id ? { ...x, text: e.target.value } : x)),
                        }))}
                      />
                      {poll.options.length > 2 && (
                        <button
                          type="button" aria-label="Remove this answer"
                          onClick={() => setPoll((p) => ({ ...p, options: p.options.filter((x) => x.id !== o.id) }))}
                        ><X width={13} height={13} /></button>
                      )}
                    </div>
                  ))}
                  <div className="vb-comp-pollfoot">
                    {poll.options.length < MAX_POLL_OPTIONS && (
                      <button
                        type="button" className="btn ghost sm"
                        onClick={() => setPoll((p) => ({ ...p, options: [...p.options, blankPollOption()] }))}
                      >Add an answer</button>
                    )}
                    <label>
                      <input
                        type="checkbox" checked={poll.multiple}
                        onChange={(e) => setPoll((p) => ({ ...p, multiple: e.target.checked }))}
                      />
                      People can pick more than one
                    </label>
                    {/* 🟥 THE REASON THE BUTTON IS STILL DISABLED, said here rather
                        than left as a dead control somebody clicks twice. */}
                    {!pollFields && (
                      <span className="vb-sub">
                        {draft.trim()
                          ? 'Two different answers are needed.'
                          : 'Write the question above.'}
                      </span>
                    )}
                  </div>
                </div>
              )}

              </div>
            )}
            styleOn={styleOn}
            onBackground={() => {
              if (!styleEligible) { flashNotice(composeL('background_hint')); return; }
              setStyleOff((v) => !v);
            }}
            mediaCount={files.length}
            hasVideo={files.some((f) => String(f.type || '').startsWith('video/'))}
            pollOn={!!poll}
            onPoll={() => setPoll((pl) => (pl ? null : { options: [blankPollOption(), blankPollOption()], multiple: false }))}
            tags={tagged}
            onOpenTags={() => setTagPick(true)}
            status={postStatus}
            onOpenStatus={() => setStatusPick(true)}
            onClearStatus={() => setPostStatus(null)}
            feeling={feeling}
            onFeeling={setFeeling}
            audience={audience}
            onAudience={setAudience}
            comments={commentsOn}
            onComments={setCommentsOn}
            ai={aiDeclared}
            onAi={setAiDeclared}
            music={music}
            onMusic={setMusic}
            onPickMedia={() => fileRef.current?.click()}
            text={draft}
            canPost={poll ? !!pollFields : (!!draft.trim() || files.length > 0)}
            posting={posting}
            banned={banned}
            onPost={submitPost}
            hasContent={composeHasContent}
            onLeave={leaveComposer}
            notice={composeNotice}
            blockEsc={tagPick || statusPick || editing}
          />
        )}
          <input ref={fileRef} type="file" accept="image/*,video/*" multiple hidden
            onChange={(e) => { addPicked(Array.from(e.target.files || [])); e.target.value = ''; }} />

        {/* 🎨 THE EDITOR, over everything. `initial` is the current edits, so
            re-opening it resumes where you left off instead of discarding the
            stickers you already placed — and Cancel keeps them for the same
            reason: it closes the editor, it does not undo your work. */}
        {editing && files.length > 0 && (
          <MediaEditor
            files={files}
            initial={edits}
            /* 🟢 THE PREVIEW IS ONLY A PREVIEW IF IT KNOWS WHOSE POST THIS IS.
               The byline, the avatar and the caption under the picture are what
               a sticker actually has to avoid, so the editor is handed the same
               `me` and the same draft the post will carry. */
            me={me}
            caption={draft}
            onCancel={() => setEditing(false)}
            onDone={(next) => { setEdits(next); setEditing(false); }}
          />
        )}

        {feedWithReels.map((p) => (p.__reels ? (
          /* 🎬 the shelf, wherever the placement rule put it this time */
          <ReelsRow
            key={REELS_ROW_ID}
            items={reels}
            onOpen={openVideo}
            onRefresh={() => { setReelsSeed(newReelsSeed()); setFeedGen((g) => g + 1); }}
          />
        ) : (
          <PostCard
            key={p.id} p={p} me={me}
            onLike={(post, liked, rx) => { if (!liked) feedSignals.liked(post.id); likeWithNotify(post, me, liked, rx || null).catch(() => {}); }}
            onComments={openComments}
            onShare={(post) => setShareFor(post)}
            onVideo={openVideo}
            onProfile={openProfile}
            onOpenPost={openPost}
            onOpenPostId={openPostById}
            onInsights={openInsights}
            onGone={(post) => setFeed((f) => f.filter((x) => x.id !== post.id))}
            onPollVoted={onPollVoted}
          />
        )))}
        {feed.length === 0 && <div className="ct-empty"><span>The feed is quiet — post something.</span></div>}
        {/* 🔁 the bottom of the ranked feed, watched so the next page is asked
            for before the reader gets here */}
        {feed.length > 0 && <div ref={feedEndRef} style={{ height: 1 }} />}
        </>)}
      </main>

      {/* ⭐ tapped a shared card whose original no longer exists */}
      {embedMiss && (
        <div className="vb-overlay" onClick={() => setEmbedMiss(false)}>
          <div className="vb-sheet vb-gone-sheet" onClick={(e) => e.stopPropagation()}>
            <b>This content isn&apos;t available</b>
            <p>It may have been deleted or its privacy changed.</p>
            <button className="btn" onClick={() => setEmbedMiss(false)}>OK</button>
          </div>
        </div>
      )}

      {/* ══ right: the squeeze pane ══ */}
      {/* 🌍 the world map, over everything */}
      <WorldMap open={worldOpen} onClose={() => setWorldOpen(false)} me={me} myUid={myUid} onProfile={openProfile} />
      {panel && panel.type === 'reels' && (
        <ReelsPane
          startPost={panel.post} feed={feedAll} me={me} myPages={myPages} chats={chats} myUid={myUid}
          archiveFilter={archiveFilter}
          savedIds={self.savedPosts || []} mute={muteActions}
          onDeleteComment={deleteCommentHere}
          onClose={() => setPanel(null)}
          onLike={(post, liked, rx) => { if (!liked) feedSignals.liked(post.id); likeWithNotify(post, me, liked, rx || null).catch(() => {}); }}
          onSave={(post, saved) => { if (!saved) feedSignals.saved(post.id); toggleSave(myUid, post.id, saved).catch(() => {}); }}
          onProfile={openProfile}
          onDrag={(e) => startRightDrag(e, { growOnly: true })}
          follow={reelFollow}
          /* 🏷 A TAG GOES TO THE SEARCH BOX THAT IS ALREADY THERE — the reels
             pane closes so the results are what you are looking at, rather than
             a second discovery screen quietly drifting from this one. */
          onTag={(tag) => { setQLive(hashtagQuery(tag)); setPanel(null); }}
        />
      )}
      {panel && panel.type !== 'reels' && (
        <RightPane
          panel={panel} me={me} myPages={myPages} feed={feed} mute={muteActions}
          onDeleteComment={deleteCommentHere}
          onClose={() => setPanel(null)}
          onOpenUser={openProfile}
          onOpenPost={(pp) => (videoUrlOf(pp) ? openVideo(pp) : openPost(pp, 0))}
          onOpenPostId={openPostById}
          onShare={(pp) => setShareFor(pp)}
          onInsights={openInsights}
          onDrag={startRightDrag}
          onResetWidth={() => setRightW(0)}
          /* the same card the feed renders, with the same handlers — so a poll
             on a page is answerable and a like from here is a like */
          renderPost={(p) => (
            <PostCard
              key={p.id} p={p} me={me}
              onLike={(post, liked, rx) => { if (!liked) feedSignals.liked(post.id); likeWithNotify(post, me, liked, rx || null).catch(() => {}); }}
              onComments={openComments}
              onShare={(post) => setShareFor(post)}
              onVideo={openVideo}
              onProfile={openProfile}
              onOpenPost={openPost}
              onOpenPostId={openPostById}
              onInsights={openInsights}
              onGone={(post) => setFeed((f) => f.filter((x) => x.id !== post.id))}
              onPollVoted={onPollVoted}
            />
          )}
        />
      )}

      {/* ══ overlays over the whole screen ══ */}
      {shareFor && (
        <div className="vb-fixed-share" onClick={() => setShareFor(null)}>
          <div onClick={(e) => e.stopPropagation()}>
            <ShareSheet
              post={feed.find((x) => x.id === shareFor.id) || shareFor}
              chats={chats} me={me} myUid={myUid}
              onClose={() => setShareFor(null)}
            />
          </div>
        </div>
      )}
      {mutedSheet && (
        <MutedExplainSheet
          notif={mutedSheet.notif || null}
          post={mutedSheet.post || null}
          myUid={me?.uid}
          onClose={() => setMutedSheet(null)}
          onDisputed={patchDispute}
          onOpenChat={openAlloChat}
        />
      )}
      {modOutcome && (
        <ModerationOutcome
          n={modOutcome}
          onClose={() => setModOutcome(null)}
          onViewPost={(postId) => {
            setModOutcome(null);
            openComments({ id: postId, comments: [], userId: '' });
          }}
        />
      )}
      {pageMaker && <PageMaker me={me} onClose={() => setPageMaker(false)} />}
      {statusPick && <StatusPicker onPick={setPostStatus} onClose={() => setStatusPick(false)} />}
      {tagPick && (
        <TagPicker value={tagged} friendIds={friendsRef.current} myUid={myUid}
          onDone={(list) => { setTagged(list); setTagPick(false); }} onClose={() => setTagPick(false)} />
      )}
      {editingPost && (
        <EditPostModal post={editingPost} myUid={myUid} friendIds={friendsRef.current} onClose={() => setEditingPost(null)}
          onSaved={(id, fields) => setFeed((f) => f.map((x) => (x.id === id ? { ...x, ...fields } : x)))} />
      )}
    </div>
  );
}
